import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import { usePersistentState } from '../../lib/usePersistentState'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
    FaArrowLeft, FaSignOutAlt, FaPlus, FaFileInvoiceDollar, FaListUl,
    FaEdit, FaCheckCircle, FaUser, FaMapMarkerAlt, FaCalendarAlt, FaHashtag,
    FaHistory, FaSync, FaCalculator, FaStickyNote, FaComment,
    FaMoneyBillWave, FaSave, FaChevronDown, FaChevronUp, FaCog,
    FaBuilding, FaRulerCombined, FaPercentage, FaTrash, FaCheck,
    FaToggleOn, FaToggleOff, FaInfoCircle,
    FaSearch, FaTimes, FaTimesCircle, FaTag, FaEye,
    FaLink, FaExclamationTriangle,
    FaHardHat, FaDraftingCompass, FaExpand, FaGripVertical, FaUndo,
    FaCopy, FaLayerGroup, FaSpinner, FaShieldAlt,
    FaStar, FaRegStar
} from 'react-icons/fa'
import { DndContext, closestCenter, rectIntersection, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, rectSortingStrategy, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import ArchEstimatePreview from './ArchEstimatePreview'
import { saveDraft, loadDraft, deleteDraft } from '../../lib/offlineStore'
import { useRole } from '../../hooks/useRole'
import { useVisibilityRefresh } from '../../hooks/useVisibilityRefresh'
import { fetchAddressSuggestions, getPlaceDetails, newSessionToken } from '../../lib/googlePlaces'
import {
    useArchEstimates, useArchivedArchEstimates, saveArchEstimate, archiveArchEstimate, markArchEstimateStatus, cloneArchEstimate, newArchNumber
} from '../../lib/archEstimates'
import { fetchEstimatesBoard } from '../../lib/monday'
import { useArchCatalogItems } from '../../lib/archCatalogItems'
import { useArchTerms, cleanTermText, useArchTermsSourceDefaults, getDefaultTermsFromCatalog } from '../../lib/archTerms'
import {
    useArchConfig,
    useArchDifficultyLevels,
    useArchCostRoles,
    useArchDisciplinePresets,
    useArchScopeMultipliers,
    useArchMinFloors,
    DISCIPLINE_SCOPE_MULTIPLIERS,
    DISCIPLINE_SCOPE_OPTIONS,
    ARCH_PHASES,
    ARCH_PROJECT_TYPES,
    SCOPE_OPTION_TO_PCT,
    DEFAULT_CUOTAS,
    ARCH_SCHEDULE_PRESETS,
    resolveProjectTypeKey,
    deriveDisciplineSuggestion,
    normalizeDiscipline,
    ADD_ALT_BASE_PRICES,
    ADD_ALT_MIN_FLOORS,
    ADD_ALT_DIFFICULTY_DEFAULTS,
    ADD_ALT_PROJECT_TYPES,
    MARKET_OPTIONS,
    getProjectTypeComResPrefix,
} from '../../lib/archConfig'
import {
    useArchAddons,
    getAddonScopeItemIds,
    applyAddonGatedFilter,
    isAddonItem,
    getOrphanAddons,
    getAddonLabel,
    getDefaultEstimateAddons,
    mergeEstimateAddons,
} from '../../lib/archAddons'
import { useArchPaymentInfo } from '../../lib/archPaymentInfo'
import { useArchAddAltDifficulties } from '../../lib/archAddAltDifficulties'
import { useArchCivilDifficultyLevels } from '../../lib/archCivilDifficulties'
import { useArchSchedulePresets } from '../../lib/archSchedulePresets'
import { getOfficeState } from '../../lib/estimateData'

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmt  = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtPct = (n) => (Number(n || 0) * 100).toFixed(1) + '%'
const round2 = (n) => Math.round((n || 0) * 100) / 100

function filterByProjectTypeSuffix(items, projectType) {
    const prefix = getProjectTypeComResPrefix(projectType)
    if (!prefix) return items
    return items.filter(item => {
        const name = (item.name || '').trim()
        const comMatch = /[\s–—-]+COM\s*$/i.test(name)
        const resMatch = /[\s–—-]+RES\s*$/i.test(name)
        if (!comMatch && !resMatch) return true
        if (prefix === 'com') return comMatch
        if (prefix === 'res') return resMatch
        return false
    })
}

const ARCH_DRAFT_TYPE = 'arch_builder'

function getStandardCuotas(t, dbPresets = []) {
    // Priority 1: DB preset marked as default
    const dbDefault = dbPresets.find(p => p.is_default)
    if (dbDefault) return dbDefault.rows.map(r => ({ label: r.label || '', pct: r.pct }))
    // Priority 2: DB preset named 'Standard (30/40/20/10)' (legacy fallback)
    const dbStandard = dbPresets.find(p => p.name === 'Standard (30/40/20/10)')
    if (dbStandard) return dbStandard.rows.map(r => ({ label: r.label || '', pct: r.pct }))
    // Priority 3: hardcoded constant
    const hardcoded = ARCH_SCHEDULE_PRESETS.find(p => p.nameKey === 'admin.arch.schedule_standard_30_40_20_10')
    return hardcoded ? hardcoded.rows.map(r => ({ label: r.labelKey ? t(r.labelKey) : r.label || '', pct: r.pct })) : []
}

const EMPTY_ARCH_ESTIMATE = () => ({
    id:              null,
    number:          newArchNumber(),
    date:            new Date().toISOString().split('T')[0],
    client:             '',
    phone:              '',
    email:              '',
    mondayItemId:       '',
    projectType:        '',
    projectDescription: '',
    address:            '',
    addressDetails:  {},
    market:          'FL_RES',
    sqft:            '',
    lengthFt:        '',
    widthFt:         '',
    useArea:         'direct',
    existSqft:       0,
    existDiff:       0.125,
    remSqft:         0,
    remDiff:         1.0,
    addSqft:         0,
    addDiff:         1.0,
    difficulty:      1.0,
    civilSqft:       null,
    civilDifficulty: null,
    disciplineScope: 'full',
    archPct:         50,
    structPct:       22,
    mepPct:          20,
    mechPct:         7,
    elecPct:         7,
    plumbPct:        6,
    civilPct:        0,
    adminPct:        8,
    civilAdminPct:   0,
    addons:          [], // populated dynamically via getDefaultEstimateAddons(addonDefs)
    cuotas:          [],
    scopeItems:      [],
    notes:           '',
    additionalComments: '',
    timeFrameValue:  '',
    timeFrameUnit:   'weeks',
    status:          'draft',
    serviceGroups:   [],
    terms:           [],
    certCostOverrides:         {},
    directCostOverrides:       {},
    commissionOverrides:       {},
    operatingCostOverrides:    {},
    franchiseCostOverrides:    {},
    profitCostOverrides:       {},
    contingencyCostOverrides:  {},
    minPriceOverride:          null,
    disciplineDeductions:        [],
})

// ── Helpers for civil split pricing ──────────────────────────────────────────
const hasCivilDeliverables = (scopeItems) =>
    (scopeItems || []).some(i => (i.discipline || '').toUpperCase() === 'CIVIL')

const getCivilMarket = (mainMarket) => {
    const state = mainMarket?.startsWith('TX') ? 'TX' : 'FL'
    return `${state}_CIVIL`
}

const NEW_CONSTRUCTION_TYPES = new Set(['res_new', 'com_new', 'civil'])

// ── Discipline percentage pools ──────────────────────────────────────────────
// When the civil split is active these are two INDEPENDENT pools: the building
// pool is applied to mainPrice and the civil pool to civilPrice, so each one has
// to add up to 100% on its own. Any shortfall is money that is still billed in
// totalContract but never itemized in a discipline line.
const MAIN_PCT_FIELDS = ['archPct', 'structPct', 'mechPct', 'elecPct', 'plumbPct', 'adminPct']
const CIVIL_PCT_FIELDS = ['civilPct', 'civilAdminPct']
const PCT_EPSILON = 0.01

// Default civil pool split: 80% engineering / 20% coordination. Matches the
// convention used for pure civil projects and calcNewConstructionCivilSplit().
const DEFAULT_CIVIL_ENGINEERING_PCT = 80

const sumPcts = (source, fields) =>
    fields.reduce((s, k) => s + (Number(source?.[k]) || 0), 0)

// Scale `fields` proportionally so they add up to `target`, then push any
// rounding drift onto the largest field so the pool closes exactly.
const scalePctsTo = (source, fields, target) => {
    const remaining = sumPcts(source, fields)
    const factor = remaining > 0 ? target / remaining : 0
    const next = {}
    let distributed = 0
    for (const k of fields) {
        next[k] = Math.max(0, Math.round((Number(source?.[k]) || 0) * factor))
        distributed += next[k]
    }
    const drift = target - distributed
    if (drift !== 0 && fields.length > 0) {
        const maxKey = fields.reduce((a, b) => (next[a] >= next[b] ? a : b))
        next[maxKey] = Math.max(0, next[maxKey] + drift)
    }
    return next
}

// mepPct is a derived rollup — the PDF renders a single MEP row from it while the
// builder edits mech/elec/plumb separately. Keep them in sync or the PDF lies.
const withMepRollup = (next) => ({
    ...next,
    mepPct: (next.mechPct || 0) + (next.elecPct || 0) + (next.plumbPct || 0),
})

// ── Monday column helpers ─────────────────────────────────────────────────────
const MON_CLIENT_COL  = 'text_mkt9adtr'
const MON_EMAIL_COL   = 'text_mkt9qv82'
const MON_PHONE_COL   = 'phone_mkt9n841'
const MON_OFFICE_COL  = 'color_mkt9jxhh'
const MON_TYPE_COL    = 'color_mktck81k'
const MON_DESC_COL    = 'color_mktcap3t'
function monCol(item, id) { return item.column_values?.find(c => c.id === id)?.text || '' }

// ── Sub-component: ArchClientAutocomplete ────────────────────────────────────
function ArchClientAutocomplete({ value, items, itemsLoading, onSelect, onRefreshItems }) {
    const { t } = useTranslation()
    const [inputValue,  setInputValue]  = useState(value || '')
    const [suggestions, setSuggestions] = useState([])
    const [highlighted, setHighlighted] = useState(-1)
    const [dropPos,     setDropPos]     = useState(null)
    const debounceRef   = useRef(null)
    const userTypingRef = useRef(false)
    const inputRef      = useRef(null)

    const getItemAddress = (item) => {
        const rawName = item.name || ''
        const pipeIdx = rawName.indexOf('|')
        return pipeIdx !== -1 ? rawName.slice(pipeIdx + 1).trim() : rawName
    }

    const computeDropPos = useCallback(() => {
        if (inputRef.current) {
            const r = inputRef.current.getBoundingClientRect()
            setDropPos({ top: r.bottom + 4, left: r.left, width: r.width })
        }
    }, [])

    useEffect(() => {
        if (!userTypingRef.current) setInputValue(value || '')
    }, [value])

    const runFilter = useCallback((text) => {
        const q = text.trim().toLowerCase()
        if (!q || q.length < 2) { setSuggestions([]); return }
        setSuggestions(
            (items || []).filter(item => {
                const clientName = monCol(item, MON_CLIENT_COL).toLowerCase()
                const address = getItemAddress(item).toLowerCase()
                const fullName = (item.name || '').toLowerCase()
                return clientName.includes(q) || address.includes(q) || fullName.includes(q)
            }).slice(0, 8)
        )
    }, [items])

    const handleInput = (text) => {
        setInputValue(text)
        userTypingRef.current = true
        setHighlighted(-1)
        computeDropPos()
        clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => runFilter(text), 300)
    }

    const handleSelect = (item) => {
        const clientName = monCol(item, MON_CLIENT_COL)
        setInputValue(clientName)
        setSuggestions([])
        userTypingRef.current = false
        onSelect(item)
    }

    const handleKeyDown = (e) => {
        if (!suggestions.length) return
        if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, suggestions.length - 1)) }
        if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)) }
        if (e.key === 'Enter' && highlighted >= 0) { e.preventDefault(); handleSelect(suggestions[highlighted]) }
        if (e.key === 'Escape') { setSuggestions([]) }
    }

    useEffect(() => () => clearTimeout(debounceRef.current), [])

    return (
        <div className="relative">
            <FaUser className="absolute left-3 top-1/2 text-gray-400 -translate-y-1/2 pointer-events-none" size={11} />
            <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={e => handleInput(e.target.value)}
                onFocus={() => {
                    computeDropPos()
                    runFilter(inputValue)
                }}
                onKeyDown={handleKeyDown}
                onBlur={() => setTimeout(() => setSuggestions([]), 200)}
                placeholder={t('admin.estimator.client_placeholder')}
                autoComplete="off"
                className="py-2 pr-8 pl-8 w-full text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            {itemsLoading && (
                <span className="absolute right-3 top-1/2 text-xs text-gray-400 -translate-y-1/2">{t('admin.estimator.loading')}</span>
            )}
            {!itemsLoading && items !== null && (
                <button
                    type="button"
                    onMouseDown={e => { e.preventDefault(); onRefreshItems() }}
                    className="absolute right-2 top-1/2 p-1 text-gray-300 transition-colors -translate-y-1/2 hover:text-gray-500"
                    title={t('admin.estimator.refresh_clients')}
                >
                    <FaHistory size={9} />
                </button>
            )}
            {suggestions.length > 0 && dropPos && (
                <ul style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, width: dropPos.width }}
                    className="z-[9999] overflow-y-auto max-h-60 bg-white rounded-xl border border-gray-200 shadow-xl">
                    {suggestions.map((item, idx) => {
                        const client  = monCol(item, MON_CLIENT_COL)
                        const typeTxt = monCol(item, MON_TYPE_COL)
                        const office  = monCol(item, MON_OFFICE_COL)
                        const address = getItemAddress(item)
                        return (
                            <li
                                key={item.id}
                                onMouseDown={e => { e.preventDefault(); handleSelect(item) }}
                                className={`px-4 py-2.5 text-sm cursor-pointer transition-colors border-b border-gray-100 last:border-b-0 ${highlighted === idx ? 'bg-primary/5' : 'hover:bg-primary/5'}`}
                            >
                                <p className="font-medium text-gray-800">{client || <span className="italic text-gray-400">Sin cliente</span>}</p>
                                <div className="flex gap-1.5 items-center mt-0.5">
                                    {address && <p className="flex flex-1 gap-1 items-center text-xs text-gray-400 truncate"><FaMapMarkerAlt size={9} className="shrink-0" />{address}</p>}
                                    {typeTxt && <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded-full shrink-0 bg-violet-50 text-violet-600 border border-violet-200">{typeTxt}</span>}
                                    {office  && <span className="text-[10px] text-gray-400 shrink-0">{office}</span>}
                                </div>
                            </li>
                        )
                    })}
                </ul>
            )}
        </div>
    )
}

// ── Sub-component: MondayAddressAutocomplete ────────────────────────────────
function MondayAddressAutocomplete({ value, items, itemsLoading, onSelectMonday, onSelectGoogle, googleSuggestions, googleLoading, onInput }) {
    const { t } = useTranslation()
    const [inputValue,  setInputValue]  = useState(value || '')
    const [mondaySuggestions, setMondaySuggestions] = useState([])
    const [highlighted, setHighlighted] = useState(-1)
    const [dropPos,     setDropPos]     = useState(null)
    const debounceRef   = useRef(null)
    const userTypingRef = useRef(false)
    const inputRef      = useRef(null)

    const getItemAddress = (item) => {
        const rawName = item.name || ''
        const pipeIdx = rawName.indexOf('|')
        return pipeIdx !== -1 ? rawName.slice(pipeIdx + 1).trim() : rawName
    }
    const getItemClient = (item) => monCol(item, MON_CLIENT_COL)

    const computeDropPos = useCallback(() => {
        if (inputRef.current) {
            const r = inputRef.current.getBoundingClientRect()
            setDropPos({ top: r.bottom + 4, left: r.left, width: r.width })
        }
    }, [])

    useEffect(() => {
        if (!userTypingRef.current) setInputValue(value || '')
    }, [value])

    const runMondayFilter = useCallback((text) => {
        const q = text.trim().toLowerCase()
        if (!q || q.length < 2) { setMondaySuggestions([]); return }
        setMondaySuggestions(
            (items || []).filter(item => {
                const address = getItemAddress(item).toLowerCase()
                const clientName = getItemClient(item).toLowerCase()
                const fullName = (item.name || '').toLowerCase()
                return address.includes(q) || clientName.includes(q) || fullName.includes(q)
            }).slice(0, 6)
        )
    }, [items])

    const handleInput = (text) => {
        setInputValue(text)
        userTypingRef.current = true
        setHighlighted(-1)
        computeDropPos()
        onInput(text)
        clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => runMondayFilter(text), 300)
    }

    const handleSelectMonday = (item) => {
        const address = getItemAddress(item)
        setInputValue(address)
        setMondaySuggestions([])
        userTypingRef.current = false
        onSelectMonday(item)
    }

    const handleSelectGoogle = (suggestion) => {
        setInputValue(suggestion.description)
        setMondaySuggestions([])
        userTypingRef.current = false
        onSelectGoogle(suggestion)
    }

    const allSuggestions = mondaySuggestions.length > 0
        ? [...mondaySuggestions.map(i => ({ type: 'monday', data: i })), ...googleSuggestions.map(s => ({ type: 'google', data: s }))]
        : googleSuggestions.map(s => ({ type: 'google', data: s }))

    const handleKeyDown = (e) => {
        if (!allSuggestions.length) return
        if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, allSuggestions.length - 1)) }
        if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)) }
        if (e.key === 'Enter' && highlighted >= 0) {
            e.preventDefault()
            const selected = allSuggestions[highlighted]
            if (selected.type === 'monday') handleSelectMonday(selected.data)
            else handleSelectGoogle(selected.data)
        }
        if (e.key === 'Escape') { setMondaySuggestions([]) }
    }

    useEffect(() => () => clearTimeout(debounceRef.current), [])

    return (
        <div className="relative">
            <FaMapMarkerAlt className="absolute left-3 top-1/2 text-gray-400 -translate-y-1/2 pointer-events-none" size={11} />
            <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={e => handleInput(e.target.value)}
                onFocus={() => {
                    computeDropPos()
                    runMondayFilter(inputValue)
                }}
                onKeyDown={handleKeyDown}
                onBlur={() => setTimeout(() => setMondaySuggestions([]), 200)}
                placeholder={t('admin.estimator.project_address')}
                autoComplete="off"
                className="py-2 pr-3 pl-8 w-full text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            {googleLoading && (
                <span className="absolute right-3 top-1/2 text-xs text-gray-400 -translate-y-1/2">{t('admin.estimator.loading')}</span>
            )}
            {allSuggestions.length > 0 && dropPos && (
                <ul style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, width: dropPos.width }}
                    className="z-[9999] overflow-y-auto max-h-72 bg-white rounded-xl border border-gray-200 shadow-xl">
                    {mondaySuggestions.length > 0 && (
                        <li className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 bg-gray-50 border-b border-gray-100">
                            Monday
                        </li>
                    )}
                    {mondaySuggestions.map((item, idx) => {
                        const address = getItemAddress(item)
                        const client = getItemClient(item)
                        const typeTxt = monCol(item, MON_TYPE_COL)
                        return (
                            <li key={item.id}
                                onMouseDown={e => { e.preventDefault(); handleSelectMonday(item) }}
                                className={`px-4 py-2.5 text-sm cursor-pointer transition-colors border-b border-gray-100 last:border-b-0 ${highlighted === idx ? 'bg-primary/5' : 'hover:bg-primary/5'}`}
                            >
                                <p className="font-medium text-gray-800">{address}</p>
                                <div className="flex gap-1.5 items-center mt-0.5">
                                    {client && <p className="flex gap-1 items-center text-xs text-gray-400"><FaUser size={9} className="shrink-0" />{client}</p>}
                                    {typeTxt && <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded-full shrink-0 bg-violet-50 text-violet-600 border border-violet-200">{typeTxt}</span>}
                                </div>
                            </li>
                        )
                    })}
                    {googleSuggestions.length > 0 && (
                        <li className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 bg-gray-50 border-b border-gray-100">
                            Google
                        </li>
                    )}
                    {googleSuggestions.map((s, idx) => {
                        const overallIdx = mondaySuggestions.length + idx
                        return (
                            <li key={s.place_id}
                                onMouseDown={e => { e.preventDefault(); handleSelectGoogle(s) }}
                                className={`px-4 py-2.5 text-sm cursor-pointer transition-colors border-b border-gray-100 last:border-b-0 ${highlighted === overallIdx ? 'bg-primary/5' : 'hover:bg-primary/5'}`}
                            >
                                <p className="text-gray-700">{s.description}</p>
                            </li>
                        )
                    })}
                </ul>
            )}
        </div>
    )
}

// ── Sub-component: ArchEstimadosTab ──────────────────────────────────────────
function ArchEstimadosTab({ estimates, loading, error, reload, onResume, onPreview, onClone, userId, session }) {
    const { t }             = useTranslation()
    const { marketOptions } = useArchConfig()
    const [search, setSearch]                  = useState('')
    const [marketFilter, setMarketFilter]        = useState('')
    const [statusFilter, setStatusFilter]        = useState('')
    const [archiving, setArchiving]            = useState(null)
    const [cloning, setCloning]                = useState(null)
    const [expandedRaw, setExpandedRaw] = usePersistentState(`est_arch_expanded_groups_${userId || 'anon'}`, [])
    const expanded = useMemo(() => new Set(expandedRaw), [expandedRaw])

    const searchFiltered = useMemo(() => {
        const q = search.toLowerCase()
        if (!q) return estimates
        return estimates.filter(e =>
            (e.client || '').toLowerCase().includes(q) ||
            (e.number || '').toLowerCase().includes(q)
        )
    }, [estimates, search])

    const filtered = useMemo(() => {
        let result = searchFiltered
        if (marketFilter) {
            result = result.filter(e => e.market === marketFilter)
        }
        if (statusFilter) {
            result = result.filter(e => (e.status || 'draft') === statusFilter)
        }
        return result
    }, [searchFiltered, marketFilter, statusFilter])

    // Group by normalized client name; unnamed go last under '' key
    const groups = useMemo(() => {
        const map = new Map()
        for (const est of filtered) {
            const key = (est.client || '').trim().toLowerCase()
            if (!map.has(key)) map.set(key, [])
            map.get(key).push(est)
        }
        // Sort groups: named clients first (alpha), then unnamed
        return [...map.entries()].sort(([a], [b]) => {
            if (!a) return 1
            if (!b) return -1
            return a.localeCompare(b)
        })
    }, [filtered])

    const toggleGroup = (key) => {
        setExpandedRaw(prev => {
            const has = prev.includes(key)
            return has ? prev.filter(k => k !== key) : [...prev, key]
        })
    }

    const handleArchive = async (id) => {
        if (!window.confirm(t('admin.arch.archive_confirm'))) return
        setArchiving(id)
        try {
            const user = session?.user
            await archiveArchEstimate(id, user?.id, user?.email, user?.user_metadata?.full_name || user?.email)
            reload()
        } catch (e) {
            alert(e.message)
        } finally {
            setArchiving(null)
        }
    }

    const handleToggleStatus = async (est) => {
        const next = est.status === 'sent' ? 'draft' : 'sent'
        try {
            await markArchEstimateStatus(est.id, next)
            reload()
        } catch (e) {
            alert(e.message)
        }
    }

    const handleClone = async (est) => {
        setCloning(est.id)
        try {
            const cloned = await onClone(est.id)
            if (cloned) {
                reload()
                onResume(cloned)
            }
        } catch (e) {
            alert(e.message)
        } finally {
            setCloning(null)
        }
    }

    if (loading) return <p className="py-10 text-center text-gray-400">{t('admin.estimator.loading')}</p>
    if (error)   return <p className="py-10 text-center text-red-400">{error}</p>

    return (
        <div className="space-y-4">
            <div className="flex gap-3 items-center">
                <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder={t('admin.arch.search_placeholder')}
                    className="flex-1 px-3 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                <button onClick={reload} className="p-2 text-gray-400 rounded-xl border border-gray-200 transition-all hover:bg-gray-50">
                    <FaSync size={13} />
                </button>
            </div>

            {/* Market + Status filter pills */}
            <div className="flex flex-wrap gap-1.5">
                <button
                    onClick={() => setMarketFilter('')}
                    className={`px-3 py-1 text-xs font-bold rounded-full transition-all ${!marketFilter ? 'bg-primary text-white' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'}`}
                >{t('admin.arch.filter_all')} ({searchFiltered.filter(e => !statusFilter || (e.status || 'draft') === statusFilter).length})</button>
                {marketOptions.map(m => {
                    const count = searchFiltered.filter(e => e.market === m.key && (!statusFilter || (e.status || 'draft') === statusFilter)).length
                    return (
                    <button
                        key={m.key}
                        onClick={() => setMarketFilter(marketFilter === m.key ? '' : m.key)}
                        className={`px-3 py-1 text-xs font-bold rounded-full transition-all ${marketFilter === m.key ? 'bg-primary text-white' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'}`}
                    >{t(m.labelKey)} ({count})</button>
                    )
                })}
                <span className="w-px h-5 self-center bg-gray-200 mx-1" />
                {['draft', 'sent'].map(s => {
                    const count = searchFiltered.filter(e => (e.status || 'draft') === s && (!marketFilter || e.market === marketFilter)).length
                    const activeColor = s === 'draft' ? 'bg-rose-500 text-white' : 'bg-emerald-500 text-white'
                    return (
                    <button
                        key={s}
                        onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
                        className={`px-3 py-1 text-xs font-bold rounded-full transition-all ${statusFilter === s ? activeColor : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'}`}
                    >{t(`admin.arch.filter_${s}`)} ({count})</button>
                    )
                })}
            </div>

            {estimates.length === 0 && (
                <p className="py-12 text-sm text-center text-gray-400">{t('admin.arch.estimates_empty')}</p>
            )}
            {estimates.length > 0 && filtered.length === 0 && (
                <p className="py-8 text-sm text-center text-gray-400">{t('admin.estimator.estimates_no_results')}</p>
            )}

            <div className="grid gap-2">
                {groups.map(([groupKey, items]) => {
                    const isOpen       = expanded.has(groupKey)
                    const displayName  = items[0].client || t('admin.estimator.no_client')
                    const latestDate   = items.reduce((d, e) => (!d || e.updated_at > d ? e.updated_at : d), null)
                    const latestEst    = items.reduce((a, e) => (!a || e.updated_at > a.updated_at ? e : a), null)
                    const latestSent   = items.filter(e => (e.status || 'draft') === 'sent')
                        .reduce((a, e) => (!a || e.updated_at > a.updated_at ? e : a), null)
                    const shownEst     = latestSent || latestEst

                    return (
                        <div key={groupKey} className="overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-sm">
                            {/* Group header — click to expand */}
                            <button
                                type="button"
                                onClick={() => toggleGroup(groupKey)}
                                className="flex gap-3 items-center px-4 py-3 w-full text-left transition-colors hover:bg-gray-50"
                            >
                                <div className="p-2 rounded-xl bg-primary/10 shrink-0">
                                    <FaBuilding className="text-primary" size={13} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-800 truncate">{displayName}</p>
                                    <p className="text-[11px] text-gray-400 mt-0.5">
                                        {items.length} {items.length === 1 ? t('admin.arch.estimate_singular') : t('admin.arch.estimates_count')}
                                        {latestDate && <> · {new Date(latestDate).toLocaleDateString()}</>}
                                    </p>
                                </div>
                                <div className="text-right shrink-0">
                                    <p className="text-sm font-bold text-primary">${fmt(shownEst?.total_contract || 0)}</p>
                                    <p className="text-[10px] text-gray-400 mt-0.5">{t('admin.estimator.latest_total')} <strong className={latestSent ? 'text-emerald-600' : 'text-rose-500'}>{latestSent ? t('admin.estimator.latest_emph_sent') : t('admin.estimator.latest_emph_not_sent')}</strong></p>
                                </div>
                                <div className="text-gray-400 shrink-0">
                                    {isOpen ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
                                </div>
                            </button>

                            {/* Expanded estimate sub-rows */}
                            <AnimatePresence initial={false}>
                                {isOpen && (
                                    <motion.div
                                        key="rows"
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.18 }}
                                        className="overflow-hidden"
                                    >
                                        <div className="px-4 pb-3 space-y-2 border-t border-gray-100">
                                            {items.map((est, idx) => {
                                                const mktOpt = marketOptions.find(m => m.key === est.market)
                                                return (
                                                    <div key={est.id} className="flex gap-3 items-center pt-2.5">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex flex-wrap gap-1.5 items-center">
                                                                <span className="text-[10px] font-bold text-gray-500">{idx < 3 ? t(`admin.estimator.ordinal_${idx + 1}`) : t('admin.estimator.ordinal_other', { n: idx + 1 })}</span>
                                                                <span className="font-mono text-[11px] text-gray-400">{est.number}</span>
                                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                                                                    est.status === 'sent'
                                                                        ? 'bg-emerald-100 text-emerald-700'
                                                                        : 'bg-gray-100 text-gray-500'
                                                                }`}>{est.status}</span>
                                                                {mktOpt && (
                                                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-100 text-violet-700">
                                                                        {mktOpt.state} {mktOpt.type}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {est.address && <p className="text-[11px] text-gray-400 truncate mt-0.5">{est.address}</p>}
                                                            <p className="text-sm font-bold text-primary mt-0.5">${fmt(est.total_contract)}</p>
                                                        </div>
                                                        <div className="flex flex-col gap-1 shrink-0">
                                                            <button
                                                                onClick={() => onResume(est)}
                                                                className="px-2.5 py-1 text-[11px] font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 transition-all"
                                                            >{t('admin.arch.edit')}</button>
                                                            <button
                                                                onClick={() => onPreview(est)}
                                                                className="px-2.5 py-1 text-[11px] font-semibold text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition-all flex items-center gap-1 justify-center"
                                                            ><FaEye size={9} /> {t('admin.arch.preview')}</button>
                                                            <button
                                                                onClick={() => handleToggleStatus(est)}
                                                                className="px-2.5 py-1 text-[11px] font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all"
                                                            >{est.status === 'sent' ? t('admin.estimator.mark_draft') : t('admin.estimator.mark_sent')}</button>
                                                            <button
                                                                onClick={() => handleArchive(est.id)}
                                                                disabled={archiving === est.id}
                                                                className="px-2.5 py-1 text-[11px] font-semibold text-red-500 bg-red-50 rounded-lg hover:bg-red-100 transition-all disabled:opacity-50"
                                                            >{t('admin.estimator.archive')}</button>
                                                            <button
                                                                onClick={() => handleClone(est)}
                                                                disabled={cloning === est.id}
                                                                className="px-2.5 py-1 text-[11px] font-semibold text-violet-600 bg-violet-50 rounded-lg hover:bg-violet-100 transition-all disabled:opacity-50 flex items-center gap-1 justify-center"
                                                            >
                                                                {cloning === est.id ? <FaSpinner size={9} className="animate-spin" /> : <FaCopy size={9} />}
                                                                {t('admin.estimator.clone')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

// ── ArchCatalogItemRow ────────────────────────────────────────────────────────
function ArchCatalogItemRow({ item, isExpanded, onToggle, onUpsert, onRemove, onReload }) {
    const { t } = useTranslation()
    const [editName,  setEditName]  = useState(item.name || '')
    const [editDisc,  setEditDisc]  = useState(item.discipline || '')
    const [editSubcat,setEditSubcat]= useState(item.subcat || '')
    const [saving,    setSaving]    = useState(false)
    const [saveErr,   setSaveErr]   = useState(null)

    const DISC_COLORS = {
        ARCHITECTURE:'bg-blue-100 text-blue-700 border-blue-200',
        ARCH:        'bg-blue-100 text-blue-700 border-blue-200',
        STRUCTURAL:  'bg-amber-100 text-amber-700 border-amber-200',
        STRUCT:      'bg-amber-100 text-amber-700 border-amber-200',
        MEP:         'bg-teal-100 text-teal-700 border-teal-200',
        '3D':        'bg-pink-100 text-pink-700 border-pink-200',
        CIVIL:       'bg-green-100 text-green-700 border-green-200',
        ECC:         'bg-orange-100 text-orange-700 border-orange-200',
        ADMIN:       'bg-violet-100 text-violet-700 border-violet-200',
        PM:          'bg-violet-100 text-violet-700 border-violet-200',
    }
    const discColor = DISC_COLORS[editDisc?.toUpperCase()] || 'bg-gray-100 text-gray-600 border-gray-200'

    const save = async (patch = {}) => {
        setSaving(true); setSaveErr(null)
        try {
            await onUpsert({ ...item, name: editName.trim() || item.name, discipline: editDisc.trim() || 'GENERAL', subcat: editSubcat.trim() || null, ...patch })
        } catch (e) {
            setSaveErr(e.message); onReload()
        } finally { setSaving(false) }
    }

    return (
        <div className={`border-b border-gray-100 last:border-b-0 last:rounded-b-xl first:rounded-t-xl ${isExpanded ? 'bg-gray-50' : 'bg-white hover:bg-gray-50'} transition-colors`}>
            <div className="flex items-center gap-2 px-3 py-2.5 cursor-pointer select-none" onClick={onToggle}>
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold border shrink-0 ${discColor}`}>{item.discipline || 'GENERAL'}</span>
                <p className="flex-1 text-sm text-gray-800 truncate">{item.name}</p>
                {item.subcat && <span className="text-[10px] text-gray-400 shrink-0">{item.subcat}</span>}
                <span className="text-gray-400 shrink-0">{isExpanded ? <FaChevronUp size={10} /> : <FaEdit size={10} />}</span>
            </div>
            {isExpanded && (
                <div className="px-4 pt-1 pb-4 space-y-3 border-t border-gray-100">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div>
                            <label className="block mb-1 text-[10px] text-gray-500 uppercase tracking-wide">{t('admin.arch.catalog.name')}</label>
                            <input value={editName} onChange={e => setEditName(e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                        </div>
                        <div>
                            <label className="block mb-1 text-[10px] text-gray-500 uppercase tracking-wide">{t('admin.arch.catalog.discipline_category')}</label>
                            <input list="arch-discipline-list" value={editDisc} onChange={e => setEditDisc(e.target.value.toUpperCase())}
                                className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
                                placeholder="e.g. ARCHITECTURE" />
                            <datalist id="arch-discipline-list">
                                {['ARCH','STRUCT','MEP','3D','CIVIL','ECC','ADMIN'].map(d => <option key={d} value={d} />)}
                            </datalist>
                        </div>
                        <div>
                            <label className="block mb-1 text-[10px] text-gray-500 uppercase tracking-wide">{t('admin.arch.catalog.subcategory')}</label>
                            <input value={editSubcat} onChange={e => setEditSubcat(e.target.value)}
                                placeholder={t('admin.arch.catalog.optional_placeholder')}
                                className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                        </div>
                    </div>
                    <div className="flex gap-2 items-center pt-1">
                        <button onClick={() => save()} disabled={saving}
                            className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white text-xs font-bold rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-all">
                            <FaSave size={10} /> {saving ? t('admin.arch.catalog.saving') : t('admin.arch.catalog.save')}
                        </button>
                        {item.is_custom && item.id && (
                            <button onClick={() => onRemove(item.id).catch(onReload)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-500 text-xs font-bold rounded-lg hover:bg-red-100 transition-all">
                                <FaTrash size={9} /> {t('admin.arch.catalog.delete')}
                            </button>
                        )}
                        {saveErr && <span className="text-[10px] text-red-500">{saveErr}</span>}
                    </div>
                </div>
            )}
        </div>
    )
}

// ── Sub-component: ArchPaymentScheduleEditor ──────────────────────────────────
function ArchPaymentScheduleEditor({ cuotas, totalContract, onChange, customPresets = [] }) {
    const { t }   = useTranslation()
    const total   = cuotas.reduce((s, c) => s + (Number(c.pct) || 0), 0)
    const isValid = Math.abs(total - 100) < 0.01
    const canAdd  = cuotas.length < 10
    const canDel  = cuotas.length > 1

    const updateRow = (idx, field, val) =>
        onChange(cuotas.map((c, i) => i === idx ? { ...c, [field]: val } : c))

    const deleteRow = (idx) =>
        onChange(cuotas.filter((_, i) => i !== idx))

    const applyPreset = (rows) =>
        onChange(rows.map(r => ({ label: r.label, pct: r.pct })))

    const activePresetName = useMemo(() => {
        if (cuotas.length === 0) return null
        for (const preset of customPresets) {
            const rows = preset.rows || []
            if (rows.length !== cuotas.length) continue
            const match = rows.every((pr, i) => {
                const cr = cuotas[i]
                return (pr.label || '') === (cr.label?.trim() || '') &&
                       (Number(pr.pct) || 0) === (Number(cr.pct) || 0)
            })
            if (match) return preset.name
        }
        return null
    }, [cuotas, customPresets])

    return (
        <div className="overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="h-[3px] bg-emerald-500" />
            {/* Header */}
            <div className="flex justify-between items-center px-5 py-3.5 border-b border-gray-100">
                <h2 className="flex gap-2 items-center text-sm font-bold text-gray-700">
                    <FaMoneyBillWave size={12} className="text-primary" />
                    {t('admin.estimator.payment_schedule')}
                </h2>
                <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border transition-all ${
                    isValid
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-red-50 text-red-600 border-red-200'
                }`}>
                    {isValid ? <FaCheckCircle size={9} /> : '⚠'} {total}% / 100%
                </span>
            </div>

            <div className="px-5 pt-4 pb-2">
                {/* Preset strip */}
                <div className="flex flex-wrap items-center gap-1.5 mb-4">
                    <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide shrink-0">{t('admin.estimator.presets')}</span>
                    {customPresets.map(p => {
                        const presetName = p.name
                        const isActive = activePresetName === presetName
                        return (
                            <button
                                key={presetName}
                                type="button"
                                onClick={() => applyPreset(p.rows)}
                                className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-all ${
                                    isActive
                                        ? 'bg-primary text-white border-primary shadow-sm'
                                        : 'bg-white text-gray-600 border-gray-200 hover:bg-primary/5 hover:border-primary/30'
                                }`}
                            >
                                {presetName}
                            </button>
                        )
                    })}
                </div>

                {/* Column headers */}
                <div className="flex items-center gap-2 mb-1 px-0.5">
                    <span className="flex-1 text-[10px] text-gray-400 uppercase tracking-wide font-semibold">{t('admin.estimator.milestone_label')}</span>
                    <span className="w-16 text-center text-[10px] text-gray-400 uppercase tracking-wide font-semibold shrink-0">%</span>
                    <span className="w-24 text-right text-[10px] text-gray-400 uppercase tracking-wide font-semibold shrink-0">{t('admin.estimator.col_amount')}</span>
                    <span className="w-7 shrink-0" />
                </div>

                {/* Rows */}
                <div className="space-y-1.5 mb-2">
                    {cuotas.map((c, idx) => {
                        const amount = totalContract > 0 ? totalContract * ((Number(c.pct) || 0) / 100) : null
                        return (
                            <div key={idx} className="flex gap-2 items-center">
                                <input
                                    type="text"
                                    value={c.label}
                                    onChange={e => updateRow(idx, 'label', e.target.value)}
                                    placeholder={t('admin.estimator.payment_placeholder', { n: idx + 1 })}
                                    maxLength={120}
                                    className="flex-1 px-3 py-1.5 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40 min-w-0"
                                />
                                <div className="relative w-16 shrink-0">
                                    <input
                                        type="number"
                                        value={c.pct}
                                        min={0}
                                        max={100}
                                        step={1}
                                        onChange={e => updateRow(idx, 'pct', Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                                        className={`w-full px-2 py-1.5 pr-5 text-xs text-center rounded-lg border focus:outline-none focus:ring-2 focus:ring-primary/40 ${
                                            total > 100 ? 'border-red-300 bg-red-50' : 'border-gray-200'
                                        }`}
                                    />
                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none">%</span>
                                </div>
                                <span className="w-24 text-xs font-semibold tabular-nums text-right text-gray-500 shrink-0">
                                    {amount !== null ? `$${fmt(amount)}` : '—'}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => deleteRow(idx)}
                                    disabled={!canDel}
                                    className="w-7 flex justify-center p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all disabled:opacity-0 disabled:pointer-events-none shrink-0"
                                    title={t('admin.estimator.remove')}
                                >
                                    <FaTimesCircle size={11} />
                                </button>
                            </div>
                        )
                    })}
                </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-5 py-2.5 border-t border-gray-100 bg-gray-50">
                <button
                    type="button"
                    onClick={() => onChange([...cuotas, { label: '', pct: 0 }])}
                    disabled={!canAdd}
                    className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 disabled:opacity-40 disabled:pointer-events-none transition-all"
                >
                    <FaPlus size={9} /> {t('admin.estimator.add_installment')}
                </button>
                <span className={`text-xs font-bold ${isValid ? 'text-emerald-600' : 'text-red-500'}`}>
                    {isValid
                        ? t('admin.estimator.total_valid')
                        : t('admin.estimator.total_invalid', { diff: `${total > 100 ? '+' : ''}${(total - 100).toFixed(1)}` })
                    }
                </span>
            </div>
        </div>
    )
}

// ── Sub-component: ArchConfigTab ──────────────────────────────────────────────
function ArchConfigTab({
    session, canEditCatalog,
    catalogItems,
    upsertItem, removeItem, reloadCatalog,
    difficultyLevels, difficultyLoading,
    upsertDifficultyLevel, removeDifficultyLevel, reloadDifficultyLevels,
    civilLevels, civilLoading,
    upsertCivilLevel, removeCivilLevel, reloadCivilLevels,
    paymentInfos, createPaymentInfo, updatePaymentInfo, deletePaymentInfo, reloadPaymentInfo,
    addAltDiffRows, addAltDiffLoading, getOptionsForComponent, upsertDifficultyOption, removeDifficultyOption, reloadAddAltDifficulties,
    addonDefs, createArchAddon, updateArchAddon, removeArchAddon, reloadAddons,
    schedulePresets, schedulePresetsLoading, createSchedulePreset, updateSchedulePreset, deleteSchedulePreset,
    setDefaultPreset, clearDefaultPreset, reloadSchedulePresets,
    defaultSourceFiles, setSourceDefault,
    uiDraft, onUiChange,
}) {
    const { t }  = useTranslation()
    const { rows, loading, error, marketOptions, getMarketConfig, saveMarketConfig, createMarket, deleteMarket, reload } = useArchConfig()
    const { multipliers: dbScopeMultipliers, saveMultiplier, reload: reloadScopeMults } = useArchScopeMultipliers()
    const { presets: dbDisciplinePresets, savePreset, reload: reloadPresets } = useArchDisciplinePresets()
    const [selectedCostRoleMarket, setSelectedCostRoleMarket] = useState('FL_RES')
    const {
        rolesByType,
        saveRole, createRole, deleteRole, updateGroupTitle, reload: reloadRoles,
        loading: rolesLoading,
    } = useArchCostRoles(selectedCostRoleMarket)
    // Clear local role edits when switching markets
    useEffect(() => {
        setLocalRoles({})
        setExpandedGroups(new Set(Object.keys(rolesByType)))
    }, [selectedCostRoleMarket])
    const { getMinFloors } = useArchMinFloors(rows)
    const [saving,        setSaving]        = useState(null)
    const [savedMkt,      setSavedMkt]      = useState(null)
    const [savingMinFloor, setSavingMinFloor] = useState(null)
    const [savedMinFloorMkt, setSavedMinFloorMkt] = useState(null)
    const [localCfg,      setLocalCfg]      = useState(uiDraft?.marketConfig || {})
    const [addMktOpen,    setAddMktOpen]    = useState(false)
    const [newMktKey,     setNewMktKey]     = useState('')
    const [newMktLabel,   setNewMktLabel]   = useState('')
    const [addingMkt,     setAddingMkt]     = useState(false)
    const [deleteModalMkt, setDeleteModalMkt] = useState(null)
    const [deletingMkt,   setDeletingMkt]   = useState(false)
    // Scope multipliers local state
    const [localScopeMults, setLocalScopeMults] = useState({})
    const [savingScope,     setSavingScope]     = useState(null)
    const [savedScopeMkt,   setSavedScopeMkt]   = useState(null)

    // Discipline presets local state
    const [localPresets,    setLocalPresets]    = useState({})
    const [savingPreset,    setSavingPreset]    = useState(null)
    const [savedPresetKey,  setSavedPresetKey]  = useState(null)

    // Cost roles local state
    const [localRoles,      setLocalRoles]      = useState({})
    const [savingRole,      setSavingRole]      = useState(null)
    const [savedRoleKey,    setSavedRoleKey]    = useState(null)
    const [expandedGroups,  setExpandedGroups]  = useState(() => new Set(Object.keys(rolesByType)))
    const [addingRoleGroup, setAddingRoleGroup] = useState(null)  // roleType currently adding a role to
    const [newRoleInputs,   setNewRoleInputs]   = useState({})   // { [roleType]: { label, pct } }
    const [addingGroup,     setAddingGroup]     = useState(false)
    const [newGroupName,    setNewGroupName]    = useState('')
    const [editingGroupTitle, setEditingGroupTitle] = useState(null) // roleType currently editing title
    const [groupTitleInputs, setGroupTitleInputs] = useState({})
    const [editingRoleLabel, setEditingRoleLabel] = useState(null) // { type, roleKey }
    const [roleLabelInputs, setRoleLabelInputs] = useState({})
    const [dragRole, setDragRole] = useState(null) // { roleType, roleKey, index }

    // Difficulty levels state
    const [showAddDiff,   setShowAddDiff]   = useState(false)
    const [newDiffValue,  setNewDiffValue]  = useState('')
    const [newDiffLabel,  setNewDiffLabel]  = useState('')
    const [newDiffDesc,   setNewDiffDesc]   = useState('')
    const [addingDiff,    setAddingDiff]    = useState(false)
    const [editingDiffId, setEditingDiffId]  = useState(null)
    const [editDiffDraft, setEditDiffDraft]  = useState({ value: '', label: '', desc: '' })

    // Civil difficulty levels state
    const [showAddCivilDiff,   setShowAddCivilDiff]   = useState(false)
    const [newCivilDiffValue,  setNewCivilDiffValue]  = useState('')
    const [newCivilDiffLabel,  setNewCivilDiffLabel]  = useState('')
    const [newCivilDiffDesc,   setNewCivilDiffDesc]   = useState('')
    const [addingCivilDiff,    setAddingCivilDiff]    = useState(false)
    const [editingCivilDiffId, setEditingCivilDiffId]  = useState(null)
    const [editCivilDiffDraft, setEditCivilDiffDraft]  = useState({ value: '', label: '', desc: '' })

    // Payment info state
    const [paymentFormOpen, setPaymentFormOpen] = useState(false)
    const [paymentEditingId, setPaymentEditingId] = useState(null)
    const [paymentForm, setPaymentForm] = useState({
        office_key: 'FL', bank_name: '', account_name: '', account_number: '',
        routing_number: '', swift_code: '', bank_address: '', additional_instructions: '',
        email: '', card_note: '', check_note: '', is_active: true,
    })
    const [paymentSaving, setPaymentSaving] = useState(false)
    const [paymentError, setPaymentError] = useState('')

    // Add/Alt/Rem difficulty editor state
    const [showAddAltDiff, setShowAddAltDiff] = useState(false)
    const [newAltDiffComponent, setNewAltDiffComponent] = useState('exist')
    const [newAltDiffValue, setNewAltDiffValue] = useState('')
    const [newAltDiffLabel, setNewAltDiffLabel] = useState('')
    const [newAltDiffDesc, setNewAltDiffDesc] = useState('')
    const [addingAltDiff, setAddingAltDiff] = useState(false)
    const [editingAltDiffId, setEditingAltDiffId] = useState(null)
    const [editAltDiffDraft, setEditAltDiffDraft] = useState({ value: '', label: '', desc: '', component: 'exist' })

    // Add-on config state
    const [showAddAddon,   setShowAddAddon]    = useState(false)
    const [newAddonKey,    setNewAddonKey]     = useState('')
    const [newAddonLabel,  setNewAddonLabel]   = useState('')
    const [newAddonAmount, setNewAddonAmount]  = useState('')
    const [newAddonField,  setNewAddonField]   = useState('name')
    const [newAddonOp,     setNewAddonOp]      = useState('equals')
    const [newAddonValue,  setNewAddonValue]   = useState('')
    const [newAddonEnabled, setNewAddonEnabled] = useState(false)
    const [newAddonExclusions, setNewAddonExclusions] = useState([])
    const [newExField, setNewExField] = useState('name')
    const [newExOp,    setNewExOp]    = useState('equals')
    const [newExValue, setNewExValue] = useState('')
    const [newAddonProjectTypes, setNewAddonProjectTypes] = useState([])
    const [newAddonPhases,       setNewAddonPhases]       = useState([])
    const [newAddonIncludeIds,   setNewAddonIncludeIds]   = useState([])
    const [newAddonExcludeIds,   setNewAddonExcludeIds]   = useState([])
    const [newAddonItemQuery,    setNewAddonItemQuery]    = useState('')
    const [addingAddon,    setAddingAddon]     = useState(false)
    const [editingAddonId, setEditingAddonId]   = useState(null)
    const [editAddonDraft, setEditAddonDraft]   = useState({ label: '', default_amount: '', rule_field: 'name', rule_op: 'equals', rule_value: '', enabled_by_default: false, rule_exclusions: [], project_types: [], phases: [], include_ids: [], exclude_ids: [] })
    const [editAddonItemQuery, setEditAddonItemQuery] = useState('')

    // Schedule preset state
    const [showAddPreset, setShowAddPreset] = useState(false)
    const [newPresetName, setNewPresetName] = useState('')
    const [newPresetRows, setNewPresetRows] = useState([{ label: '', pct: 0 }])
    const [addingPreset, setAddingPreset] = useState(false)
    const [editingPresetId, setEditingPresetId] = useState(null)
    const [editPresetDraft, setEditPresetDraft] = useState({ name: '', rows: [{ label: '', pct: 0 }] })

    // Catalog state — must be declared before any early return (Rules of Hooks)
    const [expandedItem,   setExpandedItem]   = useState(null)
    const [showAddItem,    setShowAddItem]     = useState(false)
    const [newItemName,    setNewItemName]     = useState('')
    const [newItemDisc,    setNewItemDisc]     = useState('')
    const [addingItem,     setAddingItem]      = useState(false)
    const [catalogQuery,   setCatalogQuery]   = useState('')
    const userId = session?.user?.id || null
    const [configSection,  setConfigSection]   = usePersistentState(`est_arch_config_sec_${userId}`, 'markets')

    // Migrate old split section IDs into the merged 'pricing' section
    useEffect(() => {
        if (['difficulty', 'payment_info', 'add_alt_difficulties'].includes(configSection)) {
            setConfigSection('pricing')
        }
    }, [configSection, setConfigSection])

    // Load market config into editor, but skip if a draft was restored
    useEffect(() => {
        if (rows.length === 0) return
        if (Object.keys(localCfg).length > 0) return
        const merged = {}
        marketOptions.forEach(m => { merged[m.key] = getMarketConfig(m.key) })
        setLocalCfg(merged)
    }, [rows, marketOptions, getMarketConfig]) // eslint-disable-line react-hooks/exhaustive-deps

    // Sync unsaved market config edits back to draft UI
    useEffect(() => {
        if (onUiChange && Object.keys(localCfg).length > 0) {
            onUiChange(prev => ({ ...prev, marketConfig: localCfg }))
        }
    }, [localCfg]) // eslint-disable-line react-hooks/exhaustive-deps

    // Sync DB scope multipliers into local state when they load
    useEffect(() => {
        if (Object.keys(dbScopeMultipliers).length === 0) return
        setLocalScopeMults(prev => {
            const next = {}
            for (const mkt of Object.keys(dbScopeMultipliers)) {
                next[mkt] = { ...dbScopeMultipliers[mkt], ...(prev[mkt] || {}) }
            }
            return next
        })
    }, [dbScopeMultipliers])

    // Sync DB discipline presets into local state when they load
    useEffect(() => {
        if (Object.keys(dbDisciplinePresets).length === 0) return
        setLocalPresets(prev => {
            const next = {}
            for (const key of Object.keys(dbDisciplinePresets)) {
                next[key] = { ...dbDisciplinePresets[key], ...(prev[key] || {}) }
            }
            return next
        })
    }, [dbDisciplinePresets])

    // Sync DB cost roles into local state when they load.
    // No merge with prev — avoids stale carry-over from old market causing lag.
    useEffect(() => {
        const allRoles = Object.values(rolesByType).flat()
        if (allRoles.length === 0) return
        setLocalRoles(() => {
            const next = {}
            for (const r of allRoles) {
                next[r.roleKey] = { pct: r.pct, labelKey: r.labelKey, is_enabled: r.is_enabled, discipline: r.discipline || 'ALL' }
            }
            return next
        })
    }, [rolesByType])

    // Payment form helpers
    const resetPaymentForm = () => {
        setPaymentForm({
            office_key: 'FL', bank_name: '', account_name: '', account_number: '',
            routing_number: '', swift_code: '', bank_address: '', additional_instructions: '',
            email: '', card_note: '', check_note: '', is_active: true,
        })
        setPaymentEditingId(null)
        setPaymentFormOpen(false)
        setPaymentError('')
    }
    const openEditPayment = (method) => {
        setPaymentForm({
            office_key: method.office_key || 'FL',
            bank_name: method.bank_name || '',
            account_name: method.account_name || '',
            account_number: method.account_number || '',
            routing_number: method.routing_number || '',
            swift_code: method.swift_code || '',
            bank_address: method.bank_address || '',
            additional_instructions: method.additional_instructions || '',
            email: method.email || '',
            card_note: method.card_note || '',
            check_note: method.check_note || '',
            is_active: method.is_active !== false,
        })
        setPaymentEditingId(method.id)
        setPaymentFormOpen(true)
        setPaymentError('')
    }

    const handleChange = (market, field, value) => {
        setLocalCfg(prev => ({
            ...prev,
            [market]: { ...prev[market], [field]: value }
        }))
    }

    const handleSave = async (market) => {
        setSaving(market)
        try {
            const cfg = localCfg[market] || getMarketConfig(market)
            await saveMarketConfig(market, {
                base_price_sqft:          Number(cfg.base_price_sqft)          || 0,
                add_alt_exist_base_price: Number(cfg.add_alt_exist_base_price) || 0,
                add_alt_rem_base_price:   Number(cfg.add_alt_rem_base_price)   || 0,
                add_alt_add_base_price:   Number(cfg.add_alt_add_base_price)   || 0,
                direct_cost_pct:          Number(cfg.direct_cost_pct) || 0,
                certifier_pct:            Number(cfg.certifier_pct)   || 0,
                operating_pct:            Number(cfg.operating_pct)   || 0,
                sales_pct:                Number(cfg.sales_pct)       || 0,
                franchise_pct:            Number(cfg.franchise_pct)   || 0,
                profit_pct:               Number(cfg.profit_pct)      || 0,
                contingency_pct:          Number(cfg.contingency_pct) || 0,
            })
            setSavedMkt(market)
            setTimeout(() => setSavedMkt(null), 2000)
        } catch (e) {
            alert(e.message)
        } finally {
            setSaving(null)
        }
    }

    const handleDeleteMarket = async () => {
        if (!deleteModalMkt) return
        setDeletingMkt(true)
        try {
            await deleteMarket(deleteModalMkt)
            setLocalCfg(prev => {
                const next = { ...prev }
                delete next[deleteModalMkt]
                return next
            })
            setDeleteModalMkt(null)
        } catch (e) {
            alert(e.message)
        } finally {
            setDeletingMkt(false)
        }
    }

    const handleSaveMinFloors = async (market) => {
        setSavingMinFloor(market)
        try {
            const cfg = localCfg[market] || getMarketConfig(market)
            await saveMarketConfig(market, {
                min_floor_exist:   Number(cfg.min_floor_exist)   || 0,
                min_floor_remodel: Number(cfg.min_floor_remodel) || 0,
                min_floor_adicion: Number(cfg.min_floor_adicion) || 0,
            })
            setSavedMinFloorMkt(market)
            setTimeout(() => setSavedMinFloorMkt(null), 2000)
        } catch (e) {
            alert(e.message)
        } finally {
            setSavingMinFloor(null)
        }
    }

    const handleSaveScopeMults = async (market) => {
        setSavingScope(market)
        try {
            const mults = localScopeMults[market] || {}
            for (const scopeKey of DISCIPLINE_SCOPE_OPTIONS.map(o => o.key)) {
                const val = mults[scopeKey]
                if (val != null) {
                    await saveMultiplier(market, scopeKey, Number(val))
                }
            }
            await reloadScopeMults()
            setSavedScopeMkt(market)
            setTimeout(() => setSavedScopeMkt(null), 2000)
        } catch (e) {
            alert(e.message)
        } finally {
            setSavingScope(null)
        }
    }

    const handleSavePreset = async (scopeKey) => {
        setSavingPreset(scopeKey)
        try {
            const p = localPresets[scopeKey] || dbDisciplinePresets[scopeKey] || SCOPE_OPTION_TO_PCT[scopeKey] || { archPct: 0, structPct: 0, mepPct: 0, civilPct: 0, adminPct: 0 }
            const presetSum = (Number(p.archPct) || 0) + (Number(p.structPct) || 0) + (Number(p.mepPct) || 0) + (Number(p.civilPct) || 0) + (Number(p.adminPct) || 0)
            if (Math.abs(presetSum - 100) > 0.01) {
                alert(t('admin.arch.pct_preset_save_blocked', { sum: presetSum }))
                return
            }
            await savePreset(scopeKey, {
                archPct:   Number(p.archPct)   || 0,
                structPct: Number(p.structPct) || 0,
                mepPct:    Number(p.mepPct)    || 0,
                civilPct:  Number(p.civilPct)  || 0,
                adminPct:  Number(p.adminPct)  || 0,
            })
            await reloadPresets()
            setSavedPresetKey(scopeKey)
            setTimeout(() => setSavedPresetKey(null), 2000)
        } catch (e) {
            alert(e.message)
        } finally {
            setSavingPreset(null)
        }
    }

    const handleSaveRoleSection = async (roleType, roles) => {
        setSavingRole(roleType)
        try {
            for (let i = 0; i < roles.length; i++) {
                const r = roles[i]
                const localVal = localRoles[r.roleKey]
                if (!localVal) continue
                await saveRole(roleType, r.roleKey, {
                    pct: Number(localVal.pct) || 0,
                    label_key: localVal.labelKey || r.labelKey,
                    is_enabled: localVal.is_enabled !== false,
                    sort_order: i + 1,
                    discipline: localVal.discipline || r.discipline || 'ALL',
                })
            }
            await reloadRoles()
            setSavedRoleKey(roleType)
            setTimeout(() => setSavedRoleKey(null), 2000)
        } catch (e) {
            alert(e.message)
        } finally {
            setSavingRole(null)
        }
    }

    const handleAddRole = async (roleType) => {
        const input = newRoleInputs[roleType]
        if (!input?.label?.trim()) return
        const slug = input.label.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
        const key = `${roleType}_${slug}_${Date.now()}`
        const newPct = Number(input.pct) || 0
        try {
            await createRole(roleType, key, {
                label_key: input.label.trim(),
                pct: newPct,
                is_enabled: true,
                discipline: input.discipline || 'ALL',
            })
            // Rebalance existing active roles proportionally
            const existing = (rolesByType[roleType] || []).filter(r => r.is_enabled !== false)
            const oldSum = existing.reduce((s, r) => s + r.pct, 0)
            if (existing.length > 0 && oldSum > 0) {
                const scale = (100 - newPct) / oldSum
                for (const r of existing) {
                    const rebalanced = Math.round(r.pct * scale * 1000) / 1000
                    await saveRole(roleType, r.roleKey, { pct: rebalanced })
                }
            }
            setNewRoleInputs(prev => ({ ...prev, [roleType]: { label: '', pct: '' } }))
            setAddingRoleGroup(null)
            await reloadRoles()
            setLocalRoles(prev => {
                const next = { ...prev }
                Object.keys(next).forEach(k => {
                    if ((rolesByType[roleType] || []).some(r => r.roleKey === k)) delete next[k]
                })
                return next
            })
        } catch (e) {
            alert(e.message)
        }
    }

    const handleDeleteRole = async (roleType, roleKey) => {
        if (!window.confirm(t('admin.arch.delete_role_confirm', { name: roleKey }))) return
        try {
            await deleteRole(roleType, roleKey)
            // Rebalance remaining active roles to sum to 100%
            const remaining = (rolesByType[roleType] || [])
                .filter(r => r.roleKey !== roleKey && r.is_enabled !== false)
            const remainingSum = remaining.reduce((s, r) => s + r.pct, 0)
            if (remaining.length > 0 && remainingSum > 0) {
                const scale = 100 / remainingSum
                for (const r of remaining) {
                    const rebalanced = Math.round(r.pct * scale * 1000) / 1000
                    await saveRole(roleType, r.roleKey, { pct: rebalanced })
                }
            }
            setLocalRoles(prev => {
                const next = { ...prev }
                Object.keys(next).forEach(k => {
                    if ((rolesByType[roleType] || []).some(r => r.roleKey === k)) delete next[k]
                })
                return next
            })
            await reloadRoles()
        } catch (e) {
            alert(e.message)
        }
    }

    const handleAddGroup = async () => {
        if (!newGroupName.trim()) return
        const type = newGroupName.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
        try {
            await createRole(type, `${type}_placeholder`, {
                label_key: newGroupName.trim(),
                pct: 0,
                is_enabled: true,
                group_title: newGroupName.trim(),
            })
            setNewGroupName('')
            setAddingGroup(false)
            await reloadRoles()
        } catch (e) {
            alert(e.message)
        }
    }

    const handleDeleteGroup = async (roleType) => {
        const title = rolesByType[roleType]?.[0]?.group_title || roleType
        if (!window.confirm(t('admin.arch.delete_group_confirm', { name: title }))) return
        try {
            const toDelete = (rolesByType[roleType] || []).map(r => r.roleKey)
            for (const rk of toDelete) await deleteRole(roleType, rk)
            await reloadRoles()
        } catch (e) {
            alert(e.message)
        }
    }

    const handleUpdateGroupTitle = async (roleType) => {
        const title = groupTitleInputs[roleType]
        if (title == null) return
        try {
            await updateGroupTitle(roleType, title)
            setEditingGroupTitle(null)
            await reloadRoles()
        } catch (e) {
            alert(e.message)
        }
    }

    const handleUpdateRoleLabel = async (roleType, roleKey) => {
        const label = roleLabelInputs[roleKey]
        if (label == null) return
        try {
            await saveRole(roleType, roleKey, { label_key: label })
            setEditingRoleLabel(null)
            await reloadRoles()
        } catch (e) {
            alert(e.message)
        }
    }

    // ── Auto-balance helpers ────────────────────────────────────────────────
    const getRolePct = (prev, r) => prev[r.roleKey]?.pct ?? r.pct
    const getRoleEnabled = (prev, r) => (prev[r.roleKey]?.is_enabled ?? r.is_enabled) !== false

    const balanceGroupPcts = (prev, groupType, changedKey, newPct) => {
        const groupRoles = rolesByType[groupType] || []
        const activeRoles = groupRoles.filter(r => getRoleEnabled(prev, r))
        if (activeRoles.length <= 1) {
            return { [changedKey]: { ...prev[changedKey], pct: newPct } }
        }
        let othersSum = 0
        for (const role of activeRoles) {
            if (role.roleKey !== changedKey) {
                othersSum += getRolePct(prev, role)
            }
        }
        const rest = 100 - newPct
        const updates = { [changedKey]: { ...prev[changedKey], pct: newPct } }
        if (othersSum <= 0) {
            const share = rest / (activeRoles.length - 1)
            for (const role of activeRoles) {
                if (role.roleKey !== changedKey) {
                    updates[role.roleKey] = { ...prev[role.roleKey], pct: Math.round(share * 10) / 10 }
                }
            }
        } else {
            for (const role of activeRoles) {
                if (role.roleKey !== changedKey) {
                    const oldPct = getRolePct(prev, role)
                    const scaled = rest * (oldPct / othersSum)
                    updates[role.roleKey] = { ...prev[role.roleKey], pct: Math.round(scaled * 10) / 10 }
                }
            }
        }
        return updates
    }

    const redistributeDisabledPct = (prev, groupType, disabledKey) => {
        const groupRoles = rolesByType[groupType] || []
        const remaining = groupRoles.filter(r => r.roleKey !== disabledKey && getRoleEnabled(prev, r))
        const disabledPct = getRolePct(prev, groupRoles.find(r => r.roleKey === disabledKey))
        if (remaining.length === 0 || disabledPct <= 0) {
            return { [disabledKey]: { ...prev[disabledKey], is_enabled: false, pct: 0 } }
        }
        let othersSum = 0
        for (const role of remaining) {
            othersSum += getRolePct(prev, role)
        }
        const updates = { [disabledKey]: { ...prev[disabledKey], is_enabled: false, pct: 0 } }
        if (othersSum > 0) {
            for (const role of remaining) {
                const oldPct = getRolePct(prev, role)
                const added = disabledPct * (oldPct / othersSum)
                updates[role.roleKey] = { ...prev[role.roleKey], pct: Math.round((oldPct + added) * 10) / 10 }
            }
        } else {
            const share = disabledPct / remaining.length
            for (const role of remaining) {
                updates[role.roleKey] = { ...prev[role.roleKey], pct: Math.round(share * 10) / 10 }
            }
        }
        return updates
    }

    const handleDragStart = (roleType, roleKey, index) => {
        setDragRole({ roleType, roleKey, index })
    }

    const handleDragOver = (e) => { e.preventDefault() }

    const handleDrop = (targetRoleType, targetIndex) => {
        if (!dragRole || dragRole.roleType !== targetRoleType) return
        const { index: srcIndex } = dragRole
        if (srcIndex === targetIndex) return
        const group = rolesByType[targetRoleType] || []
        const newGroup = [...group]
        const [moved] = newGroup.splice(srcIndex, 1)
        newGroup.splice(targetIndex, 0, moved)
        setLocalRoles(prev => {
            const next = { ...prev }
            newGroup.forEach((r, i) => {
                if (next[r.roleKey]) next[r.roleKey] = { ...next[r.roleKey], sort_order: i + 1 }
            })
            return next
        })
        setDragRole(null)
    }

    if (loading) return <p className="py-10 text-center text-gray-400">{t('admin.estimator.loading')}</p>
    if (error)   return <p className="py-10 text-center text-red-400">{error}</p>

    const fieldInput = (market, field, label, step = '0.01', prefix = '') => {  // eslint-disable-line
        const val = localCfg[market]?.[field] ?? getMarketConfig(market)[field] ?? ''
        return (
            <div>
                <label className="block mb-0.5 text-[11px] text-gray-500">{label}</label>
                <div className="flex gap-1 items-center">
                    {prefix && <span className="text-xs text-gray-400">{prefix}</span>}
                    <input
                        type="number"
                        step={step}
                        value={val}
                        onChange={e => handleChange(market, field, e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                </div>
            </div>
        )
    }

    // ── Catalog state ─────────────────────────────────────────────────────── (declared above early returns)

    const handleAddItem = async () => {
        if (!newItemName.trim()) return
        setAddingItem(true)
        try {
            await upsertItem({ name: newItemName.trim(), discipline: newItemDisc.trim().toUpperCase() || 'GENERAL', is_custom: true })
            setNewItemName(''); setNewItemDisc(''); setShowAddItem(false)
        } catch (e) { alert(e.message) }
        finally { setAddingItem(false) }
    }

    const handleAddMarket = async () => {
        const key = newMktKey.trim().toUpperCase()
        const lbl = newMktLabel.trim()
        if (!key || !lbl) return
        setAddingMkt(true)
        try {
            await createMarket(key, lbl)
            setNewMktKey(''); setNewMktLabel(''); setAddMktOpen(false)
        } catch (e) { alert(e.message) }
        finally { setAddingMkt(false) }
    }

    const handleAddDiff = async () => {
        if (!newDiffValue || !newDiffLabel.trim()) return
        setAddingDiff(true)
        try {
            await upsertDifficultyLevel({
                value: Number(newDiffValue),
                label: newDiffLabel.trim(),
                desc: newDiffDesc.trim(),
                sort_order: (difficultyLevels || []).length,
            })
            setNewDiffValue(''); setNewDiffLabel(''); setNewDiffDesc(''); setShowAddDiff(false)
            reloadDifficultyLevels()
        } catch (e) { alert(e.message) }
        finally { setAddingDiff(false) }
    }

    const handleSaveDiffEdit = async (id) => {
        if (!editDiffDraft.label.trim()) return
        try {
            await upsertDifficultyLevel({
                id,
                value: Number(editDiffDraft.value),
                label: editDiffDraft.label.trim(),
                desc: editDiffDraft.desc.trim(),
                sort_order: editDiffDraft.sort_order ?? 0,
            })
            setEditingDiffId(null)
            reloadDifficultyLevels()
        } catch (e) { alert(e.message) }
    }

    const handleRemoveDiff = async (id) => {
        if (!window.confirm(t('admin.arch.difficulty_delete_confirm'))) return
        try {
            await removeDifficultyLevel(id)
            reloadDifficultyLevels()
        } catch (e) { alert(e.message) }
    }

    const handleAddCivilDiff = async () => {
        if (!newCivilDiffValue || !newCivilDiffLabel.trim()) return
        setAddingCivilDiff(true)
        try {
            await upsertCivilLevel({
                value: Number(newCivilDiffValue),
                label: newCivilDiffLabel.trim(),
                desc: newCivilDiffDesc.trim(),
                sort_order: (civilLevels || []).length,
            })
            setNewCivilDiffValue(''); setNewCivilDiffLabel(''); setNewCivilDiffDesc(''); setShowAddCivilDiff(false)
            reloadCivilLevels()
        } catch (e) { alert(e.message) }
        finally { setAddingCivilDiff(false) }
    }

    const handleSaveCivilDiffEdit = async (id) => {
        if (!editCivilDiffDraft.label.trim()) return
        try {
            await upsertCivilLevel({
                id,
                value: Number(editCivilDiffDraft.value),
                label: editCivilDiffDraft.label.trim(),
                desc: editCivilDiffDraft.desc.trim(),
                sort_order: editCivilDiffDraft.sort_order ?? 0,
            })
            setEditingCivilDiffId(null)
            reloadCivilLevels()
        } catch (e) { alert(e.message) }
    }

    const handleRemoveCivilDiff = async (id) => {
        if (!window.confirm(t('admin.arch.difficulty_delete_confirm'))) return
        try {
            await removeCivilLevel(id)
            reloadCivilLevels()
        } catch (e) { alert(e.message) }
    }

    const CONFIG_SECTIONS = [
        { id: 'markets',    label: t('admin.arch.config_section_markets'),   icon: FaCog },
        { id: 'catalog',    label: t('admin.arch.config_section_catalog'),   icon: FaTag },
        { id: 'addons',     label: t('admin.arch.config_section_addons'),    icon: FaToggleOn },
        { id: 'pricing',    label: t('admin.arch.config_section_pricing'),   icon: FaCalculator },
        { id: 'parameters', label: t('admin.arch.config_section_parameters'), icon: FaCog },
        { id: 'schedules',  label: t('admin.arch.schedule_presets_title'),   icon: FaMoneyBillWave },
        { id: 'terms',      label: t('admin.arch.config_section_terms'),     icon: FaStickyNote },
        { id: 'logs',       label: t('admin.arch.config_section_logs'),       icon: FaHistory },
    ]

    return (
        <div className="space-y-6">
            {/* ── Inner segmented control ── */}
            <div className="flex gap-3 items-center">
                <div className="flex-1 flex overflow-x-auto [&::-webkit-scrollbar]:hidden rounded-xl bg-gray-100 p-1 gap-0.5 shadow-inner min-w-0">
                    {CONFIG_SECTIONS.map(({ id, label, icon: Icon }) => (
                        <button
                            key={id}
                            onClick={() => setConfigSection(id)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap shrink-0 ${
                                configSection === id
                                    ? 'bg-white text-gray-800 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <Icon size={11} />
                            {label}
                        </button>
                    ))}
                </div>
                {configSection === 'markets' && (
                    <div className="shrink-0">
                        <button
                            onClick={() => setAddMktOpen(true)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-gray-800 rounded-lg hover:bg-gray-700 transition-all"
                        >
                            <FaPlus size={9} /> {t('admin.arch.add_market')}
                        </button>
                    </div>
                )}
            </div>

            {/* New market modal */}
            <AnimatePresence>
                {addMktOpen && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="flex fixed inset-0 z-50 justify-center items-center px-4 bg-black/40"
                    >
                        <motion.div
                            initial={{ scale: 0.93, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.93, y: 12 }}
                            className="p-6 w-full max-w-sm bg-white rounded-2xl shadow-2xl"
                        >
                            <h3 className="mb-4 font-bold text-gray-800">{t('admin.arch.add_market')}</h3>
                            <div className="mb-5 space-y-3">
                                <div>
                                    <label className="block mb-1 text-xs font-medium text-gray-500">{t('admin.arch.market_key_label')}</label>
                                    <input
                                        value={newMktKey}
                                        onChange={e => {
                                            const v = e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '').replace(/_+/g, '_')
                                            const parts = v.split('_')
                                            const cleaned = parts.length > 2 ? parts.slice(0, 2).join('_') : v
                                            setNewMktKey(cleaned)
                                        }}
                                        placeholder={t('admin.arch.market_key_placeholder')}
                                        className="px-3 py-2 w-full font-mono text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
                                    />
                                </div>
                                <div>
                                    <label className="block mb-1 text-xs font-medium text-gray-500">{t('admin.arch.market_label_label')}</label>
                                    <input
                                        value={newMktLabel}
                                        onChange={e => setNewMktLabel(e.target.value)}
                                        placeholder={t('admin.arch.market_label_placeholder')}
                                        className="px-3 py-2 w-full text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={handleAddMarket}
                                    disabled={addingMkt || !newMktKey.trim() || !newMktLabel.trim()}
                                    className="flex-1 py-2.5 rounded-xl bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-all disabled:opacity-50"
                                >{addingMkt ? t('admin.estimator.saving') : t('admin.estimator.save')}</button>
                                <button
                                    onClick={() => { setAddMktOpen(false); setNewMktKey(''); setNewMktLabel('') }}
                                    className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-600 text-sm font-bold hover:bg-gray-200 transition-all"
                                >{t('admin.arch.cancel')}</button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {configSection === 'markets' && marketOptions.map(mkt => {
                const cfg = localCfg[mkt.key] || getMarketConfig(mkt.key)
                return (
                    <div key={mkt.key} className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h3 className="font-bold text-gray-800">{mkt.label}</h3>
                                <p className="text-xs text-gray-400">{mkt.key}</p>
                            </div>
                            <div className="flex gap-2 items-center">
                                {!['FL_RES','FL_COM','TX_RES','TX_COM'].includes(mkt.key) && (
                                    <button
                                        onClick={() => setDeleteModalMkt(mkt.key)}
                                        title={t('admin.arch.delete_market_button')}
                                        className="flex gap-1.5 items-center px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 rounded-lg border border-red-200 hover:bg-red-100 transition-all"
                                    >
                                        <FaTrash size={10} />
                                    </button>
                                )}
                                <button
                                    onClick={() => handleSave(mkt.key)}
                                    disabled={saving === mkt.key}
                                    className="flex gap-1.5 items-center px-3 py-1.5 text-xs font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 transition-all disabled:opacity-60"
                                >
                                    {savedMkt === mkt.key ? <FaCheck size={10} /> : <FaSave size={10} />}
                                    {saving === mkt.key ? t('admin.estimator.saving') : savedMkt === mkt.key ? t('admin.arch.saved') : t('admin.estimator.save')}
                                </button>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                            {fieldInput(mkt.key, 'base_price_sqft',          t('admin.arch.config_base_sqft'),               '0.0001', '$')}
                            {fieldInput(mkt.key, 'add_alt_exist_base_price', t('admin.arch.config_add_alt_exist_base_sqft'), '0.0001', '$')}
                            {fieldInput(mkt.key, 'add_alt_rem_base_price',   t('admin.arch.config_add_alt_rem_base_sqft'),   '0.0001', '$')}
                            {fieldInput(mkt.key, 'add_alt_add_base_price',   t('admin.arch.config_add_alt_add_base_sqft'),   '0.0001', '$')}
                            {fieldInput(mkt.key, 'direct_cost_pct',         t('admin.arch.config_direct_pct'),      '0.001')}
                            {fieldInput(mkt.key, 'certifier_pct',           t('admin.arch.config_certifier_pct'),  '0.001')}
                            {fieldInput(mkt.key, 'operating_pct',           t('admin.arch.config_operating_pct'),  '0.001')}
                            {fieldInput(mkt.key, 'sales_pct',               t('admin.arch.config_sales_pct'),      '0.001')}
                            {fieldInput(mkt.key, 'franchise_pct',           t('admin.arch.config_franchise_pct'),  '0.001')}
                            {fieldInput(mkt.key, 'profit_pct',              t('admin.arch.config_profit_pct'),     '0.001')}
                            {fieldInput(mkt.key, 'contingency_pct',         t('admin.arch.config_contingency_pct'),'0.001')}
                        </div>
                    </div>
                )
            })}

            {/* Delete market confirmation modal */}
            <AnimatePresence>
                {deleteModalMkt && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="flex fixed inset-0 z-50 justify-center items-center px-4 bg-black/40"
                    >
                        <motion.div
                            initial={{ scale: 0.93, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.93, y: 12 }}
                            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                            className="p-6 w-full max-w-sm bg-white rounded-2xl shadow-2xl"
                        >
                            <h3 className="mb-2 text-lg font-bold text-gray-800">{t('admin.arch.delete_market_title')}</h3>
                            <p className="mb-6 text-sm text-gray-600">
                                {t('admin.arch.delete_market_confirm', { name: marketOptions.find(m => m.key === deleteModalMkt)?.label || deleteModalMkt })}
                            </p>
                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={() => setDeleteModalMkt(null)}
                                    className="px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 rounded-lg transition-all hover:bg-gray-200"
                                >
                                    {t('admin.arch.delete_market_cancel')}
                                </button>
                                <button
                                    onClick={handleDeleteMarket}
                                    disabled={deletingMkt}
                                    className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg transition-all hover:bg-red-700 disabled:opacity-60"
                                >
                                    {deletingMkt ? '…' : t('admin.arch.delete_market_button')}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── Arch Catalog Items ── */}
            {canEditCatalog && configSection === 'catalog' && (
                <div className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
                    <div className="flex justify-between items-center mb-4">
                        <div>
                            <h3 className="flex gap-2 items-center font-bold text-gray-800">
                                <FaTag size={13} className="text-primary" />
                                {t('admin.arch.catalog.title')}
                            </h3>
                            <p className="text-xs text-gray-400 mt-0.5">{t('admin.arch.catalog.subtitle')}</p>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={reloadCatalog} className="p-1.5 text-gray-400 rounded-lg border border-gray-200 hover:bg-gray-50 transition-all"><FaSync size={11} /></button>
                            <button onClick={() => setShowAddItem(!showAddItem)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-primary rounded-lg hover:bg-primary/90 transition-all">
                                <FaPlus size={10} /> {t('admin.arch.catalog.add_item')}
                            </button>
                        </div>
                    </div>

                    {/* Add item form */}
                    <AnimatePresence>
                        {showAddItem && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                                <div className="flex flex-col gap-2 items-start p-3 mb-4 bg-gray-50 rounded-xl border border-gray-200 sm:flex-row sm:items-end">
                                    <div className="flex-1">
                                        <label className="block mb-1 text-[10px] text-gray-500 uppercase tracking-wide">{t('admin.arch.catalog.item_name')}</label>
                                        <input value={newItemName} onChange={e => setNewItemName(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && handleAddItem()}
                                            placeholder="e.g. Architectural Floor Plans"
                                            className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                                    </div>
                                    <div className="w-full sm:w-44">
                                        <label className="block mb-1 text-[10px] text-gray-500 uppercase tracking-wide">{t('admin.arch.catalog.discipline')}</label>
                                        <input list="arch-discipline-list-add" value={newItemDisc} onChange={e => setNewItemDisc(e.target.value.toUpperCase())}
                                            placeholder="ARCHITECTURE"
                                            className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                                        <datalist id="arch-discipline-list-add">
                                            {['ARCH','STRUCT','MEP','3D','CIVIL','ECC','ADMIN'].map(d => <option key={d} value={d} />)}
                                        </datalist>
                                    </div>
                                    <div className="flex gap-2 shrink-0">
                                        <button onClick={handleAddItem} disabled={addingItem || !newItemName.trim()}
                                            className="px-3 py-1.5 bg-primary text-white text-xs font-bold rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-all">
                                            {addingItem ? '…' : t('admin.arch.catalog.save')}
                                        </button>
                                        <button onClick={() => setShowAddItem(false)} className="p-1.5 text-gray-400 hover:text-gray-600 transition-all">
                                            <FaTimes size={12} />
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Search catalog */}
                    <div className="mb-3">
                        <div className="relative">
                            <input
                                value={catalogQuery}
                                onChange={e => setCatalogQuery(e.target.value)}
                                placeholder={t('admin.arch.catalog.search')}
                                className="px-3 py-2 pl-9 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20"
                            />
                            <span className="absolute left-3 top-1/2 text-xs text-gray-400 -translate-y-1/2">
                                🔍
                            </span>
                        </div>
                    </div>

                    {(() => {
                        const q = catalogQuery.trim().toLowerCase()
                        const filtered = q ? catalogItems.filter(item => (item.name || '').toLowerCase().includes(q)) : catalogItems
                        if (filtered.length === 0) {
                            return <p className="py-6 text-sm italic text-center text-gray-400">{q ? t('admin.arch.catalog.no_results') : t('admin.arch.catalog.empty')}</p>
                        }
                        return (
                            <div className="overflow-hidden rounded-xl border border-gray-200 divide-y divide-gray-100">
                                {filtered.map(item => (
                                    <ArchCatalogItemRow
                                        key={item.id}
                                        item={item}
                                        isExpanded={expandedItem === item.id}
                                        onToggle={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
                                        onUpsert={upsertItem}
                                        onRemove={removeItem}
                                        onReload={reloadCatalog}
                                    />
                                ))}
                            </div>
                        )
                    })()}
                </div>
            )}

            {/* ── Add-ons ── */}
            {configSection === 'addons' && (
                <div className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
                    <div className="flex flex-wrap gap-2 justify-between items-center mb-4">
                        <div className="min-w-0">
                            <h3 className="flex gap-2 items-center font-bold text-gray-800">
                                <FaToggleOn size={13} className="text-primary" />
                                {t('admin.arch.addons_title')}
                            </h3>
                            <p className="text-xs text-gray-400 mt-0.5">{t('admin.arch.addons_subtitle')}</p>
                        </div>
                        <div className="flex gap-2 shrink-0">
                            <button onClick={reloadAddons} className="p-1.5 text-gray-400 rounded-lg border border-gray-200 hover:bg-gray-50 transition-all"><FaSync size={11} /></button>
                            <button onClick={() => setShowAddAddon(!showAddAddon)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-primary rounded-lg hover:bg-primary/90 transition-all">
                                <FaPlus size={10} /> {t('admin.arch.add_addon')}
                            </button>
                        </div>
                    </div>

                    <AnimatePresence>
                        {showAddAddon && (
                            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                <div className="p-4 mb-4 space-y-3 bg-gray-50 rounded-xl border border-gray-200">
                                    {/* Basic fields */}
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                                        <input type="text" placeholder={t('admin.arch.addon_key')} value={newAddonKey}
                                            onChange={e => {
                                                const v = e.target.value.toLowerCase().replace(/[^a-z0-9_\s-]/g, '').replace(/[\s-]+/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '')
                                                setNewAddonKey(v)
                                            }}
                                            className="px-3 py-2 font-mono text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20"
                                        />
                                        <input type="text" placeholder={t('admin.arch.addon_label_placeholder')} value={newAddonLabel} onChange={e => setNewAddonLabel(e.target.value)} className="px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20" />
                                        <input type="number" placeholder={t('admin.arch.addon_default_amount')} value={newAddonAmount} onChange={e => setNewAddonAmount(e.target.value)} className="px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20" />
                                        <div className="flex flex-wrap gap-2 items-center">
                                            <select value={newAddonField} onChange={e => setNewAddonField(e.target.value)} className="px-2 py-2 min-w-0 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20">
                                                <option value="name">name</option>
                                                <option value="discipline">discipline</option>
                                            </select>
                                            <select value={newAddonOp} onChange={e => setNewAddonOp(e.target.value)} className="px-2 py-2 min-w-0 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20">
                                                <option value="equals">=</option>
                                                <option value="startsWith">starts</option>
                                                <option value="contains">contains</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <input type="text" placeholder={t('admin.arch.addon_rule_value')} value={newAddonValue} onChange={e => setNewAddonValue(e.target.value)} className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20" />
                                        <label className="flex items-center gap-1.5 text-xs text-gray-600 shrink-0">
                                            <input type="checkbox" checked={newAddonEnabled} onChange={e => setNewAddonEnabled(e.target.checked)} className="rounded" />
                                            {t('admin.arch.addon_enabled_default')}
                                        </label>
                                    </div>
                                    {/* Exclusions */}
                                    <div className="space-y-1.5">
                                        <p className="text-[10px] font-bold tracking-wide text-gray-500 uppercase">{t('admin.arch.addon_exclusions')}</p>
                                        <div className="flex flex-wrap gap-1">
                                            {(newAddonExclusions || []).map((ex, idx) => (
                                                <span key={idx} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-red-50 text-red-600 border border-red-100">
                                                    {ex.field} {ex.op} "{ex.value}"
                                                    <button onClick={() => setNewAddonExclusions(prev => prev.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600"><FaTimes size={8} /></button>
                                                </span>
                                            ))}
                                            {newAddonExclusions.length === 0 && <span className="text-[11px] text-gray-400 italic">{t('admin.arch.addon_no_exclusions')}</span>}
                                        </div>
                                        <div className="flex flex-wrap gap-2 items-center">
                                            <select value={newExField} onChange={e => setNewExField(e.target.value)} className="px-2 py-1 min-w-0 text-xs rounded-lg border border-gray-200">
                                                <option value="name">name</option>
                                                <option value="discipline">discipline</option>
                                            </select>
                                            <select value={newExOp} onChange={e => setNewExOp(e.target.value)} className="px-2 py-1 min-w-0 text-xs rounded-lg border border-gray-200">
                                                <option value="equals">=</option>
                                                <option value="startsWith">starts</option>
                                                <option value="contains">contains</option>
                                            </select>
                                            <input type="text" placeholder={t('admin.arch.addon_ex_value')} value={newExValue} onChange={e => setNewExValue(e.target.value)} className="flex-1 min-w-[120px] px-2 py-1 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary/20" />
                                            <button onClick={() => { if (!newExValue.trim()) return; setNewAddonExclusions(prev => [...prev, { field: newExField, op: newExOp, value: newExValue.trim() }]); setNewExValue('') }} className="px-2 py-1 text-xs font-bold rounded-lg border transition-all text-primary border-primary/30 hover:bg-primary/5 shrink-0">{t('admin.arch.add_exclusion')}</button>
                                        </div>
                                    </div>
                                    {/* Restrictions */}
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                        <div>
                                            <p className="text-[10px] font-bold tracking-wide text-gray-500 uppercase">{t('admin.arch.addon_project_types')}</p>
                                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                                                {ARCH_PROJECT_TYPES.map(pt => {
                                                    const selected = newAddonProjectTypes.includes(pt.key)
                                                    return (
                                                        <button key={pt.key} onClick={() => setNewAddonProjectTypes(prev => selected ? prev.filter(k => k !== pt.key) : [...prev, pt.key])} className={`px-2 py-1 rounded-lg text-xs font-medium transition-all ${selected ? 'text-white bg-primary' : 'text-gray-500 bg-gray-100 hover:bg-gray-200'}`}>
                                                            {t(pt.labelKey) || pt.key}
                                                        </button>
                                                    )
                                                })}
                                            </div>
                                            {newAddonProjectTypes.length === 0 && <p className="mt-1 text-[10px] text-gray-400 italic">{t('admin.arch.any_project_type')}</p>}
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-bold tracking-wide text-gray-500 uppercase">{t('admin.arch.addon_phases')}</p>
                                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                                                {ARCH_PHASES.map(ph => {
                                                    const selected = newAddonPhases.includes(ph.key)
                                                    return (
                                                        <button key={ph.key} onClick={() => setNewAddonPhases(prev => selected ? prev.filter(k => k !== ph.key) : [...prev, ph.key])} className={`px-2 py-1 rounded-lg text-xs font-medium transition-all ${selected ? 'text-white bg-primary' : 'text-gray-500 bg-gray-100 hover:bg-gray-200'}`}>
                                                            {t(ph.labelKey) || ph.key}
                                                        </button>
                                                    )
                                                })}
                                            </div>
                                            {newAddonPhases.length === 0 && <p className="mt-1 text-[10px] text-gray-400 italic">{t('admin.arch.any_phase')}</p>}
                                        </div>
                                    </div>
                                    {/* Preview matched deliverables */}
                                    {(() => {
                                        const previewAddon = { rule_field: newAddonField, rule_op: newAddonOp, rule_value: newAddonValue, rule_exclusions: newAddonExclusions.length ? newAddonExclusions : null, project_types: newAddonProjectTypes, phases: newAddonPhases, include_ids: newAddonIncludeIds, exclude_ids: newAddonExcludeIds }
                                        const previewPt = newAddonProjectTypes[0] || 'res_new'
                                        const previewPh = newAddonPhases[0] || 'cds'
                                        const ids = newAddonValue.trim() ? getAddonScopeItemIds(previewAddon, catalogItems, previewPt, previewPh) : new Set()
                                        const items = catalogItems.filter(ci => ids.has(ci.id))
                                        const excludedItems = catalogItems.filter(ci => newAddonExcludeIds.includes(ci.id))
                                        const includedItems = catalogItems.filter(ci => newAddonIncludeIds.includes(ci.id))
                                        const searchItems = newAddonItemQuery.trim().length >= 2
                                            ? catalogItems.filter(ci => !ids.has(ci.id) && !newAddonExcludeIds.includes(ci.id) && !newAddonIncludeIds.includes(ci.id) && ci.name.toLowerCase().includes(newAddonItemQuery.toLowerCase())).slice(0, 8)
                                            : []
                                        return (
                                            <div className="space-y-2">
                                                {/* Final deliverable list */}
                                                <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-100">
                                                    <div className="flex gap-2 items-center mb-1.5">
                                                        <p className="text-[10px] font-bold tracking-wide text-gray-500 uppercase">{t('admin.arch.addon_preview_title')}</p>
                                                        <span className="text-[10px] text-gray-400">({items.length})</span>
                                                    </div>
                                                    {items.length === 0 ? (
                                                        <p className="text-[11px] text-gray-400 italic">{t('admin.arch.addon_preview_empty')}</p>
                                                    ) : (
                                                        <div className="flex flex-wrap gap-1">
                                                            {items.map(ci => (
                                                                <span key={ci.id} className="group/chip inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-white border border-gray-200 text-gray-600">
                                                                    {ci.name}
                                                                    <button onClick={() => setNewAddonExcludeIds(prev => [...prev, ci.id])} className="ml-0.5 text-gray-300 hover:text-red-400 opacity-0 group-hover/chip:opacity-100 transition-opacity" title={t('admin.arch.addon_remove_item')}><FaTimes size={7} /></button>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                {/* Manually excluded */}
                                                {excludedItems.length > 0 && (
                                                    <div className="p-2.5 rounded-lg bg-red-50/60 border border-red-100">
                                                        <p className="text-[10px] font-bold tracking-wide text-red-400 uppercase mb-1.5">{t('admin.arch.addon_excluded')}</p>
                                                        <div className="flex flex-wrap gap-1">
                                                            {excludedItems.map(ci => (
                                                                <span key={ci.id} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-white border border-red-100 text-red-500">
                                                                    {ci.name}
                                                                    <button onClick={() => setNewAddonExcludeIds(prev => prev.filter(id => id !== ci.id))} className="ml-0.5 text-red-300 hover:text-red-500"><FaTimes size={7} /></button>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {/* Manually included */}
                                                {includedItems.length > 0 && (
                                                    <div className="p-2.5 rounded-lg bg-emerald-50/60 border border-emerald-100">
                                                        <p className="text-[10px] font-bold tracking-wide text-emerald-500 uppercase mb-1.5">{t('admin.arch.addon_included')}</p>
                                                        <div className="flex flex-wrap gap-1">
                                                            {includedItems.map(ci => (
                                                                <span key={ci.id} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-white border border-emerald-100 text-emerald-600">
                                                                    {ci.name}
                                                                    <button onClick={() => setNewAddonIncludeIds(prev => prev.filter(id => id !== ci.id))} className="ml-0.5 text-emerald-300 hover:text-emerald-600"><FaTimes size={7} /></button>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {/* Search to add more */}
                                                <div className="relative">
                                                    <input type="text" placeholder={t('admin.arch.addon_search_items')} value={newAddonItemQuery} onChange={e => setNewAddonItemQuery(e.target.value)} className="w-full px-3 py-1.5 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary/20" />
                                                    {searchItems.length > 0 && (
                                                        <div className="overflow-y-auto absolute z-10 mt-1 w-full max-h-40 bg-white rounded-lg border border-gray-200 shadow-lg">
                                                            {searchItems.map(ci => (
                                                                <button key={ci.id} onClick={() => { setNewAddonIncludeIds(prev => [...prev, ci.id]); setNewAddonItemQuery('') }} className="flex justify-between items-center w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50">
                                                                    <span className="text-gray-700">{ci.name}</span>
                                                                    <span className="text-[9px] text-gray-400 uppercase">{ci.discipline}</span>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    })()}
                                    {/* Actions */}
                                    <div className="flex gap-2 justify-end pt-1">
                                        <button onClick={async () => {
                                            setAddingAddon(true)
                                            try {
                                                const nextSort = addonDefs.length > 0 ? Math.max(...addonDefs.map(d => d.sort_order ?? 0)) + 1 : 0
                                                await createArchAddon({ key: newAddonKey, label: newAddonLabel, default_amount: Number(newAddonAmount) || 0, rule_field: newAddonField, rule_op: newAddonOp, rule_value: newAddonValue, enabled_by_default: newAddonEnabled, rule_exclusions: newAddonExclusions.length ? newAddonExclusions : null, include_ids: newAddonIncludeIds, exclude_ids: newAddonExcludeIds, project_types: newAddonProjectTypes, phases: newAddonPhases, sort_order: nextSort })
                                                setNewAddonKey(''); setNewAddonLabel(''); setNewAddonAmount(''); setNewAddonField('name'); setNewAddonOp('equals'); setNewAddonValue(''); setNewAddonEnabled(false); setNewAddonExclusions([]); setNewAddonProjectTypes([]); setNewAddonPhases([]); setNewAddonIncludeIds([]); setNewAddonExcludeIds([]); setNewAddonItemQuery(''); setNewExField('name'); setNewExOp('equals'); setNewExValue('')
                                                setShowAddAddon(false)
                                            } catch (e) { alert(e.message) }
                                            finally { setAddingAddon(false) }
                                        }} disabled={addingAddon || !newAddonKey.trim() || !newAddonLabel.trim() || !newAddonValue.trim() || addonDefs.some(a => a.key === newAddonKey.trim())} className="px-3 py-2 text-xs font-bold text-white rounded-lg transition-all bg-primary hover:bg-primary/90 disabled:opacity-40 shrink-0">
                                            {addingAddon ? <FaSpinner className="animate-spin" size={10} /> : t('admin.arch.add_addon')}
                                        </button>
                                        <button onClick={() => setShowAddAddon(false)} className="px-3 py-2 text-xs font-bold text-gray-500 rounded-lg border border-gray-200 transition-all hover:bg-gray-50 shrink-0">{t('admin.arch.cancel')}</button>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <div className="space-y-2">
                        {addonDefs.length === 0 && (
                            <p className="py-8 text-sm text-center text-gray-400">{t('admin.arch.addons_empty')}</p>
                        )}
                        {addonDefs.map(a => {
                            const isEditing = editingAddonId === a.id
                            const ruleText = `${a.rule_field} ${a.rule_op} "${a.rule_value}"`
                            const exList = (a.rule_exclusions || []).map(ex => `${ex.field} ${ex.op} "${ex.value}"`).join(', ')
                            const ptText = (a.project_types || []).join(', ')
                            const phText = (a.phases || []).join(', ')
                            return (
                                <div key={a.id} className={`group flex gap-3 items-start p-3 rounded-xl border ${a.is_system ? 'bg-gray-50 border-gray-100' : 'bg-white border-gray-200'}`}>
                                    {isEditing ? (
                                        <div className="flex-1 space-y-2">
                                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                                <input type="text" value={editAddonDraft.label} onChange={e => setEditAddonDraft(p => ({ ...p, label: e.target.value }))} className="px-2 py-1 min-w-0 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary/30" />
                                                <input type="number" value={editAddonDraft.default_amount} onChange={e => setEditAddonDraft(p => ({ ...p, default_amount: e.target.value }))} className="px-2 py-1 min-w-0 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary/30" />
                                                <div className="flex flex-wrap gap-1">
                                                    <select value={editAddonDraft.rule_field} onChange={e => setEditAddonDraft(p => ({ ...p, rule_field: e.target.value }))} className="px-2 py-1 min-w-0 text-xs rounded-lg border border-gray-200">
                                                        <option value="name">name</option>
                                                        <option value="discipline">discipline</option>
                                                    </select>
                                                    <select value={editAddonDraft.rule_op} onChange={e => setEditAddonDraft(p => ({ ...p, rule_op: e.target.value }))} className="px-2 py-1 min-w-0 text-xs rounded-lg border border-gray-200">
                                                        <option value="equals">equals</option>
                                                        <option value="startsWith">startsWith</option>
                                                        <option value="contains">contains</option>
                                                    </select>
                                                </div>
                                                <input type="text" value={editAddonDraft.rule_value} onChange={e => setEditAddonDraft(p => ({ ...p, rule_value: e.target.value }))} className="px-2 py-1 min-w-0 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary/30" />
                                                <label className="flex items-center gap-1.5 text-xs text-gray-600">
                                                    <input type="checkbox" checked={editAddonDraft.enabled_by_default} onChange={e => setEditAddonDraft(p => ({ ...p, enabled_by_default: e.target.checked }))} className="rounded" />
                                                    {t('admin.arch.addon_enabled_default')}
                                                </label>
                                            </div>
                                            {/* Edit exclusions */}
                                            <div className="space-y-1">
                                                <p className="text-[10px] font-bold tracking-wide text-gray-500 uppercase">{t('admin.arch.addon_exclusions')}</p>
                                                <div className="flex flex-wrap gap-1">
                                                    {(editAddonDraft.rule_exclusions || []).map((ex, idx) => (
                                                        <span key={idx} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-red-50 text-red-600 border border-red-100">
                                                            {ex.field} {ex.op} "{ex.value}"
                                                            <button onClick={() => setEditAddonDraft(p => ({ ...p, rule_exclusions: (p.rule_exclusions || []).filter((_, i) => i !== idx) }))} className="text-red-400 hover:text-red-600"><FaTimes size={8} /></button>
                                                        </span>
                                                    ))}
                                                    {(!editAddonDraft.rule_exclusions || editAddonDraft.rule_exclusions.length === 0) && <span className="text-[11px] text-gray-400 italic">{t('admin.arch.addon_no_exclusions')}</span>}
                                                </div>
                                                <div className="flex flex-wrap gap-2 items-center">
                                                    <select value={editAddonDraft._exField || 'name'} onChange={e => setEditAddonDraft(p => ({ ...p, _exField: e.target.value }))} className="px-2 py-1 min-w-0 text-xs rounded-lg border border-gray-200">
                                                        <option value="name">name</option>
                                                        <option value="discipline">discipline</option>
                                                    </select>
                                                    <select value={editAddonDraft._exOp || 'equals'} onChange={e => setEditAddonDraft(p => ({ ...p, _exOp: e.target.value }))} className="px-2 py-1 min-w-0 text-xs rounded-lg border border-gray-200">
                                                        <option value="equals">=</option>
                                                        <option value="startsWith">starts</option>
                                                        <option value="contains">contains</option>
                                                    </select>
                                                    <input type="text" placeholder={t('admin.arch.addon_ex_value')} value={editAddonDraft._exValue || ''} onChange={e => setEditAddonDraft(p => ({ ...p, _exValue: e.target.value }))} className="flex-1 min-w-[120px] px-2 py-1 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary/20" />
                                                    <button onClick={() => {
                                                        const val = (editAddonDraft._exValue || '').trim()
                                                        if (!val) return
                                                        const ex = { field: editAddonDraft._exField || 'name', op: editAddonDraft._exOp || 'equals', value: val }
                                                        setEditAddonDraft(p => ({ ...p, rule_exclusions: [...(p.rule_exclusions || []), ex], _exValue: '' }))
                                                    }} className="px-2 py-1 text-xs font-bold rounded-lg border transition-all text-primary border-primary/30 hover:bg-primary/5 shrink-0">{t('admin.arch.add_exclusion')}</button>
                                                </div>
                                            </div>
                                            {/* Edit restrictions */}
                                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                                <div>
                                                    <p className="text-[10px] font-bold tracking-wide text-gray-500 uppercase">{t('admin.arch.addon_project_types')}</p>
                                                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                                                        {ARCH_PROJECT_TYPES.map(pt => {
                                                            const selected = (editAddonDraft.project_types || []).includes(pt.key)
                                                            return (
                                                                <button key={pt.key} onClick={() => setEditAddonDraft(p => {
                                                                    const arr = p.project_types || []
                                                                    return { ...p, project_types: selected ? arr.filter(k => k !== pt.key) : [...arr, pt.key] }
                                                                })} className={`px-2 py-1 rounded-lg text-xs font-medium transition-all ${selected ? 'text-white bg-primary' : 'text-gray-500 bg-gray-100 hover:bg-gray-200'}`}>
                                                                    {t(pt.labelKey) || pt.key}
                                                                </button>
                                                            )
                                                        })}
                                                    </div>
                                                    {(editAddonDraft.project_types || []).length === 0 && <p className="mt-1 text-[10px] text-gray-400 italic">{t('admin.arch.any_project_type')}</p>}
                                                </div>
                                                <div>
                                                    <p className="text-[10px] font-bold tracking-wide text-gray-500 uppercase">{t('admin.arch.addon_phases')}</p>
                                                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                                                        {ARCH_PHASES.map(ph => {
                                                            const selected = (editAddonDraft.phases || []).includes(ph.key)
                                                            return (
                                                                <button key={ph.key} onClick={() => setEditAddonDraft(p => {
                                                                    const arr = p.phases || []
                                                                    return { ...p, phases: selected ? arr.filter(k => k !== ph.key) : [...arr, ph.key] }
                                                                })} className={`px-2 py-1 rounded-lg text-xs font-medium transition-all ${selected ? 'text-white bg-primary' : 'text-gray-500 bg-gray-100 hover:bg-gray-200'}`}>
                                                                    {t(ph.labelKey) || ph.key}
                                                                </button>
                                                            )
                                                        })}
                                                    </div>
                                                    {(editAddonDraft.phases || []).length === 0 && <p className="mt-1 text-[10px] text-gray-400 italic">{t('admin.arch.any_phase')}</p>}
                                                </div>
                                            </div>
                                            {/* Preview matched deliverables */}
                                            {(() => {
                                                const previewAddon = { rule_field: editAddonDraft.rule_field, rule_op: editAddonDraft.rule_op, rule_value: editAddonDraft.rule_value, rule_exclusions: (editAddonDraft.rule_exclusions || []).length ? editAddonDraft.rule_exclusions : null, project_types: editAddonDraft.project_types || [], phases: editAddonDraft.phases || [], include_ids: editAddonDraft.include_ids || [], exclude_ids: editAddonDraft.exclude_ids || [] }
                                                const previewPt = (editAddonDraft.project_types || [])[0] || 'res_new'
                                                const previewPh = (editAddonDraft.phases || [])[0] || 'cds'
                                                const ids = (editAddonDraft.rule_value || '').trim() ? getAddonScopeItemIds(previewAddon, catalogItems, previewPt, previewPh) : new Set()
                                                const items = catalogItems.filter(ci => ids.has(ci.id))
                                                const excludedItems = catalogItems.filter(ci => (editAddonDraft.exclude_ids || []).includes(ci.id))
                                                const includedItems = catalogItems.filter(ci => (editAddonDraft.include_ids || []).includes(ci.id))
                                                const incSet = new Set(editAddonDraft.include_ids || [])
                                                const excSet = new Set(editAddonDraft.exclude_ids || [])
                                                const searchItems = editAddonItemQuery.trim().length >= 2
                                                    ? catalogItems.filter(ci => !ids.has(ci.id) && !excSet.has(ci.id) && !incSet.has(ci.id) && ci.name.toLowerCase().includes(editAddonItemQuery.toLowerCase())).slice(0, 8)
                                                    : []
                                                return (
                                                    <div className="space-y-2">
                                                        <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-100">
                                                            <div className="flex gap-2 items-center mb-1.5">
                                                                <p className="text-[10px] font-bold tracking-wide text-gray-500 uppercase">{t('admin.arch.addon_preview_title')}</p>
                                                                <span className="text-[10px] text-gray-400">({items.length})</span>
                                                            </div>
                                                            {items.length === 0 ? (
                                                                <p className="text-[11px] text-gray-400 italic">{t('admin.arch.addon_preview_empty')}</p>
                                                            ) : (
                                                                <div className="flex flex-wrap gap-1">
                                                                    {items.map(ci => (
                                                                        <span key={ci.id} className="group/chip inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-white border border-gray-200 text-gray-600">
                                                                            {ci.name}
                                                                            <button onClick={() => setEditAddonDraft(p => ({ ...p, exclude_ids: [...(p.exclude_ids || []), ci.id] }))} className="ml-0.5 text-gray-300 hover:text-red-400 opacity-0 group-hover/chip:opacity-100 transition-opacity" title={t('admin.arch.addon_remove_item')}><FaTimes size={7} /></button>
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {excludedItems.length > 0 && (
                                                            <div className="p-2.5 rounded-lg bg-red-50/60 border border-red-100">
                                                                <p className="text-[10px] font-bold tracking-wide text-red-400 uppercase mb-1.5">{t('admin.arch.addon_excluded')}</p>
                                                                <div className="flex flex-wrap gap-1">
                                                                    {excludedItems.map(ci => (
                                                                        <span key={ci.id} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-white border border-red-100 text-red-500">
                                                                            {ci.name}
                                                                            <button onClick={() => setEditAddonDraft(p => ({ ...p, exclude_ids: (p.exclude_ids || []).filter(id => id !== ci.id) }))} className="ml-0.5 text-red-300 hover:text-red-500"><FaTimes size={7} /></button>
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                        {includedItems.length > 0 && (
                                                            <div className="p-2.5 rounded-lg bg-emerald-50/60 border border-emerald-100">
                                                                <p className="text-[10px] font-bold tracking-wide text-emerald-500 uppercase mb-1.5">{t('admin.arch.addon_included')}</p>
                                                                <div className="flex flex-wrap gap-1">
                                                                    {includedItems.map(ci => (
                                                                        <span key={ci.id} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-white border border-emerald-100 text-emerald-600">
                                                                            {ci.name}
                                                                            <button onClick={() => setEditAddonDraft(p => ({ ...p, include_ids: (p.include_ids || []).filter(id => id !== ci.id) }))} className="ml-0.5 text-emerald-300 hover:text-emerald-600"><FaTimes size={7} /></button>
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div className="relative">
                                                            <input type="text" placeholder={t('admin.arch.addon_search_items')} value={editAddonItemQuery} onChange={e => setEditAddonItemQuery(e.target.value)} className="w-full px-3 py-1.5 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary/20" />
                                                            {searchItems.length > 0 && (
                                                                <div className="overflow-y-auto absolute z-10 mt-1 w-full max-h-40 bg-white rounded-lg border border-gray-200 shadow-lg">
                                                                    {searchItems.map(ci => (
                                                                        <button key={ci.id} onClick={() => { setEditAddonDraft(p => ({ ...p, include_ids: [...(p.include_ids || []), ci.id] })); setEditAddonItemQuery('') }} className="flex justify-between items-center w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50">
                                                                            <span className="text-gray-700">{ci.name}</span>
                                                                            <span className="text-[9px] text-gray-400 uppercase">{ci.discipline}</span>
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )
                                            })()}
                                        </div>
                                    ) : (
                                        <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap gap-2 items-center">
                                                <span className="text-sm font-semibold text-gray-700">{a.label}</span>
                                                <span className="text-[10px] font-mono text-gray-400">{a.key}</span>
                                                {a.is_system && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-gray-200 text-gray-500">SYSTEM</span>}
                                                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-violet-100 text-violet-700">{ruleText}</span>
                                                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-700">${Number(a.default_amount) || 0}</span>
                                                {a.enabled_by_default && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-primary/10 text-primary">ON</span>}
                                                {exList && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-50 text-red-600" title={exList}>excl</span>}
                                                {ptText && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-blue-50 text-blue-600" title={ptText}>PT</span>}
                                                {phText && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-50 text-amber-600" title={phText}>PH</span>}
                                                {(a.include_ids || []).length > 0 && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-50 text-emerald-600" title={(a.include_ids || []).join(', ')}>+{(a.include_ids || []).length}</span>}
                                                {(a.exclude_ids || []).length > 0 && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-50 text-red-600" title={(a.exclude_ids || []).join(', ')}>-{(a.exclude_ids || []).length}</span>}
                                            </div>
                                        </div>
                                    )}
                                    <div className="flex gap-1 opacity-100 transition-opacity sm:opacity-0 shrink-0 sm:group-hover:opacity-100">
                                        {isEditing ? (
                                            <>
                                                <button onClick={async () => {
                                                    try {
                                                        await updateArchAddon(a.id, { label: editAddonDraft.label, default_amount: Number(editAddonDraft.default_amount) || 0, rule_field: editAddonDraft.rule_field, rule_op: editAddonDraft.rule_op, rule_value: editAddonDraft.rule_value, enabled_by_default: editAddonDraft.enabled_by_default, rule_exclusions: (editAddonDraft.rule_exclusions || []).length ? editAddonDraft.rule_exclusions : null, include_ids: editAddonDraft.include_ids || [], exclude_ids: editAddonDraft.exclude_ids || [], project_types: editAddonDraft.project_types || [], phases: editAddonDraft.phases || [] })
                                                        setEditingAddonId(null)
                                                    } catch (e) { alert(e.message) }
                                                }} className="p-1 text-emerald-500 hover:text-emerald-600"><FaCheck size={10} /></button>
                                                <button onClick={() => setEditingAddonId(null)} className="p-1 text-gray-400 hover:text-gray-600"><FaTimes size={10} /></button>
                                            </>
                                        ) : (
                                            <>
                                                <button onClick={() => { setEditingAddonId(a.id); setEditAddonDraft({ label: a.label, default_amount: String(a.default_amount || 0), rule_field: a.rule_field, rule_op: a.rule_op, rule_value: a.rule_value, enabled_by_default: a.enabled_by_default, rule_exclusions: Array.isArray(a.rule_exclusions) ? a.rule_exclusions : [], project_types: a.project_types || [], phases: a.phases || [], include_ids: a.include_ids || [], exclude_ids: a.exclude_ids || [] }) }} className="p-1 text-gray-400 hover:text-primary"><FaEdit size={10} /></button>
                                                {!a.is_system && (
                                                    <button onClick={() => {
                                                        if (window.confirm(t('admin.arch.addon_delete_confirm'))) {
                                                            removeArchAddon(a.id).catch(e => alert(e.message))
                                                        }
                                                    }} className="p-1 text-gray-400 hover:text-red-500"><FaTrash size={9} /></button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}

            {/* ── Pricing & Difficulty ── */}
            {configSection === 'pricing' && (
                <div className="space-y-4">
                    {canEditCatalog && (
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                            {/* ── Difficulty Levels (New Construction) ── */}
                            <div className="p-5 bg-white rounded-2xl border border-l-4 border-gray-200 shadow-sm border-l-amber-400">
                    <div className="flex justify-between items-center mb-4">
                        <div>
                            <h3 className="flex gap-2 items-center font-bold text-gray-800">
                                <FaBuilding size={13} className="text-amber-500" />
                                {t('admin.arch.difficulty_title')}
                            </h3>
                            <p className="text-xs text-gray-400 mt-0.5">{t('admin.arch.difficulty_subtitle')}</p>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={reloadDifficultyLevels} className="p-1.5 text-gray-400 rounded-lg border border-gray-200 hover:bg-gray-50 transition-all"><FaSync size={11} /></button>
                            <button onClick={() => setShowAddDiff(!showAddDiff)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-amber-500 rounded-lg hover:bg-amber-600 transition-all">
                                <FaPlus size={10} /> {t('admin.arch.difficulty_add')}
                            </button>
                        </div>
                    </div>

                    {/* Add difficulty form */}
                    <AnimatePresence>
                        {showAddDiff && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                                <div className="flex flex-col gap-2 items-start p-3 mb-4 bg-gray-50 rounded-xl border border-gray-200 sm:flex-row sm:items-end">
                                    <div className="w-full sm:w-28">
                                        <label className="block mb-1 text-[10px] text-gray-500 uppercase tracking-wide">{t('admin.arch.difficulty_multiplier')}</label>
                                        <input type="number" step="0.1" value={newDiffValue} onChange={e => setNewDiffValue(e.target.value)}
                                            placeholder="1.0"
                                            className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                                    </div>
                                    <div className="w-full sm:w-32">
                                        <label className="block mb-1 text-[10px] text-gray-500 uppercase tracking-wide">{t('admin.arch.difficulty_label_field')}</label>
                                        <input value={newDiffLabel} onChange={e => setNewDiffLabel(e.target.value)}
                                            placeholder="Base"
                                            className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                                    </div>
                                    <div className="flex-1">
                                        <label className="block mb-1 text-[10px] text-gray-500 uppercase tracking-wide">{t('admin.arch.difficulty_desc_field')}</label>
                                        <input value={newDiffDesc} onChange={e => setNewDiffDesc(e.target.value)}
                                            placeholder="Standard"
                                            className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                                    </div>
                                    <div className="flex gap-2 shrink-0">
                                        <button onClick={handleAddDiff} disabled={addingDiff || !newDiffValue || !newDiffLabel.trim()}
                                            className="px-3 py-1.5 bg-primary text-white text-xs font-bold rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-all">
                                            {addingDiff ? t('admin.arch.difficulty_saving') : t('admin.arch.catalog.save')}
                                        </button>
                                        <button onClick={() => setShowAddDiff(false)} className="p-1.5 text-gray-400 hover:text-gray-600 transition-all">
                                            <FaTimes size={12} />
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {difficultyLoading && <p className="py-6 text-sm text-center text-gray-400">{t('admin.estimator.loading')}</p>}
                    {!difficultyLoading && (difficultyLevels || []).length === 0 && (
                        <p className="py-6 text-sm italic text-center text-gray-400">{t('admin.arch.difficulty_empty')}</p>
                    )}
                    {!difficultyLoading && (difficultyLevels || []).length > 0 && (
                        <div className="space-y-1.5">
                            {(difficultyLevels || []).map(lvl => (
                                <div key={lvl.id || lvl.value} className="flex gap-2 items-center px-3 py-2 bg-gray-50 rounded-lg border border-gray-100 group">
                                    {editingDiffId === (lvl.id || lvl.value) ? (
                                        <>
                                            <div className="w-16 sm:w-20">
                                                <input type="number" step="0.1" value={editDiffDraft.value} onChange={e => setEditDiffDraft(p => ({ ...p, value: e.target.value }))}
                                                    className="px-2 py-1 w-full text-xs bg-white rounded-lg border border-primary/50 focus:outline-none focus:ring-1 focus:ring-primary/30" />
                                            </div>
                                            <div className="w-20 sm:w-28">
                                                <input value={editDiffDraft.label} onChange={e => setEditDiffDraft(p => ({ ...p, label: e.target.value }))}
                                                    className="px-2 py-1 w-full text-xs bg-white rounded-lg border border-primary/50 focus:outline-none focus:ring-1 focus:ring-primary/30" />
                                            </div>
                                            <div className="flex-1">
                                                <input value={editDiffDraft.desc} onChange={e => setEditDiffDraft(p => ({ ...p, desc: e.target.value }))}
                                                    className="px-2 py-1 w-full text-xs bg-white rounded-lg border border-primary/50 focus:outline-none focus:ring-1 focus:ring-primary/30" />
                                            </div>
                                            <div className="flex gap-1 shrink-0">
                                                <button onClick={() => handleSaveDiffEdit(lvl.id)} className="p-1 text-emerald-500 hover:text-emerald-600"><FaCheck size={10} /></button>
                                                <button onClick={() => setEditingDiffId(null)} className="p-1 text-gray-400 hover:text-gray-600"><FaTimes size={10} /></button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <span className="w-12 text-sm font-bold text-primary sm:w-16 shrink-0">×{lvl.value}</span>
                                            <span className="w-16 text-sm font-semibold text-gray-700 truncate sm:w-24 shrink-0">{t(lvl.labelKey) || lvl.label || ''}</span>
                                            <span className="flex-1 text-sm text-gray-500 truncate">{t(lvl.descKey) || lvl.desc || ''}</span>
                                            <div className="flex gap-1 opacity-100 transition-opacity sm:opacity-0 shrink-0 sm:group-hover:opacity-100">
                                                <button onClick={() => { setEditingDiffId(lvl.id || lvl.value); setEditDiffDraft({ value: String(lvl.value), label: lvl.label, desc: lvl.desc || '', sort_order: lvl.sort_order ?? 0 }) }} className="p-1 text-gray-400 hover:text-primary"><FaEdit size={10} /></button>
                                                <button onClick={() => handleRemoveDiff(lvl.id)} className="p-1 text-gray-400 hover:text-red-500"><FaTrash size={9} /></button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                            </div>

                            {/* ── Add/Alt/Rem Difficulties ── */}
                            <div className="p-5 bg-white rounded-2xl border border-l-4 border-gray-200 shadow-sm border-l-orange-400">
                    <div className="flex justify-between items-center mb-4">
                        <div>
                            <h3 className="flex gap-2 items-center font-bold text-gray-800">
                                <FaHardHat size={13} className="text-orange-500" />
                                {t('admin.arch.add_alt_diff_title')}
                            </h3>
                            <p className="text-xs text-gray-400 mt-0.5">{t('admin.arch.add_alt_diff_subtitle')}</p>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={reloadAddAltDifficulties} className="p-1.5 text-gray-400 rounded-lg border border-gray-200 hover:bg-gray-50 transition-all"><FaSync size={11} /></button>
                            <button onClick={() => setShowAddAltDiff(!showAddAltDiff)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-all">
                                <FaPlus size={10} /> {t('admin.arch.add_alt_diff_add')}
                            </button>
                        </div>
                    </div>

                    {/* Add new option form */}
                    <AnimatePresence>
                        {showAddAltDiff && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                                <div className="flex flex-col gap-2 items-start p-3 mb-4 bg-gray-50 rounded-xl border border-gray-200 sm:flex-row sm:items-end">
                                    <div className="w-full sm:w-32">
                                        <label className="block mb-1 text-[10px] text-gray-500 uppercase tracking-wide">{t('admin.arch.add_alt_diff_component')}</label>
                                        <div className="relative">
                                            <select value={newAltDiffComponent} onChange={e => setNewAltDiffComponent(e.target.value)}
                                                className="w-full px-2.5 py-1.5 pr-8 text-sm rounded-lg border border-gray-200 bg-white text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary/30">
                                                <option value="exist">{t('admin.arch.component_exist')}</option>
                                                <option value="rem">{t('admin.arch.component_rem')}</option>
                                                <option value="add">{t('admin.arch.component_add')}</option>
                                            </select>
                                            <FaChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" size={10} />
                                        </div>
                                    </div>
                                    <div className="w-full sm:w-24">
                                        <label className="block mb-1 text-[10px] text-gray-500 uppercase tracking-wide">{t('admin.arch.add_alt_diff_value')}</label>
                                        <input type="number" step="0.05" value={newAltDiffValue} onChange={e => setNewAltDiffValue(e.target.value)}
                                            className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                                    </div>
                                    <div className="w-full sm:w-32">
                                        <label className="block mb-1 text-[10px] text-gray-500 uppercase tracking-wide">{t('admin.arch.add_alt_diff_label')}</label>
                                        <input value={newAltDiffLabel} onChange={e => setNewAltDiffLabel(e.target.value)}
                                            className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                                    </div>
                                    <div className="flex-1">
                                        <label className="block mb-1 text-[10px] text-gray-500 uppercase tracking-wide">{t('admin.arch.add_alt_diff_desc')}</label>
                                        <input value={newAltDiffDesc} onChange={e => setNewAltDiffDesc(e.target.value)}
                                            className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                                    </div>
                                    <div className="flex gap-2 shrink-0">
                                        <button onClick={async () => {
                                            if (!newAltDiffValue || !newAltDiffLabel.trim()) return
                                            setAddingAltDiff(true)
                                            try {
                                                await upsertDifficultyOption({ component: newAltDiffComponent, value: Number(newAltDiffValue), label: newAltDiffLabel.trim(), description: newAltDiffDesc.trim(), sort_order: addAltDiffRows.filter(r => r.component === newAltDiffComponent).length })
                                                setNewAltDiffValue(''); setNewAltDiffLabel(''); setNewAltDiffDesc(''); setShowAddAltDiff(false)
                                                reloadAddAltDifficulties()
                                            } catch (e) { alert(e.message) }
                                            finally { setAddingAltDiff(false) }
                                        }} disabled={addingAltDiff || !newAltDiffValue || !newAltDiffLabel.trim()}
                                            className="px-3 py-1.5 bg-primary text-white text-xs font-bold rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-all">
                                            {addingAltDiff ? '…' : t('admin.arch.catalog.save')}
                                        </button>
                                        <button onClick={() => setShowAddAltDiff(false)} className="p-1.5 text-gray-400 hover:text-gray-600 transition-all"><FaTimes size={12} /></button>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {addAltDiffLoading && <p className="py-6 text-sm text-center text-gray-400">{t('admin.estimator.loading')}</p>}
                    {!addAltDiffLoading && (
                        <div className="space-y-6">
                            {['exist', 'rem', 'add'].map(comp => {
                                const compLabel = comp === 'exist' ? t('admin.arch.component_exist') : comp === 'rem' ? t('admin.arch.component_rem') : t('admin.arch.component_add')
                                const options = getOptionsForComponent(comp)
                                return (
                                    <div key={comp}>
                                        <h4 className="mb-2 text-xs font-bold tracking-wide text-gray-500 uppercase">{compLabel}</h4>
                                        {options.length === 0 && <p className="py-2 text-sm italic text-gray-400">{t('admin.arch.add_alt_diff_empty')}</p>}
                                        <div className="space-y-1.5">
                                            {options.map(opt => (
                                                <div key={opt.id || `${comp}-${opt.value}`} className="flex gap-2 items-center px-3 py-2 bg-gray-50 rounded-lg border border-gray-100 group">
                                                    {editingAltDiffId === opt.id ? (
                                                        <>
                                                            <div className="w-16">
                                                                <input type="number" step="0.05" value={editAltDiffDraft.value} onChange={e => setEditAltDiffDraft(p => ({ ...p, value: e.target.value }))}
                                                                    className="px-2 py-1 w-full text-xs bg-white rounded-lg border border-primary/50 focus:outline-none focus:ring-1 focus:ring-primary/30" />
                                                            </div>
                                                            <div className="w-20">
                                                                <input value={editAltDiffDraft.label} onChange={e => setEditAltDiffDraft(p => ({ ...p, label: e.target.value }))}
                                                                    className="px-2 py-1 w-full text-xs bg-white rounded-lg border border-primary/50 focus:outline-none focus:ring-1 focus:ring-primary/30" />
                                                            </div>
                                                            <div className="flex-1">
                                                                <input value={editAltDiffDraft.desc} onChange={e => setEditAltDiffDraft(p => ({ ...p, desc: e.target.value }))}
                                                                    className="px-2 py-1 w-full text-xs bg-white rounded-lg border border-primary/50 focus:outline-none focus:ring-1 focus:ring-primary/30" />
                                                            </div>
                                                            <div className="flex gap-1 shrink-0">
                                                                <button onClick={async () => {
                                                                    try {
                                                                        await upsertDifficultyOption({ id: opt.id, component: editAltDiffDraft.component, value: Number(editAltDiffDraft.value), label: editAltDiffDraft.label.trim(), description: editAltDiffDraft.desc.trim(), sort_order: opt.sort_order ?? 0 })
                                                                        setEditingAltDiffId(null)
                                                                        reloadAddAltDifficulties()
                                                                    } catch (e) { alert(e.message) }
                                                                }} className="p-1 text-emerald-500 hover:text-emerald-600"><FaCheck size={10} /></button>
                                                                <button onClick={() => setEditingAltDiffId(null)} className="p-1 text-gray-400 hover:text-gray-600"><FaTimes size={10} /></button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span className="w-12 text-sm font-bold text-primary sm:w-16 shrink-0">×{opt.value}</span>
                                                            <span className="w-16 text-sm font-semibold text-gray-700 truncate sm:w-24 shrink-0">{opt.label}</span>
                                                            <span className="flex-1 text-sm text-gray-500 truncate">{opt.description || opt.desc}</span>
                                                            <div className="flex gap-1 opacity-100 transition-opacity sm:opacity-0 shrink-0 sm:group-hover:opacity-100">
                                                                <button onClick={() => { setEditingAltDiffId(opt.id); setEditAltDiffDraft({ value: String(opt.value), label: opt.label, desc: opt.description || opt.desc || '', component: comp }) }} className="p-1 text-gray-400 hover:text-primary"><FaEdit size={10} /></button>
                                                                <button onClick={async () => { if (!window.confirm(t('admin.arch.add_alt_diff_delete_confirm'))) return; try { await removeDifficultyOption(opt.id); reloadAddAltDifficulties() } catch (e) { alert(e.message) } }} className="p-1 text-gray-400 hover:text-red-500"><FaTrash size={9} /></button>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                            </div>

                            {/* ── Civil Difficulty Levels ── */}
                            <div className="p-5 bg-white rounded-2xl border border-l-4 border-gray-200 shadow-sm border-l-sky-400">
                    <div className="flex justify-between items-center mb-4">
                        <div>
                            <h3 className="flex gap-2 items-center font-bold text-gray-800">
                                <FaBuilding size={13} className="text-sky-500" />
                                {t('admin.arch.civil_diff_title')}
                            </h3>
                            <p className="text-xs text-gray-400 mt-0.5">{t('admin.arch.civil_diff_subtitle')}</p>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={reloadCivilLevels} className="p-1.5 text-gray-400 rounded-lg border border-gray-200 hover:bg-gray-50 transition-all"><FaSync size={11} /></button>
                            <button onClick={() => setShowAddCivilDiff(!showAddCivilDiff)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-sky-500 rounded-lg hover:bg-sky-600 transition-all">
                                <FaPlus size={10} /> {t('admin.arch.civil_diff_add')}
                            </button>
                        </div>
                    </div>

                    {/* Add civil difficulty form */}
                    <AnimatePresence>
                        {showAddCivilDiff && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                                <div className="flex flex-col gap-2 items-start p-3 mb-4 bg-gray-50 rounded-xl border border-gray-200 sm:flex-row sm:items-end">
                                    <div className="w-full sm:w-28">
                                        <label className="block mb-1 text-[10px] text-gray-500 uppercase tracking-wide">{t('admin.arch.difficulty_multiplier')}</label>
                                        <input type="number" step="0.1" value={newCivilDiffValue} onChange={e => setNewCivilDiffValue(e.target.value)}
                                            placeholder="1.0"
                                            className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                                    </div>
                                    <div className="w-full sm:w-32">
                                        <label className="block mb-1 text-[10px] text-gray-500 uppercase tracking-wide">{t('admin.arch.difficulty_label_field')}</label>
                                        <input value={newCivilDiffLabel} onChange={e => setNewCivilDiffLabel(e.target.value)}
                                            placeholder="Base"
                                            className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                                    </div>
                                    <div className="flex-1">
                                        <label className="block mb-1 text-[10px] text-gray-500 uppercase tracking-wide">{t('admin.arch.difficulty_desc_field')}</label>
                                        <input value={newCivilDiffDesc} onChange={e => setNewCivilDiffDesc(e.target.value)}
                                            placeholder="Standard"
                                            className="w-full px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                                    </div>
                                    <div className="flex gap-2 shrink-0">
                                        <button onClick={handleAddCivilDiff} disabled={addingCivilDiff || !newCivilDiffValue || !newCivilDiffLabel.trim()}
                                            className="px-3 py-1.5 bg-primary text-white text-xs font-bold rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-all">
                                            {addingCivilDiff ? t('admin.arch.difficulty_saving') : t('admin.arch.catalog.save')}
                                        </button>
                                        <button onClick={() => setShowAddCivilDiff(false)} className="p-1.5 text-gray-400 hover:text-gray-600 transition-all">
                                            <FaTimes size={12} />
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {civilLoading && <p className="py-6 text-sm text-center text-gray-400">{t('admin.estimator.loading')}</p>}
                    {!civilLoading && (civilLevels || []).length === 0 && (
                        <p className="py-6 text-sm italic text-center text-gray-400">{t('admin.arch.difficulty_empty')}</p>
                    )}
                    {!civilLoading && (civilLevels || []).length > 0 && (
                        <div className="space-y-1.5">
                            {(civilLevels || []).map(lvl => (
                                <div key={lvl.id || lvl.value} className="flex gap-2 items-center px-3 py-2 bg-gray-50 rounded-lg border border-gray-100 group">
                                    {editingCivilDiffId === (lvl.id || lvl.value) ? (
                                        <>
                                            <div className="w-16 sm:w-20">
                                                <input type="number" step="0.1" value={editCivilDiffDraft.value} onChange={e => setEditCivilDiffDraft(p => ({ ...p, value: e.target.value }))}
                                                    className="px-2 py-1 w-full text-xs bg-white rounded-lg border border-primary/50 focus:outline-none focus:ring-1 focus:ring-primary/30" />
                                            </div>
                                            <div className="w-20 sm:w-28">
                                                <input value={editCivilDiffDraft.label} onChange={e => setEditCivilDiffDraft(p => ({ ...p, label: e.target.value }))}
                                                    className="px-2 py-1 w-full text-xs bg-white rounded-lg border border-primary/50 focus:outline-none focus:ring-1 focus:ring-primary/30" />
                                            </div>
                                            <div className="flex-1">
                                                <input value={editCivilDiffDraft.desc} onChange={e => setEditCivilDiffDraft(p => ({ ...p, desc: e.target.value }))}
                                                    className="px-2 py-1 w-full text-xs bg-white rounded-lg border border-primary/50 focus:outline-none focus:ring-1 focus:ring-primary/30" />
                                            </div>
                                            <div className="flex gap-1 shrink-0">
                                                <button onClick={() => handleSaveCivilDiffEdit(lvl.id)} className="p-1 text-emerald-500 hover:text-emerald-600"><FaCheck size={10} /></button>
                                                <button onClick={() => setEditingCivilDiffId(null)} className="p-1 text-gray-400 hover:text-gray-600"><FaTimes size={10} /></button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <span className="w-12 text-sm font-bold text-primary sm:w-16 shrink-0">×{lvl.value}</span>
                                            <span className="w-16 text-sm font-semibold text-gray-700 truncate sm:w-24 shrink-0">{t(lvl.labelKey) || lvl.label || ''}</span>
                                            <span className="flex-1 text-sm text-gray-500 truncate">{t(lvl.descKey) || lvl.desc || ''}</span>
                                            <div className="flex gap-1 opacity-100 transition-opacity sm:opacity-0 shrink-0 sm:group-hover:opacity-100">
                                                <button onClick={() => { setEditingCivilDiffId(lvl.id || lvl.value); setEditCivilDiffDraft({ value: String(lvl.value), label: lvl.label, desc: lvl.desc || '', sort_order: lvl.sort_order ?? 0 }) }} className="p-1 text-gray-400 hover:text-primary"><FaEdit size={10} /></button>
                                                <button onClick={() => handleRemoveCivilDiff(lvl.id)} className="p-1 text-gray-400 hover:text-red-500"><FaTrash size={9} /></button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                            </div>
                        </div>
                    )}

                    {/* ── Payment Info ── */}
                    <div className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="font-bold text-gray-800">{t('admin.arch.payment_info_title')}</h3>
                            <button
                                onClick={() => { resetPaymentForm(); setPaymentFormOpen(true) }}
                                className="flex gap-1.5 items-center px-3 py-1.5 text-xs font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 transition-all"
                            >
                                <FaPlus size={10} /> {t('admin.arch.payment_method_add')}
                            </button>
                        </div>

                        {/* List */}
                        {paymentInfos.length === 0 ? (
                            <div className="py-8 text-center text-gray-400">
                                <p className="text-sm">{t('admin.arch.payment_list_empty')}</p>
                                <button onClick={() => { resetPaymentForm(); setPaymentFormOpen(true) }} className="mt-2 text-xs text-primary hover:underline">{t('admin.arch.payment_method_add')}</button>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {paymentInfos.map((method) => (
                                    <div key={method.id} className="flex gap-3 items-center p-3 bg-gray-50 rounded-xl border border-gray-100 group">
                                        <div className="flex flex-col flex-1 gap-0.5 min-w-0">
                                            <div className="flex gap-2 items-center">
                                                <span className="text-sm font-semibold text-gray-800 truncate">{method.bank_name || '—'}</span>
                                                <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase rounded-full ${
                                                    method.office_key === 'FL' ? 'bg-blue-100 text-blue-700' :
                                                    method.office_key === 'TX' ? 'bg-amber-100 text-amber-700' :
                                                    'bg-emerald-100 text-emerald-700'
                                                }`}>
                                                    {method.office_key === 'FL' ? t('admin.arch.payment_office_fl') :
                                                     method.office_key === 'TX' ? t('admin.arch.payment_office_tx') :
                                                     t('admin.arch.payment_office_both')}
                                                </span>
                                                {!method.is_active && <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase rounded-full bg-gray-200 text-gray-500">{t('admin.arch.inactive')}</span>}
                                            </div>
                                            <div className="flex gap-3 text-xs text-gray-500">
                                                <span className="truncate">{method.account_number || '—'}</span>
                                                <span className="truncate">{method.routing_number || '—'}</span>
                                            </div>
                                        </div>
                                        <div className="flex gap-1 opacity-100 transition-opacity sm:opacity-0 shrink-0 sm:group-hover:opacity-100">
                                            <button onClick={() => openEditPayment(method)} className="p-1.5 text-gray-400 hover:text-primary" title={t('admin.arch.payment_method_edit')}><FaEdit size={10} /></button>
                                            <button onClick={async () => {
                                                if (!window.confirm(t('admin.arch.payment_method_delete_confirm'))) return
                                                try { await deletePaymentInfo(method.id); reloadPaymentInfo() } catch (e) { alert(e.message) }
                                            }} className="p-1.5 text-gray-400 hover:text-red-500" title={t('admin.arch.payment_method_delete')}><FaTrash size={9} /></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Form */}
                        {paymentFormOpen && (
                            <div className="pt-4 mt-4 border-t border-gray-100">
                                <h4 className="mb-3 text-sm font-semibold text-gray-700">
                                    {paymentEditingId ? t('admin.arch.payment_form_title_edit') : t('admin.arch.payment_form_title_add')}
                                </h4>
                                {paymentError && <div className="mb-3 text-xs text-red-500">{paymentError}</div>}
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    <div>
                                        <label className="block mb-1 text-[11px] text-gray-500">{t('admin.arch.payment_office')}</label>
                                        <div className="relative">
                                            <select
                                                value={paymentForm.office_key}
                                                onChange={e => setPaymentForm(p => ({ ...p, office_key: e.target.value }))}
                                                className="w-full px-2.5 py-1.5 pr-8 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary/20"
                                            >
                                                <option value="FL">{t('admin.arch.payment_office_fl')}</option>
                                                <option value="TX">{t('admin.arch.payment_office_tx')}</option>
                                                <option value="both">{t('admin.arch.payment_office_both')}</option>
                                            </select>
                                            <FaChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" size={10} />
                                        </div>
                                    </div>
                                    <div className="flex gap-2 items-center sm:justify-end">
                                        <label className="flex gap-2 items-center text-sm text-gray-700 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={paymentForm.is_active}
                                                onChange={e => setPaymentForm(p => ({ ...p, is_active: e.target.checked }))}
                                                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                            />
                                            {t('admin.arch.payment_active')}
                                        </label>
                                    </div>
                                    {[
                                        { key: 'bank_name',               label: t('admin.arch.payment_bank_name') },
                                        { key: 'account_name',            label: t('admin.arch.payment_account_name') },
                                        { key: 'account_number',          label: t('admin.arch.payment_account_number') },
                                        { key: 'routing_number',          label: t('admin.arch.payment_routing') },
                                        { key: 'swift_code',              label: t('admin.arch.payment_swift') },
                                        { key: 'bank_address',            label: t('admin.arch.payment_bank_address') },
                                        { key: 'email',                   label: t('admin.arch.payment_email') },
                                        { key: 'additional_instructions', label: t('admin.arch.payment_instructions'), span: true },
                                        { key: 'card_note',               label: t('admin.arch.payment_card_note'), span: true },
                                        { key: 'check_note',              label: t('admin.arch.payment_check_note'), span: true },
                                    ].map(({ key, label, span }) => (
                                        <div key={key} className={span ? 'sm:col-span-2' : ''}>
                                            <label className="block mb-1 text-[11px] text-gray-500">{label}</label>
                                            <input
                                                type="text"
                                                value={paymentForm[key] || ''}
                                                onChange={e => setPaymentForm(p => ({ ...p, [key]: e.target.value }))}
                                                className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div className="flex gap-2 justify-end mt-4">
                                    <button onClick={resetPaymentForm} className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">{t('admin.arch.payment_cancel')}</button>
                                    <button
                                        onClick={async () => {
                                            setPaymentSaving(true); setPaymentError('')
                                            try {
                                                if (paymentEditingId) {
                                                    await updatePaymentInfo(paymentEditingId, paymentForm)
                                                } else {
                                                    await createPaymentInfo(paymentForm)
                                                }
                                                resetPaymentForm()
                                                reloadPaymentInfo()
                                            } catch (e) { setPaymentError(e.message) }
                                            finally { setPaymentSaving(false) }
                                        }}
                                        disabled={paymentSaving}
                                        className="flex gap-1.5 items-center px-3 py-1.5 text-xs font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 transition-all disabled:opacity-60"
                                    >
                                        {paymentSaving && <FaSpinner size={10} className="animate-spin" />}
                                        {t('admin.arch.payment_save')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── Global Parameters ── */}
            {configSection === 'parameters' && (
                <div className="space-y-4">
                    {/* Min Floors per Market */}
                    <div className="p-5 bg-white rounded-2xl border border-l-4 border-gray-200 shadow-sm border-l-primary">
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h3 className="flex gap-2 items-center font-bold text-gray-800">
                                    <FaShieldAlt size={13} className="text-primary" />
                                    {t('admin.arch.parameters_min_floors_title')}
                                </h3>
                                <p className="text-xs text-gray-400 mt-0.5">{t('admin.arch.parameters_min_floors_subtitle')}</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {marketOptions.map(mkt => {
                                const cfg = localCfg[mkt.key] || getMarketConfig(mkt.key)
                                const defaults = getMinFloors(mkt.key)
                                const isCustom = {
                                    exist: localCfg[mkt.key]?.min_floor_exist !== undefined && localCfg[mkt.key]?.min_floor_exist !== ''
                                        ? (localCfg[mkt.key].min_floor_exist || 0) !== defaults.exist
                                        : (getMarketConfig(mkt.key).min_floor_exist || 0) !== defaults.exist,
                                    remodel: localCfg[mkt.key]?.min_floor_remodel !== undefined && localCfg[mkt.key]?.min_floor_remodel !== ''
                                        ? (localCfg[mkt.key].min_floor_remodel || 0) !== defaults.remodel
                                        : (getMarketConfig(mkt.key).min_floor_remodel || 0) !== defaults.remodel,
                                    adicion: localCfg[mkt.key]?.min_floor_adicion !== undefined && localCfg[mkt.key]?.min_floor_adicion !== ''
                                        ? (localCfg[mkt.key].min_floor_adicion || 0) !== defaults.adicion
                                        : (getMarketConfig(mkt.key).min_floor_adicion || 0) !== defaults.adicion,
                                }
                                return (
                                    <div key={mkt.key} className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                                        <div className="flex gap-2 items-center mb-2">
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-gray-100 text-gray-600 border border-gray-200">{mkt.state}</span>
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-primary/10 text-primary border border-primary/20">{mkt.type}</span>
                                            <h4 className="text-xs font-bold text-gray-800">{mkt.label}</h4>
                                        </div>
                                        <div className="grid grid-cols-3 gap-2">
                                            {[
                                                { key: 'min_floor_exist',   labelKey: 'admin.arch.min_floor_exist',   fallback: defaults.exist },
                                                { key: 'min_floor_remodel', labelKey: 'admin.arch.min_floor_remodel', fallback: defaults.remodel },
                                                { key: 'min_floor_adicion', labelKey: 'admin.arch.min_floor_adicion', fallback: defaults.adicion },
                                            ].map(({ key, labelKey, fallback }) => {
                                                const val = localCfg[mkt.key]?.[key] ?? cfg[key] ?? ''
                                                const shortKey = key.replace('min_floor_', '')
                                                const customFlag = isCustom[shortKey === 'exist' ? 'exist' : shortKey]
                                                return (
                                                    <div key={key}>
                                                        <label className="block mb-0.5 text-[10px] text-gray-500 uppercase truncate">{t(labelKey) || key}</label>
                                                        <div className="relative">
                                                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-[10px]">$</span>
                                                            <input
                                                                type="number"
                                                                step="1"
                                                                placeholder={String(fallback)}
                                                                value={val}
                                                                onChange={e => setLocalCfg(prev => ({ ...prev, [mkt.key]: { ...prev[mkt.key], [key]: e.target.value === '' ? '' : Number(e.target.value) } }))}
                                                                className={`py-1 pr-2 pl-4 w-full text-xs bg-white rounded-lg border focus:outline-none focus:ring-1 focus:ring-primary/30 ${customFlag ? 'border-primary/60' : 'border-gray-200'}`}
                                                            />
                                                        </div>
                                                        <p className="mt-0.5 text-[9px] text-gray-400">
                                                            {customFlag ? `${t('admin.arch.custom_value')}` : `${t('admin.arch.value_default')}: $${fallback.toLocaleString()}`}
                                                        </p>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                        <button
                                            onClick={() => handleSaveMinFloors(mkt.key)}
                                            disabled={savingMinFloor === mkt.key}
                                            className="mt-2 flex gap-1 items-center px-2 py-1 text-[10px] font-semibold text-white bg-primary rounded-md hover:bg-primary/90 transition-all disabled:opacity-60"
                                        >
                                            {savedMinFloorMkt === mkt.key ? <FaCheck size={8} /> : <FaSave size={8} />}
                                            {savingMinFloor === mkt.key ? t('admin.estimator.saving') : savedMinFloorMkt === mkt.key ? t('admin.arch.saved') : t('admin.estimator.save')}
                                        </button>
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    {/* Scope Multipliers per Market */}
                    <div className="p-5 bg-white rounded-2xl border border-l-4 border-gray-200 shadow-sm border-l-sky-400">
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h3 className="flex gap-2 items-center font-bold text-gray-800">
                                    <FaExpand size={13} className="text-sky-500" />
                                    {t('admin.arch.parameters_scope_mult_title')}
                                </h3>
                                <p className="text-xs text-gray-400 mt-0.5">{t('admin.arch.parameters_scope_mult_subtitle')}</p>
                            </div>
                        </div>
                        <div className="space-y-3">
                            {marketOptions.map(mkt => (
                                <div key={mkt.key} className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                                    <div className="flex justify-between items-center mb-2">
                                        <h4 className="text-xs font-bold text-gray-700 uppercase">{mkt.label}</h4>
                                        <button
                                            onClick={() => handleSaveScopeMults(mkt.key)}
                                            disabled={savingScope === mkt.key}
                                            className="flex gap-1 items-center px-2 py-1 text-[10px] font-semibold text-white bg-primary rounded-md hover:bg-primary/90 transition-all disabled:opacity-60"
                                        >
                                            {savedScopeMkt === mkt.key ? <FaCheck size={8} /> : <FaSave size={8} />}
                                            {savingScope === mkt.key ? t('admin.estimator.saving') : savedScopeMkt === mkt.key ? t('admin.arch.saved') : t('admin.estimator.save')}
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-5 gap-2 sm:grid-cols-10">
                                        {DISCIPLINE_SCOPE_OPTIONS.map(opt => {
                                            const dbVal = dbScopeMultipliers[mkt.key]?.[opt.key] ?? DISCIPLINE_SCOPE_MULTIPLIERS[mkt.key]?.[opt.key] ?? 1
                                            const currentVal = localScopeMults[mkt.key]?.[opt.key] ?? dbVal
                                            return (
                                                <div key={opt.key} className="flex flex-col items-center">
                                                    <label className="mb-0.5 text-[9px] text-gray-500 text-center truncate w-full">{t(opt.labelKey) || opt.key}</label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={currentVal}
                                                        onChange={e => setLocalScopeMults(prev => ({
                                                            ...prev,
                                                            [mkt.key]: { ...prev[mkt.key], [opt.key]: e.target.value === '' ? '' : Number(e.target.value) }
                                                        }))}
                                                        className="w-full px-1 py-1 text-center text-[11px] bg-white rounded border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary/30"
                                                    />
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Discipline Presets */}
                    <div className="p-5 bg-white rounded-2xl border border-l-4 border-gray-200 shadow-sm border-l-amber-400">
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h3 className="flex gap-2 items-center font-bold text-gray-800">
                                    <FaLayerGroup size={13} className="text-amber-500" />
                                    {t('admin.arch.parameters_disc_preset_title')}
                                </h3>
                                <p className="text-xs text-gray-400 mt-0.5">{t('admin.arch.parameters_disc_preset_subtitle')}</p>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="bg-gray-50">
                                        <th className="px-2 py-1.5 text-left font-bold text-gray-600 uppercase tracking-wide">{t('admin.arch.scope_label')}</th>
                                        <th className="px-2 py-1.5 text-right font-bold text-gray-600 uppercase tracking-wide">{t('admin.arch.disc_arch')} %</th>
                                        <th className="px-2 py-1.5 text-right font-bold text-gray-600 uppercase tracking-wide">{t('admin.arch.disc_struct')} %</th>
                                        <th className="px-2 py-1.5 text-right font-bold text-gray-600 uppercase tracking-wide">{t('admin.arch.disc_mep')} %</th>
                                        <th className="px-2 py-1.5 text-right font-bold text-gray-600 uppercase tracking-wide">{t('admin.arch.disc_civil')} %</th>
                                        <th className="px-2 py-1.5 text-right font-bold text-gray-600 uppercase tracking-wide">{t('admin.arch.disc_pm')} %</th>
                                        <th className="px-2 py-1.5 text-right font-bold text-gray-600 uppercase tracking-wide">{t('admin.arch.preset_sum')}</th>
                                        <th className="px-2 py-1.5 text-right font-bold text-gray-600 uppercase tracking-wide"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {DISCIPLINE_SCOPE_OPTIONS.map(opt => {
                                        const dbP = dbDisciplinePresets[opt.key] || SCOPE_OPTION_TO_PCT[opt.key] || { archPct: 0, structPct: 0, mepPct: 0, civilPct: 0, adminPct: 0 }
                                        const p = localPresets[opt.key] || dbP
                                        const sum = (p.archPct || 0) + (p.structPct || 0) + (p.mepPct || 0) + (p.civilPct || 0) + (p.adminPct || 0)
                                        const valid = Math.abs(sum - 100) < 0.01
                                        return (
                                            <tr key={opt.key} className="border-b border-gray-100 hover:bg-gray-50/50">
                                                <td className="px-2 py-1.5 font-medium text-gray-700">{t(opt.labelKey) || opt.key}</td>
                                                {['archPct', 'structPct', 'mepPct', 'civilPct', 'adminPct'].map(k => (
                                                    <td key={k} className="px-2 py-1">
                                                        <input
                                                            type="number"
                                                            step="0.1"
                                                            value={p[k] ?? 0}
                                                            onChange={e => {
                                                                const val = e.target.value === '' ? '' : Number(e.target.value)
                                                                setLocalPresets(prev => ({
                                                                    ...prev,
                                                                    [opt.key]: { ...(prev[opt.key] || dbP), [k]: val }
                                                                }))
                                                            }}
                                                            className="w-14 px-1 py-0.5 text-right text-[11px] bg-white rounded border border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary/30"
                                                        />
                                                    </td>
                                                ))}
                                                <td className={`px-2 py-1.5 text-right font-mono font-bold ${valid ? 'text-emerald-600' : 'text-red-500'}`}>{sum.toFixed(1)}%</td>
                                                <td className="px-2 py-1.5 text-right">
                                                    <button
                                                        onClick={() => handleSavePreset(opt.key)}
                                                        disabled={savingPreset === opt.key}
                                                        className="flex gap-1 items-center px-2 py-1 text-[10px] font-semibold text-white bg-primary rounded-md hover:bg-primary/90 transition-all disabled:opacity-60"
                                                    >
                                                        {savedPresetKey === opt.key ? <FaCheck size={8} /> : <FaSave size={8} />}
                                                        {savingPreset === opt.key ? t('admin.estimator.saving') : savedPresetKey === opt.key ? t('admin.arch.saved') : t('admin.estimator.save')}
                                                    </button>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Global Cost Roles */}
                    <div className="p-5 bg-white rounded-2xl border border-l-4 border-gray-200 shadow-sm border-l-emerald-400">
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h3 className="flex gap-2 items-center font-bold text-gray-800">
                                    <FaHardHat size={13} className="text-emerald-500" />
                                    {t('admin.arch.parameters_cost_roles_title')}
                                </h3>
                                <p className="text-xs text-gray-400 mt-0.5">{t('admin.arch.parameters_cost_roles_subtitle')}</p>
                            </div>
                            <select
                                className="px-2 py-1 text-xs font-semibold bg-white rounded-lg border border-gray-300 focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                                value={selectedCostRoleMarket}
                                onChange={(e) => setSelectedCostRoleMarket(e.target.value)}
                            >
                                {marketOptions.map(m => (
                                    <option key={m.key} value={m.key}>{t(m.labelKey) || m.key}</option>
                                ))}
                            </select>
                        </div>
                        {rolesLoading && (
                            <div className="flex justify-center items-center py-8 text-gray-400">
                                <FaSync size={14} className="mr-2 animate-spin" />
                                <span className="text-xs">{t('admin.estimator.loading')}</span>
                            </div>
                        )}
                        {!rolesLoading && (() => {
                            const DISC_BADGE = { ARCH: 'bg-blue-100 text-blue-700', STRUCT: 'bg-amber-100 text-amber-700', MEP: 'bg-teal-100 text-teal-700', CIVIL: 'bg-green-100 text-green-700', ADMIN: 'bg-violet-100 text-violet-700', ALL: 'bg-gray-100 text-gray-500' }
                            const BUCKET_FIELD = {
                                direct: 'direct_cost_pct', certifier: 'certifier_pct', commission: 'sales_pct',
                                operating: 'operating_pct', franchise: 'franchise_pct', profit: 'profit_pct', contingency: 'contingency_pct',
                            }
                            const marketCfg = getMarketConfig(selectedCostRoleMarket) || {}
                            return Object.entries(rolesByType).map(([type, roles]) => {
                                const groupTitle = roles[0]?.group_title || t(`admin.arch.${type}_costs`) || type
                                const isCustom = !['direct','certifier','commission','operating','franchise','profit','contingency'].includes(type)
                                const enabledRoles = roles.filter(r => (localRoles[r.roleKey]?.is_enabled ?? r.is_enabled) !== false)
                                const groupTotal = enabledRoles.reduce((s, r) => s + (localRoles[r.roleKey]?.pct ?? r.pct), 0)
                                const gColor = groupTotal < 99.9 ? 'text-amber-600' : groupTotal > 100.1 ? 'text-red-500' : 'text-emerald-600'
                                const bucketPct = marketCfg[BUCKET_FIELD[type]]
                                return (
                                <div key={type} className="p-3 mb-4 bg-gray-50 rounded-xl border border-gray-100 last:mb-0">
                                    <div className="flex justify-between items-center mb-2">
                                        <div className="flex gap-2 items-center">
                                            {editingGroupTitle === type ? (
                                                <input type="text" value={groupTitleInputs[type] ?? groupTitle} onChange={e => setGroupTitleInputs(p => ({ ...p, [type]: e.target.value }))} onBlur={() => handleUpdateGroupTitle(type)} onKeyDown={e => e.key === 'Enter' && handleUpdateGroupTitle(type)} autoFocus className="px-1 py-0.5 text-xs bg-white rounded border border-gray-200" />
                                            ) : (
                                                <h4 className="text-xs font-bold uppercase cursor-pointer hover:text-primary" onClick={() => { setEditingGroupTitle(type); setGroupTitleInputs(p => ({ ...p, [type]: groupTitle })) }}>{groupTitle}</h4>
                                            )}
                                            {bucketPct != null && (
                                                <span className="text-[10px] font-mono text-gray-400">({(bucketPct * 100).toFixed(1)}% {t('admin.arch.bucket')})</span>
                                            )}
                                            <span className={`font-mono text-[10px] ${gColor}`}>{groupTotal.toFixed(1)}%</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <button onClick={() => handleSaveRoleSection(type, roles)} disabled={savingRole === type} className="flex gap-1 items-center px-2 py-1 text-[10px] font-semibold text-white bg-primary rounded-md hover:bg-primary/90 disabled:opacity-60">{savedRoleKey === type ? <FaCheck size={8} /> : <FaSave size={8} />}{savingRole === type ? t('admin.estimator.saving') : savedRoleKey === type ? t('admin.arch.saved') : t('admin.estimator.save')}</button>
                                            {isCustom && <button onClick={() => handleDeleteGroup(type)} className="p-1 text-gray-400 hover:text-red-500" title={t('admin.arch.delete_group')}><FaTrash size={10} /></button>}
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        {roles.map(r => {
                                            const localVal = localRoles[r.roleKey]
                                            const pct = localVal?.pct != null ? localVal.pct : r.pct
                                            const isEnabled = localVal?.is_enabled != null ? localVal.is_enabled : r.is_enabled !== false
                                            return (
                                                <div key={r.roleKey} className="flex gap-2 items-center px-2 py-1 bg-white rounded border border-gray-100">
                                                    <input type="checkbox" checked={isEnabled} onChange={e => {
                                                        const checked = e.target.checked
                                                        if (checked) {
                                                            setLocalRoles(p => ({ ...p, [r.roleKey]: { ...p[r.roleKey], is_enabled: true, pct: 0 } }))
                                                        } else {
                                                            setLocalRoles(p => ({ ...p, ...redistributeDisabledPct(p, type, r.roleKey) }))
                                                        }
                                                    }} className="shrink-0 w-3.5 h-3.5 rounded border-gray-300 text-primary" />
                                                    {editingRoleLabel?.type === type && editingRoleLabel?.roleKey === r.roleKey ? (
                                                        <input type="text" value={roleLabelInputs[r.roleKey] || t(r.labelKey) || r.roleKey} onChange={e => setRoleLabelInputs(p => ({ ...p, [r.roleKey]: e.target.value }))} onBlur={() => handleUpdateRoleLabel(type, r.roleKey)} onKeyDown={e => e.key === 'Enter' && handleUpdateRoleLabel(type, r.roleKey)} autoFocus className="flex-1 px-1 py-0.5 text-[10px] bg-white rounded border border-gray-200" />
                                                    ) : (
                                                        <span className="flex-1 text-[10px] text-gray-600 truncate cursor-pointer hover:text-primary" onClick={() => { setEditingRoleLabel({ type, roleKey: r.roleKey }); setRoleLabelInputs(p => ({ ...p, [r.roleKey]: t(r.labelKey) || r.roleKey })) }}>{t(r.labelKey) || r.roleKey}</span>
                                                    )}
                                                    <select
                                                        value={localRoles[r.roleKey]?.discipline ?? r.discipline ?? 'ALL'}
                                                        onChange={e => setLocalRoles(p => ({ ...p, [r.roleKey]: { ...p[r.roleKey], discipline: e.target.value } }))}
                                                        className={`shrink-0 text-[9px] font-bold rounded px-1 py-0.5 border-0 outline-none cursor-pointer appearance-none ${DISC_BADGE[localRoles[r.roleKey]?.discipline ?? r.discipline ?? 'ALL'] || DISC_BADGE.ALL}`}
                                                    >
                                                        {['ALL','ARCH','STRUCT','MEP','CIVIL','ADMIN'].map(d => <option key={d} value={d}>{d}</option>)}
                                                    </select>
                                                    <div className="relative w-20">
                                                        <input type="number" step="0.1" value={Math.round(pct * 10) / 10} disabled={!isEnabled} onChange={e => {
                                                            const val = e.target.value
                                                            const newPct = val === '' ? '' : Number(val)
                                                            if (newPct === '') {
                                                                setLocalRoles(p => ({ ...p, [r.roleKey]: { ...p[r.roleKey], pct: '' } }))
                                                                return
                                                            }
                                                            setLocalRoles(prev => ({ ...prev, ...balanceGroupPcts(prev, type, r.roleKey, newPct) }))
                                                        }} className={`px-1 py-0.5 pr-4 w-full text-[11px] bg-white rounded border ${isEnabled ? 'border-gray-200' : 'border-gray-100 bg-gray-50 text-gray-400'} text-right`} />
                                                        <span className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 text-[9px]">%</span>
                                                    </div>
                                                    <button onClick={() => handleDeleteRole(type, r.roleKey)} className="p-1 text-gray-300 hover:text-red-500" title={t('admin.arch.delete_role')}><FaTrash size={9} /></button>
                                                </div>
                                            )
                                        })}
                                    </div>
                                    {addingRoleGroup === type ? (
                                        <div className="flex gap-2 items-center mt-2">
                                            <input type="text" placeholder={t('admin.arch.new_role_placeholder')} value={newRoleInputs[type]?.label || ''} onChange={e => setNewRoleInputs(p => ({ ...p, [type]: { ...p[type], label: e.target.value } }))} className="flex-1 px-2 py-1 text-xs bg-white rounded border border-gray-200" />
                                            <select value={newRoleInputs[type]?.discipline || 'ALL'} onChange={e => setNewRoleInputs(p => ({ ...p, [type]: { ...p[type], discipline: e.target.value } }))} className={`shrink-0 text-[9px] font-bold rounded px-1 py-0.5 border-0 outline-none cursor-pointer appearance-none ${DISC_BADGE[newRoleInputs[type]?.discipline || 'ALL'] || DISC_BADGE.ALL}`}>
                                                {['ALL','ARCH','STRUCT','MEP','CIVIL','ADMIN'].map(d => <option key={d} value={d}>{d}</option>)}
                                            </select>
                                            <input type="number" step="0.1" placeholder="%" value={newRoleInputs[type]?.pct || ''} onChange={e => setNewRoleInputs(p => ({ ...p, [type]: { ...p[type], pct: e.target.value } }))} className="px-2 py-1 w-20 text-xs bg-white rounded border border-gray-200" />
                                            <button onClick={() => handleAddRole(type)} className="px-2 py-1 text-[10px] font-semibold text-white bg-emerald-500 rounded hover:bg-emerald-600"><FaCheck size={8} /></button>
                                            <button onClick={() => { setAddingRoleGroup(null); setNewRoleInputs(p => ({ ...p, [type]: { label: '', pct: '' } })) }} className="px-2 py-1 text-[10px] font-semibold text-white bg-gray-400 rounded hover:bg-gray-500"><FaTimes size={8} /></button>
                                        </div>
                                    ) : (
                                        <button onClick={() => setAddingRoleGroup(type)} className="flex items-center gap-1 mt-2 text-[10px] font-semibold text-primary hover:text-primary/80"><FaPlus size={8} />{t('admin.arch.add_role')}</button>
                                    )}
                                </div>
                            )
                        })
                    })()} 
                        {!rolesLoading && (
                            <div className="mt-2 text-[10px] text-gray-400 italic">{t('admin.arch.each_group_must_sum_100')}</div>
                        )}
                    </div>

                </div>
            )}

            {/* ── Payment Schedule Presets ── */}
            {configSection === 'schedules' && (
                <div className="space-y-4">
                    <div className="p-5 bg-white rounded-2xl border border-l-4 border-gray-200 shadow-sm border-l-amber-400">
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h3 className="flex gap-2 items-center font-bold text-gray-800">
                                    <FaMoneyBillWave size={13} className="text-amber-500" />
                                    {t('admin.arch.schedule_presets_title')}
                                </h3>
                                <p className="text-xs text-gray-400 mt-0.5">{t('admin.arch.schedule_presets_subtitle')}</p>
                            </div>
                            <button
                                onClick={() => { setShowAddPreset(true); setEditingPresetId(null); setNewPresetName(''); setNewPresetRows([{ label: '', pct: 0 }]) }}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-amber-500 rounded-lg hover:bg-amber-600 transition-all"
                            >
                                <FaPlus size={10} /> {t('admin.arch.schedule_preset_add')}
                            </button>
                        </div>

                        {schedulePresetsLoading && <p className="text-xs text-gray-400">{t('admin.estimator.loading')}</p>}

                        {/* Preset list */}
                        <div className="space-y-2">
                            {(schedulePresets || []).map(preset => {
                                const rows = preset.rows || []
                                const total = rows.reduce((s, r) => s + (Number(r.pct) || 0), 0)
                                const isEditing = editingPresetId === preset.id
                                const isDefault = !!preset.is_default
                                return (
                                    <div key={preset.id} className={`overflow-hidden rounded-xl border ${isDefault ? 'border-amber-300 bg-amber-50/30' : 'border-gray-200'}`}>
                                        {!isEditing ? (
                                            <div className="flex justify-between items-center px-4 py-3 bg-gray-50/60">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex gap-2 items-center">
                                                        <span className="text-sm font-bold text-gray-800 truncate">{preset.name}</span>
                                                        {isDefault && (
                                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-200 shrink-0">
                                                                <FaStar size={7} /> {t('admin.arch.schedule_preset_is_default')}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-[10px] text-gray-400">
                                                        {rows.length} {t('admin.arch.schedule_preset_rows')} · {total}%
                                                    </div>
                                                </div>
                                                <div className="flex gap-1.5 items-center shrink-0">
                                                    <button
                                                        onClick={async () => {
                                                            try {
                                                                if (isDefault) await clearDefaultPreset(preset.id)
                                                                else await setDefaultPreset(preset.id)
                                                            } catch (e) { alert(e.message) }
                                                        }}
                                                        className={`flex items-center gap-1 px-2 py-1 text-[10px] font-bold rounded-lg border transition-all ${
                                                            isDefault
                                                                ? 'text-amber-700 bg-amber-100 border-amber-200 hover:bg-amber-200'
                                                                : 'text-gray-400 bg-white border-gray-200 hover:bg-amber-50 hover:text-amber-600 hover:border-amber-200'
                                                        }`}
                                                        title={isDefault ? t('admin.arch.schedule_preset_is_default') : t('admin.arch.schedule_preset_set_default')}
                                                    >
                                                        {isDefault ? <FaStar size={9} /> : <FaRegStar size={9} />}
                                                        {isDefault ? t('admin.arch.schedule_preset_is_default') : t('admin.arch.schedule_preset_set_default')}
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setEditingPresetId(preset.id)
                                                            setEditPresetDraft({ name: preset.name, rows: rows.map(r => ({ label: r.label || '', pct: r.pct || 0 })) })
                                                            setShowAddPreset(false)
                                                        }}
                                                        className="px-2 py-1 text-[10px] font-bold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-all"
                                                    >
                                                        {t('admin.arch.schedule_preset_edit')}
                                                    </button>
                                                    <button
                                                        onClick={async () => {
                                                            if (!window.confirm(t('admin.arch.difficulty_delete_confirm'))) return
                                                            try { await deleteSchedulePreset(preset.id); reloadSchedulePresets() } catch (e) { alert(e.message) }
                                                        }}
                                                        className="px-2 py-1 text-[10px] font-bold text-red-500 bg-white border border-gray-200 rounded-lg hover:bg-red-50 transition-all"
                                                    >
                                                        {t('admin.arch.schedule_preset_delete')}
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="p-4 space-y-3">
                                                <input
                                                    value={editPresetDraft.name}
                                                    onChange={e => setEditPresetDraft(prev => ({ ...prev, name: e.target.value }))}
                                                    placeholder={t('admin.arch.schedule_preset_name')}
                                                    className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
                                                />
                                                <div className="space-y-1.5">
                                                    {editPresetDraft.rows.map((r, idx) => (
                                                        <div key={idx} className="flex gap-2 items-center">
                                                            <input
                                                                value={r.label}
                                                                onChange={e => {
                                                                    const next = [...editPresetDraft.rows]
                                                                    next[idx] = { ...next[idx], label: e.target.value }
                                                                    setEditPresetDraft(prev => ({ ...prev, rows: next }))
                                                                }}
                                                                placeholder={t('admin.estimator.milestone_label')}
                                                                className="flex-1 px-3 py-1.5 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-400/30 min-w-0"
                                                            />
                                                            <div className="relative w-16 shrink-0">
                                                                <input
                                                                    type="number"
                                                                    value={r.pct}
                                                                    min={0} max={100} step={1}
                                                                    onChange={e => {
                                                                        const next = [...editPresetDraft.rows]
                                                                        next[idx] = { ...next[idx], pct: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) }
                                                                        setEditPresetDraft(prev => ({ ...prev, rows: next }))
                                                                    }}
                                                                    className="w-full px-2 py-1.5 pr-5 text-xs text-center rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
                                                                />
                                                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none">%</span>
                                                            </div>
                                                            <button
                                                                onClick={() => {
                                                                    const next = editPresetDraft.rows.filter((_, i) => i !== idx)
                                                                    setEditPresetDraft(prev => ({ ...prev, rows: next }))
                                                                }}
                                                                disabled={editPresetDraft.rows.length <= 1}
                                                                className="flex justify-center p-1 w-7 text-gray-300 rounded-lg transition-all hover:text-red-500 hover:bg-red-50 disabled:opacity-0 disabled:pointer-events-none shrink-0"
                                                            >
                                                                <FaTimesCircle size={11} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="flex gap-2 items-center">
                                                    <button
                                                        onClick={() => setEditPresetDraft(prev => ({ ...prev, rows: [...prev.rows, { label: '', pct: 0 }] }))}
                                                        className="flex gap-1 items-center text-xs font-semibold text-amber-600 transition-all hover:text-amber-700"
                                                    >
                                                        <FaPlus size={9} /> {t('admin.arch.schedule_preset_new_row')}
                                                    </button>
                                                    <span className={`text-xs font-bold ml-auto ${Math.abs(editPresetDraft.rows.reduce((s, r) => s + (Number(r.pct) || 0), 0) - 100) < 0.01 ? 'text-emerald-600' : 'text-red-500'}`}>
                                                        {t('admin.arch.schedule_preset_total_pct')}: {editPresetDraft.rows.reduce((s, r) => s + (Number(r.pct) || 0), 0)}%
                                                    </span>
                                                </div>
                                                <div className="flex gap-2 pt-1">
                                                    <button
                                                        onClick={async () => {
                                                            const total = editPresetDraft.rows.reduce((s, r) => s + (Number(r.pct) || 0), 0)
                                                            if (Math.abs(total - 100) >= 0.01) { alert(t('admin.arch.schedule_preset_must_equal_100')); return }
                                                            try {
                                                                await updateSchedulePreset(preset.id, { name: editPresetDraft.name, rows: editPresetDraft.rows })
                                                                setEditingPresetId(null)
                                                                reloadSchedulePresets()
                                                            } catch (e) { alert(e.message) }
                                                        }}
                                                        className="flex-1 py-2 text-xs font-bold text-white bg-amber-500 rounded-xl transition-all hover:bg-amber-600"
                                                    >
                                                        {t('admin.estimator.save')}
                                                    </button>
                                                    <button
                                                        onClick={() => setEditingPresetId(null)}
                                                        className="flex-1 py-2 text-xs font-bold text-gray-600 bg-gray-100 rounded-xl transition-all hover:bg-gray-200"
                                                    >
                                                        {t('admin.arch.cancel')}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>

                        {/* Add form */}
                        {showAddPreset && (
                            <div className="p-4 mt-4 space-y-3 rounded-xl border border-amber-200 bg-amber-50/40">
                                <input
                                    value={newPresetName}
                                    onChange={e => setNewPresetName(e.target.value)}
                                    placeholder={t('admin.arch.schedule_preset_name')}
                                    className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
                                />
                                <div className="space-y-1.5">
                                    {newPresetRows.map((r, idx) => (
                                        <div key={idx} className="flex gap-2 items-center">
                                            <input
                                                value={r.label}
                                                onChange={e => {
                                                    const next = [...newPresetRows]
                                                    next[idx] = { ...next[idx], label: e.target.value }
                                                    setNewPresetRows(next)
                                                }}
                                                placeholder={t('admin.estimator.milestone_label')}
                                                className="flex-1 px-3 py-1.5 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-400/30 min-w-0"
                                            />
                                            <div className="relative w-16 shrink-0">
                                                <input
                                                    type="number"
                                                    value={r.pct}
                                                    min={0} max={100} step={1}
                                                    onChange={e => {
                                                        const next = [...newPresetRows]
                                                        next[idx] = { ...next[idx], pct: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) }
                                                        setNewPresetRows(next)
                                                    }}
                                                    className="w-full px-2 py-1.5 pr-5 text-xs text-center rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
                                                />
                                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none">%</span>
                                            </div>
                                            <button
                                                onClick={() => setNewPresetRows(prev => prev.filter((_, i) => i !== idx))}
                                                disabled={newPresetRows.length <= 1}
                                                className="flex justify-center p-1 w-7 text-gray-300 rounded-lg transition-all hover:text-red-500 hover:bg-red-50 disabled:opacity-0 disabled:pointer-events-none shrink-0"
                                            >
                                                <FaTimesCircle size={11} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex gap-2 items-center">
                                    <button
                                        onClick={() => setNewPresetRows(prev => [...prev, { label: '', pct: 0 }])}
                                        className="flex gap-1 items-center text-xs font-semibold text-amber-600 transition-all hover:text-amber-700"
                                    >
                                        <FaPlus size={9} /> {t('admin.arch.schedule_preset_new_row')}
                                    </button>
                                    <span className={`text-xs font-bold ml-auto ${Math.abs(newPresetRows.reduce((s, r) => s + (Number(r.pct) || 0), 0) - 100) < 0.01 ? 'text-emerald-600' : 'text-red-500'}`}>
                                        {t('admin.arch.schedule_preset_total_pct')}: {newPresetRows.reduce((s, r) => s + (Number(r.pct) || 0), 0)}%
                                    </span>
                                </div>
                                <div className="flex gap-2 pt-1">
                                    <button
                                        onClick={async () => {
                                            const total = newPresetRows.reduce((s, r) => s + (Number(r.pct) || 0), 0)
                                            if (!newPresetName.trim()) { alert(t('admin.arch.schedule_preset_name')); return }
                                            if (Math.abs(total - 100) >= 0.01) { alert(t('admin.arch.schedule_preset_must_equal_100')); return }
                                            setAddingPreset(true)
                                            try {
                                                await createSchedulePreset({ name: newPresetName.trim(), rows: newPresetRows })
                                                setShowAddPreset(false)
                                                setNewPresetName('')
                                                setNewPresetRows([{ label: '', pct: 0 }])
                                                reloadSchedulePresets()
                                            } catch (e) { alert(e.message) }
                                            finally { setAddingPreset(false) }
                                        }}
                                        disabled={addingPreset}
                                        className="flex-1 py-2 text-xs font-bold text-white bg-amber-500 rounded-xl transition-all hover:bg-amber-600 disabled:opacity-60"
                                    >
                                        {addingPreset ? t('admin.estimator.saving') : t('admin.estimator.save')}
                                    </button>
                                    <button
                                        onClick={() => { setShowAddPreset(false); setNewPresetName(''); setNewPresetRows([{ label: '', pct: 0 }]) }}
                                        className="flex-1 py-2 text-xs font-bold text-gray-600 bg-gray-100 rounded-xl transition-all hover:bg-gray-200"
                                    >
                                        {t('admin.arch.cancel')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── Terms & Conditions ── */}
            {canEditCatalog && configSection === 'terms' && (
                <ArchTermsConfigTab session={session} defaultSourceFiles={defaultSourceFiles} setSourceDefault={setSourceDefault} />
            )}

            {/* ── Logs (Archived Estimates) ── */}
            {configSection === 'logs' && <ArchLogsSubTab />}
        </div>
    )
}

// ── Sortable term row for Config tab DnD ──────────────────────────────────
function SortableConfigTerm({ id, children }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
    return (
        <div
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : undefined }}
            className={`flex gap-1 items-center w-full ${isDragging ? 'opacity-60' : ''}`}
        >
            <div
                {...attributes} {...listeners}
                className="p-1 text-gray-300 select-none shrink-0 cursor-grab active:cursor-grabbing hover:text-gray-500"
                title="Drag to reorder"
            >
                <FaGripVertical size={12} />
            </div>
            {children}
        </div>
    )
}

// ── Search helpers ──────────────────────────────────────────────────────────
function useDebouncedValue(value, delay = 200) {
    const [debounced, setDebounced] = useState(value)
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay)
        return () => clearTimeout(t)
    }, [value, delay])
    return debounced
}

function buildSearchTokens(raw) {
    const q = (raw || '').trim().toLowerCase()
    if (!q) return []
    return [...new Set(q.split(/\s+/).filter(Boolean))]
}

function HighlightText({ text, tokens }) {
    if (!tokens?.length || !text) return <>{text}</>
    const pattern = tokens.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')
    const parts = text.split(new RegExp(`(${pattern})`, 'gi'))
    return (
        <>
            {parts.map((part, i) =>
                i % 2 === 1 ? <mark key={i} className="bg-amber-200 rounded px-0.5">{part}</mark> : part
            )}
        </>
    )
}

// ── Terms & Conditions Config Tab ─────────────────────────────────────────
function ArchTermsConfigTab({ session, defaultSourceFiles = new Set(), setSourceDefault }) {
    const { t } = useTranslation()
    const { items, loading, error, createTerm, updateTerm, deleteTerm, renameSourceFile, deleteSourceFile, reload, reorderTerms } = useArchTerms(session)
    const [editingId, setEditingId] = useState(null)
    const [editDraft, setEditDraft] = useState('')
    const [editMarket, setEditMarket] = useState('')
    const [addError, setAddError] = useState('')
    const [adding, setAdding] = useState(false)
    const [newText, setNewText] = useState('')
    const [newSourceFile, setNewSourceFile] = useState('custom')
    const [newSectionTitle, setNewSectionTitle] = useState('')
    const [newIsSectionTitle, setNewIsSectionTitle] = useState(false)
    const [newServiceGroups, setNewServiceGroups] = useState([])
    const [newMarket, setNewMarket] = useState('')
    const [searchQuery, setSearchQuery] = useState('')
    const debouncedQuery = useDebouncedValue(searchQuery)
    const searchTokens = useMemo(() => buildSearchTokens(debouncedQuery), [debouncedQuery])
    const [expandedFilesRaw, setExpandedFilesRaw] = usePersistentState(`est_arch_terms_expanded_${session?.user?.id || 'anon'}`, [])
    const [renamingSource, setRenamingSource] = useState(null) // { oldName: string, draft: string }
    const expandedFiles = useMemo(() => new Set(expandedFilesRaw), [expandedFilesRaw])
    const editRef = useRef(null)

    const SERVICE_GROUPS = ['ARCH', 'STRUCT', 'MEP', '3D', 'CIVIL', 'ECC', 'ADMIN']

    const uniqueSourceFiles = useMemo(() => {
        const set = new Set(items.map(i => i.source_file).filter(Boolean))
        return ['custom', ...Array.from(set).sort()]
    }, [items])

    const sectionOptions = useMemo(() => {
        if (newSourceFile === 'custom') return []
        const set = new Set()
        for (const item of items) {
            if (item.source_file === newSourceFile && item.is_section_title && item.text) {
                set.add(item.text)
            }
        }
        return Array.from(set).sort()
    }, [items, newSourceFile])

    useEffect(() => {
        if (editingId && editRef.current) editRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, [editingId])

    // Auto-expand all source files on first load only if no persisted preference
    useEffect(() => {
        if (items.length > 0 && expandedFilesRaw.length === 0) {
            setExpandedFilesRaw(Array.from(new Set(items.map(i => i.source_file))))
        }
    }, [items.length > 0 ? items[0]?.source_file : null]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleToggle = async (id, current) => {
        try { await updateTerm(id, { is_active: !current }) } catch (e) { alert(e.message) }
    }

    const handleDelete = async (id) => {
        if (!window.confirm(t('admin.arch.terms_delete_confirm'))) return
        try { await deleteTerm(id) } catch (e) { alert(e.message) }
    }

    const handleAdd = async () => {
        setAddError('')
        if (!newText.trim()) { setAddError(t('admin.arch.terms_add_error_text')); return }
        if (!newMarket) { setAddError(t('admin.arch.terms_market_required')); return }
        setAdding(true)
        const targetFile = newSourceFile || 'custom'
        const fileItems = items.filter(i => i.source_file === targetFile)
        const nextSort = fileItems.length > 0 ? Math.max(...fileItems.map(i => i.sort_order ?? 0)) + 1 : 0
        try {
            await createTerm({
                text: newText.trim(),
                market: newMarket,
                source_file: targetFile,
                section_title: newIsSectionTitle ? null : (newSectionTitle || null),
                is_section_title: newIsSectionTitle,
                sort_order: nextSort,
                service_groups: newServiceGroups.length > 0 ? newServiceGroups : null,
            })
            setNewText('')
            setNewSourceFile('custom')
            setNewSectionTitle('')
            setNewIsSectionTitle(false)
            setNewServiceGroups([])
            setNewMarket('')
            setExpandedFilesRaw(prev => prev.includes(targetFile) ? prev : [...prev, targetFile])
        } catch (e) { setAddError(e.message) }
        finally { setAdding(false) }
    }

    const handleSaveEdit = async (id) => {
        if (!editDraft.trim()) { alert(t('admin.arch.terms_edit_empty')); return }
        const patch = { text: editDraft.trim() }
        if (editMarket) patch.market = editMarket
        try { await updateTerm(id, patch); setEditingId(null) }
        catch (e) { alert(e.message) }
    }

    const handleRenameSource = async (oldName, newName) => {
        if (!newName.trim() || newName.trim() === oldName) { setRenamingSource(null); return }
        try {
            await renameSourceFile(oldName, newName.trim())
            setRenamingSource(null)
        } catch (e) { alert(e.message) }
    }

    const handleDeleteSource = async (sourceFile) => {
        if (!window.confirm(t('admin.arch.terms_delete_source_confirm', { name: displayFileName(sourceFile) }))) return
        try {
            await deleteSourceFile(sourceFile)
            setExpandedFilesRaw(prev => prev.filter(f => f !== sourceFile))
        } catch (e) { alert(e.message) }
    }

    const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))

    const toggleFile = (sourceFile) => {
        setExpandedFilesRaw(prev => {
            const has = prev.includes(sourceFile)
            return has ? prev.filter(f => f !== sourceFile) : [...prev, sourceFile]
        })
    }

    const fileGroups = useMemo(() => {
        // Collect items by source_file (items may be interleaved by global sort_order)
        const fileMap = new Map()
        const fileOrder = []
        for (const item of items) {
            if (!fileMap.has(item.source_file)) {
                fileMap.set(item.source_file, [])
                fileOrder.push(item.source_file)
            }
            fileMap.get(item.source_file).push(item)
        }

        const groups = []
        for (const sourceFile of fileOrder) {
            const rawItems = fileMap.get(sourceFile)
            // Deduplicate by text within this file (keep first occurrence)
            const seenTexts = new Set()
            const fileItems = []
            for (const item of rawItems) {
                if (seenTexts.has(item.text)) continue
                seenTexts.add(item.text)
                fileItems.push(item)
            }
            // Separate section headers from content
            const sectionHeaders = new Map() // secTitle → header item
            const contentItems = []
            for (const item of fileItems) {
                if (item.is_section_title) {
                    const key = item.text || ''
                    sectionHeaders.set(key, item)
                } else {
                    contentItems.push(item)
                }
            }
            // Group content items by section_title
            const sectionMap = new Map()
            const sectionOrder = []
            for (const item of contentItems) {
                const secTitle = item.section_title || ''
                if (!sectionMap.has(secTitle)) {
                    sectionMap.set(secTitle, { title: secTitle ? { text: secTitle } : null, items: [] })
                    sectionOrder.push(secTitle)
                }
                sectionMap.get(secTitle).items.push(item)
            }
            // Sort sections by the minimum sort_order of their items to preserve document order
            const sections = sectionOrder.map(key => {
                const sec = sectionMap.get(key)
                // Attach the header item if it exists
                if (sectionHeaders.has(key)) {
                    sec.headerItem = sectionHeaders.get(key)
                }
                return sec
            })
            sections.sort((a, b) => {
                const aMin = Math.min(...a.items.map(i => i.sort_order ?? Infinity))
                const bMin = Math.min(...b.items.map(i => i.sort_order ?? Infinity))
                return aMin - bMin
            })
            groups.push({ sourceFile, sections })
        }
        return groups
    }, [items])

    const filteredGroups = useMemo(() => {
        if (!debouncedQuery.trim()) return fileGroups
        const tokens = buildSearchTokens(debouncedQuery)
        return fileGroups.map(file => {
            const fileName = displayFileName(file.sourceFile).toLowerCase()
            const fileMatches = tokens.every(tok => fileName.includes(tok))
            if (fileMatches) return file
            const filteredSections = file.sections.map(sec => {
                const titleText = (sec.title?.text || '').toLowerCase()
                const headerText = (sec.headerItem?.text || '').toLowerCase()
                const secMatches = tokens.every(tok => titleText.includes(tok) || headerText.includes(tok))
                if (secMatches) return sec
                const filteredItems = sec.items.filter(it => {
                    const text = (it.text || '').toLowerCase()
                    const secTitle = (it.section_title || '').toLowerCase()
                    return tokens.every(tok => text.includes(tok) || secTitle.includes(tok) || fileName.includes(tok))
                })
                if (filteredItems.length === 0) return null
                return { ...sec, items: filteredItems }
            }).filter(Boolean)
            if (filteredSections.length === 0) return null
            return { ...file, sections: filteredSections }
        }).filter(Boolean)
    }, [fileGroups, debouncedQuery])

    const handleSectionDragEnd = useCallback((event) => {
        const { active, over } = event
        if (!over || active.id === over.id) return
        for (const file of fileGroups) {
            for (const section of file.sections) {
                const oldIndex = section.items.findIndex(i => i.id === active.id)
                const newIndex = section.items.findIndex(i => i.id === over.id)
                if (oldIndex !== -1 && newIndex !== -1) {
                    const reordered = arrayMove(section.items, oldIndex, newIndex)
                    const baseSort = section.items.length > 0 ? Math.min(...section.items.map(i => i.sort_order ?? 0)) : 0
                    reorderTerms(reordered.map(i => i.id), baseSort)
                    return
                }
            }
        }
    }, [fileGroups, reorderTerms])

    // Count from deduplicated fileGroups so the counter matches what's rendered
    const displayedTotal = fileGroups.reduce((n, file) => n + file.sections.reduce((m, sec) => m + sec.items.length + (sec.headerItem ? 1 : 0), 0), 0)
    const displayedActive = fileGroups.reduce((n, file) => n + file.sections.reduce((m, sec) => m + sec.items.filter(it => it.is_active).length + (sec.headerItem?.is_active ? 1 : 0), 0), 0)

    return (
        <div className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="flex justify-between items-center mb-4">
                <div>
                    <h3 className="flex gap-2 items-center font-bold text-gray-800">
                        <FaStickyNote size={13} className="text-primary" />
                        {t('admin.arch.config_section_terms')}
                        <span className="text-[10px] font-normal text-gray-400 ml-1">{displayedActive}/{displayedTotal}</span>
                    </h3>
                </div>
                <button onClick={reload} className="p-1.5 text-gray-400 rounded-lg border border-gray-200 hover:bg-gray-50 transition-all"><FaSync size={11} /></button>
            </div>

            {/* Add custom term */}
            <div className="flex flex-col gap-3 p-4 mb-4 bg-gray-50 rounded-xl border border-gray-200">
                {/* Row 1: Meta controls */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-12 sm:items-end">
                    {/* Market */}
                    <div className="sm:col-span-3">
                        <label className="block mb-1 text-[10px] font-semibold tracking-wide text-gray-500 uppercase">{t('admin.arch.terms_label_market')}</label>
                        <div className="relative">
                            <select
                                value={newMarket}
                                onChange={e => setNewMarket(e.target.value)}
                                className="w-full px-2.5 py-1.5 pr-8 text-sm rounded-lg border border-gray-200 bg-white text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                            >
                                <option value="">— {t('admin.arch.terms_market_placeholder')} —</option>
                                <option value="FL">Florida</option>
                                <option value="TX">Texas</option>
                                <option value="both">{t('admin.arch.terms_market_both')}</option>
                            </select>
                            <FaChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" size={10} />
                        </div>
                    </div>
                    {/* Source file */}
                    <div className="sm:col-span-3">
                        <label className="block mb-1 text-[10px] font-semibold tracking-wide text-gray-500 uppercase">{t('admin.arch.terms_label_source_file')}</label>
                        <div className="relative">
                            <select
                                value={newSourceFile}
                                onChange={e => {
                                    const val = e.target.value
                                    setNewSourceFile(val)
                                    setNewSectionTitle('')
                                }}
                                className="w-full px-2.5 py-1.5 pr-8 text-sm rounded-lg border border-gray-200 bg-white text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                            >
                                <option value="custom">— Custom —</option>
                                {uniqueSourceFiles.filter(f => f !== 'custom').map(f => (
                                    <option key={f} value={f} title={f}>{f.length > 36 ? displayFileName(f).slice(0, 33) + '…' : displayFileName(f)}</option>
                                ))}
                            </select>
                            <FaChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" size={10} />
                        </div>
                    </div>
                    {/* Section */}
                    <div className="sm:col-span-4">
                        <label className="block mb-1 text-[10px] font-semibold tracking-wide text-gray-500 uppercase">{t('admin.arch.terms_label_section')}</label>
                        <div className="relative">
                            <select
                                value={newSectionTitle}
                                onChange={e => setNewSectionTitle(e.target.value)}
                                disabled={newSourceFile === 'custom' || newIsSectionTitle}
                                className="w-full px-2.5 py-1.5 pr-8 text-sm rounded-lg border border-gray-200 bg-white text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50"
                            >
                                <option value="">— None —</option>
                                {sectionOptions.map(sec => (
                                    <option key={sec} value={sec} title={sec}>{sec.length > 36 ? sec.slice(0, 33) + '…' : sec}</option>
                                ))}
                            </select>
                            <FaChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" size={10} />
                        </div>
                    </div>
                    {/* Is section title */}
                    <div className="sm:col-span-2">
                        <label className="flex gap-2 items-center px-3 py-2 text-xs font-medium text-gray-600 bg-white rounded-lg border border-gray-200 transition-colors cursor-pointer hover:bg-gray-50">
                            <input
                                type="checkbox"
                                checked={newIsSectionTitle}
                                onChange={e => setNewIsSectionTitle(e.target.checked)}
                                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary/30"
                            />
                            <span>{t('admin.arch.terms_is_section_title')}</span>
                        </label>
                    </div>
                </div>
                {/* Row 1.5: Service groups */}
                <div className="flex flex-wrap gap-1.5">
                    <span className="text-[10px] text-gray-400 self-center mr-1">{t('admin.arch.terms_label_groups')}:</span>
                    {SERVICE_GROUPS.map(sg => {
                        const active = newServiceGroups.includes(sg)
                        return (
                            <button
                                key={sg}
                                type="button"
                                onClick={() => setNewServiceGroups(prev => active ? prev.filter(g => g !== sg) : [...prev, sg])}
                                className={`px-2 py-0.5 text-[10px] font-semibold rounded-md border transition-all ${active ? 'bg-primary/10 text-primary border-primary/30' : 'bg-gray-50 text-gray-400 border-gray-200 hover:border-gray-300'}`}
                            >
                                {sg}
                            </button>
                        )
                    })}
                </div>
                {/* Row 2: Text + Save */}
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                    <textarea value={newText} onChange={e => setNewText(e.target.value)}
                        rows={2}
                        placeholder={t('admin.arch.terms_add_placeholder')}
                        className="flex-1 px-3 py-2 w-full text-sm rounded-lg border border-gray-200 resize-y focus:outline-none focus:ring-2 focus:ring-primary/30" />
                    <button onClick={handleAdd} disabled={adding || !newText.trim()}
                        className="flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-all shrink-0">
                        <FaPlus size={10} /> {adding ? '…' : t('admin.estimator.save')}
                    </button>
                </div>
                {addError && <p className="px-3 py-2 text-xs text-red-500 bg-red-50 rounded-lg border border-red-100">{addError}</p>}
            </div>

            {/* Search bar */}
            <div className="relative mb-4">
                <FaSearch size={12} className="absolute left-3 top-1/2 text-gray-400 -translate-y-1/2" />
                <input
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder={t('admin.arch.terms_search_placeholder')}
                    className="py-2 pr-8 pl-8 w-full text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 text-gray-400 -translate-y-1/2 hover:text-gray-600">
                        <FaTimes size={10} />
                    </button>
                )}
            </div>

            {loading && <p className="py-6 text-sm text-center text-gray-400">{t('admin.estimator.loading')}</p>}
            {error && <p className="py-6 text-sm text-center text-red-400">{error}</p>}

            {!loading && filteredGroups.length === 0 && (
                <p className="py-6 text-sm italic text-center text-gray-400">{t('admin.arch.terms_empty')}</p>
            )}

            {!loading && filteredGroups.map((file) => (
                <div key={file.sourceFile} className="mb-4">
                    {/* File header (collapsible) */}
                    {renamingSource?.oldName === file.sourceFile ? (
                        <div className="flex gap-2 items-center px-3 py-2 mb-2 w-full bg-gray-50 rounded-lg border border-gray-200">
                            <input
                                autoFocus
                                value={renamingSource.draft}
                                onChange={e => setRenamingSource(prev => ({ ...prev, draft: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Enter') handleRenameSource(file.sourceFile, renamingSource.draft); if (e.key === 'Escape') setRenamingSource(null) }}
                                className="flex-1 px-2 py-1 text-xs rounded-lg border border-primary/50 focus:outline-none focus:ring-1 focus:ring-primary/30"
                            />
                            <button onClick={() => handleRenameSource(file.sourceFile, renamingSource.draft)} className="p-1 text-emerald-500 hover:text-emerald-600"><FaCheck size={10} /></button>
                            <button onClick={() => setRenamingSource(null)} className="p-1 text-gray-400 hover:text-gray-600"><FaTimes size={10} /></button>
                        </div>
                    ) : (
                        <div className={`flex gap-2 items-center px-3 py-2 mb-2 w-full rounded-lg border transition-colors hover:bg-gray-100 ${defaultSourceFiles.has(file.sourceFile) ? 'bg-emerald-50/60 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}>
                            <button onClick={() => toggleFile(file.sourceFile)} className="flex flex-1 gap-2 items-center min-w-0 text-left">
                                {expandedFiles.has(file.sourceFile) ? <FaChevronUp size={10} className="text-gray-500 shrink-0" /> : <FaChevronDown size={10} className="text-gray-500 shrink-0" />}
                                <span className="text-[11px] font-bold tracking-wide text-gray-700 uppercase truncate" title={displayFileName(file.sourceFile)}>{displayFileName(file.sourceFile)}</span>
                                <span className="text-[10px] text-gray-400 shrink-0 ml-auto">{file.sections.reduce((n, s) => n + s.items.length + (s.headerItem ? 1 : 0), 0)}</span>
                            </button>
                            <div className="flex gap-1 items-center shrink-0">
                                {defaultSourceFiles.has(file.sourceFile) && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200 shrink-0">
                                        <FaStar size={7} /> {t('admin.arch.terms_source_is_default')}
                                    </span>
                                )}
                                {setSourceDefault && (
                                    <button
                                        onClick={async () => {
                                            try { await setSourceDefault(file.sourceFile, !defaultSourceFiles.has(file.sourceFile)) }
                                            catch (e) { alert(e.message) }
                                        }}
                                        className={`flex items-center gap-1 p-1 rounded-md transition-all ${defaultSourceFiles.has(file.sourceFile) ? 'text-emerald-600 hover:text-emerald-800' : 'text-gray-400 hover:text-emerald-600'}`}
                                        title={defaultSourceFiles.has(file.sourceFile) ? t('admin.arch.terms_source_is_default') : t('admin.arch.terms_set_source_default')}
                                    >
                                        {defaultSourceFiles.has(file.sourceFile) ? <FaStar size={10} /> : <FaRegStar size={10} />}
                                    </button>
                                )}
                                <button onClick={() => setRenamingSource({ oldName: file.sourceFile, draft: displayFileName(file.sourceFile) })} className="p-1 text-gray-400 hover:text-primary" title={t('admin.arch.terms_rename_source')}><FaEdit size={10} /></button>
                                <button onClick={() => handleDeleteSource(file.sourceFile)} className="p-1 text-gray-400 hover:text-red-500" title={t('admin.arch.terms_delete_source')}><FaTrash size={9} /></button>
                            </div>
                        </div>
                    )}

                    {expandedFiles.has(file.sourceFile) && (
                        <div className="pl-3 space-y-3">
                            {file.sections.map((section, sIdx) => (
                                <div key={sIdx}>
                                    {/* Section title badge — only shown when there's no explicit headerItem */}
                                    {section.title && !section.headerItem && (
                                        <div className="flex gap-2 items-center px-3 py-1.5 mb-1.5 bg-gray-100 rounded-md">
                                            <span className="text-[10px] font-bold tracking-wide text-gray-600 uppercase"><HighlightText text={section.title.text} tokens={searchTokens} /></span>
                                        </div>
                                    )}
                                    {/* Section header item (is_section_title row) */}
                                    {section.headerItem && (
                                        <div ref={editingId === section.headerItem.id ? editRef : null}
                                            className={`w-full flex gap-2 items-center px-3 py-2 mb-1 rounded-lg border ${section.headerItem.is_active ? 'bg-amber-50 border-amber-100' : 'bg-gray-50 border-gray-100 opacity-60'}`}>
                                            <button onClick={() => handleToggle(section.headerItem.id, section.headerItem.is_active)} className="shrink-0">
                                                {section.headerItem.is_active ? <FaToggleOn className="text-amber-400" size={18} /> : <FaToggleOff className="text-gray-300" size={18} />}
                                            </button>
                                            {editingId === section.headerItem.id ? (
                                                <div className="flex flex-col flex-1 gap-1">
                                                    <textarea autoFocus value={editDraft} onChange={e => setEditDraft(e.target.value)}
                                                        rows={2}
                                                        onKeyDown={e => { if (e.key === 'Escape') setEditingId(null) }}
                                                        className="px-2 py-1 w-full text-xs bg-white rounded-lg border resize-y border-primary/50 focus:outline-none focus:ring-1 focus:ring-primary/30" />
                                                    <div className="relative">
                                                        <select value={editMarket} onChange={e => setEditMarket(e.target.value)}
                                                            className="px-2 py-1 pr-6 w-full text-xs text-gray-700 bg-white rounded-lg border border-gray-200 appearance-none focus:outline-none focus:ring-1 focus:ring-primary/30">
                                                            <option value="">— {t('admin.arch.terms_label_market')} —</option>
                                                            <option value="FL">Florida</option>
                                                            <option value="TX">Texas</option>
                                                            <option value="both">{t('admin.arch.terms_market_both')}</option>
                                                        </select>
                                                        <FaChevronDown className="absolute right-2 top-1/2 text-gray-400 -translate-y-1/2 pointer-events-none" size={9} />
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className="flex-1 text-sm font-semibold text-amber-700"><HighlightText text={section.headerItem.text} tokens={searchTokens} /></span>
                                            )}
                                            <div className="flex gap-1 shrink-0">
                                                {editingId === section.headerItem.id ? (
                                                    <>
                                                        <button onClick={() => handleSaveEdit(section.headerItem.id)} className="p-1 text-emerald-500 hover:text-emerald-600"><FaCheck size={10} /></button>
                                                        <button onClick={() => setEditingId(null)} className="p-1 text-gray-400 hover:text-gray-600"><FaTimes size={10} /></button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button onClick={() => { setEditingId(section.headerItem.id); setEditDraft(section.headerItem.text); setEditMarket(section.headerItem.market || '') }} className="p-1 text-gray-400 hover:text-amber-500"><FaEdit size={10} /></button>
                                                        <button onClick={() => handleDelete(section.headerItem.id)} className="p-1 text-gray-400 hover:text-red-500"><FaTrash size={9} /></button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {debouncedQuery.trim() ? (
                                        <div className="space-y-1">
                                            {section.items.map((item) => (
                                                <div key={item.id} ref={editingId === item.id ? editRef : null}
                                                    className={`w-full flex gap-2 items-center px-3 py-2 rounded-lg border group ${item.is_active ? 'bg-white border-gray-100' : 'bg-gray-50 border-gray-100 opacity-60'}`}>
                                                    <button onClick={() => handleToggle(item.id, item.is_active)} className="shrink-0">
                                                        {item.is_active ? <FaToggleOn className="text-primary" size={18} /> : <FaToggleOff className="text-gray-300" size={18} />}
                                                    </button>
                                                    {editingId === item.id ? (
                                                        <div className="flex flex-col flex-1 gap-1">
                                                            <textarea autoFocus value={editDraft} onChange={e => setEditDraft(e.target.value)}
                                                                rows={2}
                                                                onKeyDown={e => { if (e.key === 'Escape') setEditingId(null) }}
                                                                className="px-2 py-1 w-full text-xs bg-white rounded-lg border resize-y border-primary/50 focus:outline-none focus:ring-1 focus:ring-primary/30" />
                                                            <div className="relative">
                                                                <select value={editMarket} onChange={e => setEditMarket(e.target.value)}
                                                                    className="px-2 py-1 pr-6 w-full text-xs text-gray-700 bg-white rounded-lg border border-gray-200 appearance-none focus:outline-none focus:ring-1 focus:ring-primary/30">
                                                                    <option value="">— {t('admin.arch.terms_label_market')} —</option>
                                                                    <option value="FL">Florida</option>
                                                                    <option value="TX">Texas</option>
                                                                    <option value="both">{t('admin.arch.terms_market_both')}</option>
                                                                </select>
                                                                <FaChevronDown className="absolute right-2 top-1/2 text-gray-400 -translate-y-1/2 pointer-events-none" size={9} />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <span className="flex-1 text-sm text-gray-700"><HighlightText text={item.text} tokens={searchTokens} /></span>
                                                    )}
                                                    <div className="flex gap-1 opacity-100 transition-opacity sm:opacity-0 shrink-0 sm:group-hover:opacity-100">
                                                        {editingId === item.id ? (
                                                            <>
                                                                <button onClick={() => handleSaveEdit(item.id)} className="p-1 text-emerald-500 hover:text-emerald-600"><FaCheck size={10} /></button>
                                                                <button onClick={() => setEditingId(null)} className="p-1 text-gray-400 hover:text-gray-600"><FaTimes size={10} /></button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <button onClick={() => { setEditingId(item.id); setEditDraft(item.text); setEditMarket(item.market || '') }} className="p-1 text-gray-400 hover:text-primary"><FaEdit size={10} /></button>
                                                                <button onClick={() => handleDelete(item.id)} className="p-1 text-gray-400 hover:text-red-500"><FaTrash size={9} /></button>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <DndContext key={`dnd-${file.sourceFile}-${sIdx}`} id={`terms-${file.sourceFile}-${sIdx}`} sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
                                            <SortableContext items={section.items.map(i => i.id)} strategy={verticalListSortingStrategy}>
                                                <div className="space-y-1">
                                                    {section.items.map((item) => (
                                                        <SortableConfigTerm key={item.id} id={item.id}>
                                                            <div ref={editingId === item.id ? editRef : null}
                                                                className={`w-full flex gap-2 items-center px-3 py-2 rounded-lg border group ${item.is_active ? 'bg-white border-gray-100' : 'bg-gray-50 border-gray-100 opacity-60'}`}>
                                                                <button onClick={() => handleToggle(item.id, item.is_active)} className="shrink-0">
                                                                    {item.is_active ? <FaToggleOn className="text-primary" size={18} /> : <FaToggleOff className="text-gray-300" size={18} />}
                                                                </button>
                                                                {editingId === item.id ? (
                                                                    <div className="flex flex-col flex-1 gap-1">
                                                                        <textarea autoFocus value={editDraft} onChange={e => setEditDraft(e.target.value)}
                                                                            rows={2}
                                                                            onKeyDown={e => { if (e.key === 'Escape') setEditingId(null) }}
                                                                            className="px-2 py-1 w-full text-xs bg-white rounded-lg border resize-y border-primary/50 focus:outline-none focus:ring-1 focus:ring-primary/30" />
                                                                        <div className="relative">
                                                                            <select value={editMarket} onChange={e => setEditMarket(e.target.value)}
                                                                                className="px-2 py-1 pr-6 w-full text-xs text-gray-700 bg-white rounded-lg border border-gray-200 appearance-none focus:outline-none focus:ring-1 focus:ring-primary/30">
                                                                                <option value="">— {t('admin.arch.terms_label_market')} —</option>
                                                                                <option value="FL">Florida</option>
                                                                                <option value="TX">Texas</option>
                                                                                <option value="both">{t('admin.arch.terms_market_both')}</option>
                                                                            </select>
                                                                            <FaChevronDown className="absolute right-2 top-1/2 text-gray-400 -translate-y-1/2 pointer-events-none" size={9} />
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <span className="flex-1 text-sm text-gray-700"><HighlightText text={item.text} tokens={searchTokens} /></span>
                                                                )}
                                                                <div className="flex gap-1 opacity-100 transition-opacity sm:opacity-0 shrink-0 sm:group-hover:opacity-100">
                                                                    {editingId === item.id ? (
                                                                        <>
                                                                            <button onClick={() => handleSaveEdit(item.id)} className="p-1 text-emerald-500 hover:text-emerald-600"><FaCheck size={10} /></button>
                                                                            <button onClick={() => setEditingId(null)} className="p-1 text-gray-400 hover:text-gray-600"><FaTimes size={10} /></button>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <button onClick={() => { setEditingId(item.id); setEditDraft(item.text); setEditMarket(item.market || '') }} className="p-1 text-gray-400 hover:text-primary"><FaEdit size={10} /></button>
                                                                            <button onClick={() => handleDelete(item.id)} className="p-1 text-gray-400 hover:text-red-500"><FaTrash size={9} /></button>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </SortableConfigTerm>
                                                    ))}
                                                </div>
                                            </SortableContext>
                                        </DndContext>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    )
}

// ── Logs Sub-Tab for Arch Estimates ─────────────────────────────────────────
function ArchLogsSubTab() {
    const { t } = useTranslation()
    const { estimates, loading, error, reload } = useArchivedArchEstimates()
    const [searchQuery, setSearchQuery] = useState('')
    const [clientFilter, setClientFilter] = useState('')
    const [archiverFilter, setArchiverFilter] = useState('')

    const uniqueClients = useMemo(() => {
        const set = new Set(estimates.map(e => (e.client || '').trim()).filter(Boolean))
        return Array.from(set).sort((a, b) => a.localeCompare(b))
    }, [estimates])

    const uniqueArchivers = useMemo(() => {
        const set = new Set(estimates.map(e => e.archived_by_name || e.archived_by_email || '—').filter(n => n !== '—'))
        return Array.from(set).sort((a, b) => a.localeCompare(b))
    }, [estimates])

    const groups = useMemo(() => {
        let filtered = estimates
        const q = searchQuery.trim().toLowerCase()
        if (q) {
            filtered = filtered.filter(est =>
                (est.number || '').toLowerCase().includes(q) ||
                (est.client || '').toLowerCase().includes(q) ||
                (est.address || '').toLowerCase().includes(q) ||
                (est.archived_by_name || '').toLowerCase().includes(q) ||
                (est.archived_by_email || '').toLowerCase().includes(q)
            )
        }
        if (clientFilter) {
            filtered = filtered.filter(est => (est.client || '').trim() === clientFilter)
        }
        if (archiverFilter) {
            filtered = filtered.filter(est => (est.archived_by_name || est.archived_by_email || '—') === archiverFilter)
        }
        const map = {}
        for (const est of filtered) {
            const key = (est.client || '').trim().toLowerCase() || '__none__'
            const label = (est.client || '').trim() || null
            if (!map[key]) map[key] = { label, key, items: [] }
            map[key].items.push(est)
        }
        return Object.values(map).sort((a, b) => {
            if (a.key === '__none__') return 1
            if (b.key === '__none__') return -1
            return a.key.localeCompare(b.key)
        })
    }, [estimates, searchQuery, clientFilter, archiverFilter])

    if (loading) return <p className="py-10 text-center text-gray-400">{t('admin.estimator.loading')}</p>
    if (error)   return <p className="py-10 text-center text-red-400">{error}</p>
    if (estimates.length === 0) return <p className="py-14 text-sm text-center text-gray-400">{t('admin.arch.logs_empty')}</p>

    return (
        <div className="space-y-4">
            <div className="flex gap-3 items-center">
                <h2 className="text-lg font-bold text-gray-800">{t('admin.arch.logs_title')}</h2>
                <button onClick={reload} className="p-2 text-gray-400 rounded-xl border border-gray-200 transition-all hover:bg-gray-50">
                    <FaSync size={13} />
                </button>
            </div>
            <div className="flex flex-wrap gap-3 items-center">
                <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder={t('admin.arch.logs_search_placeholder')}
                    className="flex-1 min-w-[12rem] px-3 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                <div className="relative min-w-[10rem]">
                    <select
                        value={clientFilter}
                        onChange={e => setClientFilter(e.target.value)}
                        className="px-3 py-2 pr-8 w-full text-sm text-gray-700 bg-white rounded-xl border border-gray-200 appearance-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                    >
                        <option value="">{t('admin.arch.logs_filter_all_clients')}</option>
                        {uniqueClients.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <FaChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" size={10} />
                </div>
                <div className="relative min-w-[10rem]">
                    <select
                        value={archiverFilter}
                        onChange={e => setArchiverFilter(e.target.value)}
                        className="px-3 py-2 pr-8 w-full text-sm text-gray-700 bg-white rounded-xl border border-gray-200 appearance-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                    >
                        <option value="">{t('admin.arch.logs_filter_all_users')}</option>
                        {uniqueArchivers.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <FaChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" size={10} />
                </div>
            </div>
            <div className="grid gap-2">
                {groups.map(group => {
                    const latestEst = group.items.reduce((a, e) => (!a || e.updated_at > a.updated_at ? e : a), null)
                    return (
                        <div key={group.key} className="overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-sm">
                            <div className="flex gap-3 items-center px-4 py-3">
                                <div className="p-2 bg-gray-100 rounded-xl shrink-0">
                                    <FaHistory className="text-gray-500" size={13} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-800 truncate">
                                        {group.label || <span className="italic text-gray-400">{t('admin.estimator.no_client')}</span>}
                                    </p>
                                    <p className="text-[11px] text-gray-400 mt-0.5">
                                        {t('admin.arch.estimates_count', { count: group.items.length })}
                                    </p>
                                </div>
                                <div className="text-right shrink-0">
                                    <p className="text-sm font-bold text-primary">${fmt(latestEst?.total_contract || 0)}</p>
                                    <p className="text-[10px] text-gray-400 mt-0.5">{t('admin.estimator.latest_total')}</p>
                                </div>
                            </div>
                            <div className="px-4 pb-3 space-y-2 border-t border-gray-100">
                                {group.items.map(est => {
                                    const date = est.updated_at
                                        ? new Date(est.updated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                                        : ''
                                    const archiverName = est.archived_by_name || est.archived_by_email || '—'
                                    return (
                                        <div key={est.id} className="flex gap-3 items-center pt-2.5">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex flex-wrap gap-1.5 items-center">
                                                    <span className="font-mono text-[11px] text-gray-400">{est.number}</span>
                                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-red-100 text-red-600">{t('admin.arch.archived_status')}</span>
                                                </div>
                                                <p className="text-[11px] text-gray-400 mt-0.5">
                                                    {date}{est.address ? ` · ${est.address}` : ''}
                                                </p>
                                                <p className="text-[11px] text-gray-400 mt-0.5">
                                                    {t('admin.arch.archived_by_label')}: <span className="font-semibold text-gray-600">{archiverName}</span>
                                                </p>
                                                {est.min_price_override && (
                                                    <p className="mt-1 text-[10px]">
                                                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold border ${est.min_price_override.type === 'disable' ? 'text-red-600 bg-red-50 border-red-200' : 'text-amber-600 bg-amber-50 border-amber-200'}`}>
                                                            <FaExclamationTriangle size={8} />
                                                            {est.min_price_override.type === 'disable'
                                                                ? (t('admin.arch.override_min_badge_disabled'))
                                                                : t('admin.arch.override_min_badge_custom', { value: fmt(Number(est.min_price_override.value) || 0) })}
                                                            {est.min_price_override.appliedBy && (
                                                                <span className="font-normal opacity-60">— {est.min_price_override.appliedBy}</span>
                                                            )}
                                                        </span>
                                                        {est.min_price_override.reason && (
                                                            <span className="block mt-0.5 text-gray-400 italic">"{est.min_price_override.reason}"</span>
                                                        )}
                                                    </p>
                                                )}
                                                <p className="text-sm font-bold text-primary mt-0.5">${fmt(est.total_contract || 0)}</p>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

// ── Main component ────────────────────────────────────────────────────────────
const ARCH_TAB_DEFS = [
    { id: 'estimados',  tKey: 'admin.arch.tab_estimados',  icon: FaFileInvoiceDollar },
    { id: 'constructor',tKey: 'admin.arch.tab_constructor', icon: FaCalculator },
    { id: 'config',     tKey: 'admin.arch.tab_config',     icon: FaCog },
]

export default function ArchEstimateBuilder({ session, onReturn, onLogout, onSwitchToConstruction }) {
    const { t }      = useTranslation()
    const navigate   = useNavigate()
    const userId     = session?.user?.id || null
    const { role, isSuperAdmin, isPM, permissions, loading: roleLoading } = useRole()
    const canEditCatalog = !!permissions['admin.estimator.edit_catalog']
    const hasCon = !!permissions['admin.estimator.construction']
    const hasArch = !!permissions['admin.estimator.architecture']
    const showModeToggle = hasCon && hasArch

    const [tab,      setTab]      = usePersistentState(`est_arch_tab_${userId}`, 'estimados')

    // If the persisted tab is 'config' but the user no longer has access, reset to 'estimados'
    useEffect(() => {
        if (!roleLoading && tab === 'config' && !canEditCatalog) setTab('estimados')
    }, [roleLoading, canEditCatalog, tab]) // eslint-disable-line react-hooks/exhaustive-deps

    const { rows: schedulePresets, loading: schedulePresetsLoading, createSchedulePreset, updateSchedulePreset, deleteSchedulePreset, setDefaultPreset, clearDefaultPreset, reload: reloadSchedulePresets } = useArchSchedulePresets()

    const [estimate, setEstimate] = useState(() => {
        const base = EMPTY_ARCH_ESTIMATE()
        base.cuotas = getStandardCuotas(t, schedulePresets)
        return base
    })

    const { getMarketConfig, marketOptions, rows: configRows } = useArchConfig()
    const { rolesByType } = useArchCostRoles(estimate.market)
    const { presets: dbDisciplinePresets } = useArchDisciplinePresets()
    const { multipliers: dbScopeMultipliers } = useArchScopeMultipliers()
    const { getMinFloors } = useArchMinFloors(configRows)
    const { estimates: archEstimates, loading: archEstimatesLoading, error: archEstimatesError, reload: reloadArchEstimates, silentReload: silentReloadArchEstimates } = useArchEstimates()
    const { items: archCatalogItems, upsertArchCatalogItem, removeArchCatalogItem, reload: reloadCatalog, silentReload: silentReloadCatalog } = useArchCatalogItems(session)
    const { levels: difficultyLevels, loading: difficultyLoading, reload: reloadDifficultyLevels, upsertLevel: upsertDifficultyLevel, removeLevel: removeDifficultyLevel } = useArchDifficultyLevels()
    const { rows: paymentInfoRows, loading: paymentInfoLoading, getPaymentInfos, createPaymentInfo, updatePaymentInfo, deletePaymentInfo, reload: reloadPaymentInfo } = useArchPaymentInfo()
    const { rows: addAltDiffRows, loading: addAltDiffLoading, getOptionsForComponent, upsertDifficultyOption, removeDifficultyOption, reload: reloadAddAltDifficulties } = useArchAddAltDifficulties()
    const { levels: civilLevels, loading: civilLoading, reload: reloadCivilLevels, upsertLevel: upsertCivilLevel, removeLevel: removeCivilLevel } = useArchCivilDifficultyLevels()
    const { items: archTermsConditions } = useArchTerms(session)
    const { defaultSourceFiles, setSourceDefault } = useArchTermsSourceDefaults()
    const { items: addonDefs, createArchAddon, updateArchAddon, removeArchAddon, reload: reloadAddons, silentReload: silentReloadAddons } = useArchAddons(session)

    // Initialize estimate.addons from defaults when addonDefs loads and addons is empty
    useEffect(() => {
        if (addonDefs.length > 0 && (!estimate.addons || estimate.addons.length === 0)) {
            setEstimate(prev => ({ ...prev, addons: getDefaultEstimateAddons(addonDefs) }))
        }
    }, [addonDefs, estimate.addons])

    // Auto-populate cuotas from default preset when schedulePresets loads and estimate is new/empty
    useEffect(() => {
        if (schedulePresets.length === 0) return
        const defaultPreset = schedulePresets.find(p => p.is_default)
        if (!defaultPreset) return
        setEstimate(prev => {
            if (prev.id) return prev
            if (prev.cuotas && prev.cuotas.length > 0) return prev
            return { ...prev, cuotas: defaultPreset.rows.map(r => ({ label: r.label || '', pct: r.pct })) }
        })
    }, [schedulePresets])

    // Auto-populate terms from default source files when catalog + defaults load and estimate is new/empty
    useEffect(() => {
        if (defaultSourceFiles.size === 0) return
        if (archTermsConditions.length === 0) return
        setEstimate(prev => {
            if (prev.id) return prev
            if (prev.terms && prev.terms.length > 0) return prev
            const defaults = getDefaultTermsFromCatalog(archTermsConditions, defaultSourceFiles, prev.market)
            if (defaults.length === 0) return prev
            return { ...prev, terms: defaults }
        })
    }, [archTermsConditions, defaultSourceFiles])

    // Preload Monday estimates board once on mount
    const [allMondayItems, setAllMondayItems] = useState(null)
    const [mondayItemsLoading, setMondayItemsLoading] = useState(false)
    const loadMondayItems = useCallback(async (force = false) => {
        setMondayItemsLoading(true)
        try {
            const raw = await fetchEstimatesBoard(session, { force })
            setAllMondayItems(raw.items || [])
        } catch { setAllMondayItems([]) }
        finally { setMondayItemsLoading(false) }
    }, [session])
    useEffect(() => {
        if (session) loadMondayItems()
    }, [session, loadMondayItems])

    const handleApplyMonday = useCallback((item) => {
        const clientName = monCol(item, MON_CLIENT_COL)
        const rawName    = item.name || ''
        const pipeIdx    = rawName.indexOf('|')
        const address    = pipeIdx !== -1 ? rawName.slice(pipeIdx + 1).trim() : rawName
        const officeTxt  = monCol(item, MON_OFFICE_COL).toLowerCase()
        const state      = officeTxt.includes('dallas') ? 'TX' : 'FL'
        const typeTxtRaw = monCol(item, MON_TYPE_COL)
        const descTxtRaw = monCol(item, MON_DESC_COL)
        const isRes      = typeTxtRaw.toLowerCase().includes('resid')
        const isCivil    = resolveProjectTypeKey(typeTxtRaw, descTxtRaw) === 'civil'
        const marketKey  = isCivil ? `${state}_CIVIL` : `${state}_${isRes ? 'RES' : 'COM'}`
        const phone      = monCol(item, MON_PHONE_COL)
        const email      = monCol(item, MON_EMAIL_COL)

        // Resolve project_type key from Monday Type + Description
        const ptKey = resolveProjectTypeKey(typeTxtRaw, descTxtRaw)
        setAutoPhase('cds')

        setEstimate(prev => {
            // Auto-populate phase-specific items
            let scopeItems = []
            if (ptKey && archCatalogItems?.length) {
                scopeItems = archCatalogItems
                    .filter(ci => ci.project_type === ptKey && ci.phase === 'cds')
                    .map(ci => ({ id: ci.id, name: ci.name, discipline: ci.discipline, subcat: ci.subcat, phase: ci.phase }))
            }

            // Merge enabled add-on items
            const seenIds = new Set(scopeItems.map(i => i.id))
            const currentAddons = prev.addons || getDefaultEstimateAddons(addonDefs)
            for (const addon of currentAddons.filter(a => a.enabled)) {
                const def = addonDefs.find(d => d.key === addon.key)
                if (!def) continue
                const ids = getAddonScopeItemIds(def, archCatalogItems, ptKey, 'cds')
                for (const ci of archCatalogItems) {
                    if (ids.has(ci.id) && !seenIds.has(ci.id)) {
                        seenIds.add(ci.id)
                        scopeItems.push({ id: ci.id, name: ci.name, discipline: ci.discipline, subcat: ci.subcat, phase: ci.phase })
                    }
                }
            }
            scopeItems = filterByProjectTypeSuffix(scopeItems, ptKey)

            // Snapshot current scopeItems for undo
            if (scopeItems.length > 0) {
                setUndoScopeSnapshot(prev.scopeItems || [])
                clearTimeout(undoTimerRef.current)
                undoTimerRef.current = setTimeout(() => setUndoScopeSnapshot(null), 8000)
                setDisciplineSuggestion(deriveDisciplineSuggestion(scopeItems))
            }

            const addAltDiffs = ADD_ALT_PROJECT_TYPES.has(ptKey)
                ? ADD_ALT_DIFFICULTY_DEFAULTS[state] || ADD_ALT_DIFFICULTY_DEFAULTS.FL
                : null
            return {
                ...prev,
                client:             clientName  || prev.client,
                phone:              phone       || prev.phone,
                email:              email       || prev.email,
                mondayItemId:       String(item.id),
                projectType:        ptKey || typeTxtRaw || prev.projectType,
                projectDescription: descTxtRaw  || prev.projectDescription,
                address:            address     || prev.address,
                market:             marketKey,
                office:             officeTxt || prev.office,
                terms:              getDefaultTermsFromCatalog(archTermsConditions, defaultSourceFiles, marketKey),
                ...(ptKey === 'civil' ? { disciplineScope: 'solo_civil' } : {}),
                ...(addAltDiffs ? { existDiff: addAltDiffs.exist, remDiff: addAltDiffs.remodel, addDiff: addAltDiffs.adicion } : {}),
                ...(scopeItems.length > 0 ? { scopeItems } : {}),
            }
        })
    }, [setEstimate, archCatalogItems, addonDefs, archTermsConditions, defaultSourceFiles])

    // Min-price-override modal
    const [minOvOpen,     setMinOvOpen]     = useState(false)
    const [minOvType,     setMinOvType]     = useState('disable')
    const [minOvValue,    setMinOvValue]    = useState('')
    const [minOvReason,   setMinOvReason]   = useState('')

    const openMinOverride = useCallback(() => {
        setMinOvOpen(true)
        setMinOvType('disable')
        setMinOvValue('')
        setMinOvReason('')
    }, [])

    // Re-populate deliverables when user switches phase (CDs ↔ Design)
    const handleRepopulatePhase = useCallback((newPhase) => {
        setAutoPhase(newPhase)
        const ptKey = estimate.projectType
        if (!ptKey || !archCatalogItems?.length) return

        let items = archCatalogItems
            .filter(ci => ci.project_type === ptKey && ci.phase === newPhase)
            .map(ci => ({ id: ci.id, name: ci.name, discipline: ci.discipline, subcat: ci.subcat, phase: ci.phase }))

        // Merge enabled add-on items
        const seenIds = new Set(items.map(i => i.id))
        const currentAddons = estimate.addons || getDefaultEstimateAddons(addonDefs)
        for (const addon of currentAddons.filter(a => a.enabled)) {
            const def = addonDefs.find(d => d.key === addon.key)
            if (!def) continue
            const ids = getAddonScopeItemIds(def, archCatalogItems, ptKey, newPhase)
            for (const ci of archCatalogItems) {
                if (ids.has(ci.id) && !seenIds.has(ci.id)) {
                    seenIds.add(ci.id)
                    items.push({ id: ci.id, name: ci.name, discipline: ci.discipline, subcat: ci.subcat, phase: ci.phase })
                }
            }
        }
        items = filterByProjectTypeSuffix(items, ptKey)

        if (items.length > 0) {
            setUndoScopeSnapshot(estimate.scopeItems || [])
            clearTimeout(undoTimerRef.current)
            undoTimerRef.current = setTimeout(() => setUndoScopeSnapshot(null), 8000)
            setEstimate(prev => ({ ...prev, scopeItems: items }))
            setDisciplineSuggestion(deriveDisciplineSuggestion(items))
        }
    }, [estimate.projectType, estimate.scopeItems, estimate.addons, archCatalogItems, setEstimate, addonDefs])

    // ── Draft / auto-save ──
    const draftTimerRef  = useRef(null)
    const draftDataRef   = useRef(null)
    const draftCheckedRef = useRef(false) // guards Strict Mode double-invocation
    const isFirstRender  = useRef(true)
    const [isSaving,     setIsSaving]     = useState(false)
    const [savedAt,      setSavedAt]      = useState(null)
    const [draftPrompt,  setDraftPrompt]  = useState(null)
    const [isDbSaving,   setIsDbSaving]   = useState(false)
    const [dbSavedAt,    setDbSavedAt]    = useState(null)
    const markArchRefreshedRef = useRef(null)

    // Silent background refresh when tab becomes visible (guard while saving)
    const { markRefreshed: markArchRefreshed } = useVisibilityRefresh({
        staleMs: 300_000,
        onRefresh: useCallback(() => {
            if (isDbSaving || isSaving) return
            Promise.allSettled([silentReloadArchEstimates(), silentReloadCatalog(), silentReloadAddons()])
                .finally(() => markArchRefreshedRef.current?.())
        }, [isDbSaving, isSaving, silentReloadArchEstimates, silentReloadCatalog, silentReloadAddons]),
    })
    markArchRefreshedRef.current = markArchRefreshed

    // Address autocomplete
    const [addrSuggestions, setAddrSuggestions] = useState([])
    const [addrLoading,     setAddrLoading]     = useState(false)
    const addrSessionRef = useRef(newSessionToken())

    // Internal panel open/closed
    const [internalOpen, setInternalOpen] = usePersistentState(`est_arch_internal_${userId}`, false)
    const [previewEst,   setPreviewEst]   = useState(null)

    // UI backup for draft persistence (catalog filters, config editors)
    const [uiBackup, setUiBackup] = useState(null)

    // Catalog filter state — lifted to parent so it survives in IndexedDB draft
    const [catSearch,      setCatSearch]      = useState(uiBackup?.catalog?.search || '')
    const [catPhase,       setCatPhase]       = useState(uiBackup?.catalog?.phase || 'cds')
    const [catProjectType, setCatProjectType] = useState(uiBackup?.catalog?.projectType || '')
    const [catSearchOpen,  setCatSearchOpen]  = useState(uiBackup?.catalog?.searchOpen !== false)

    // Auto-populate suggestion states
    const [disciplineSuggestion, setDisciplineSuggestion] = useState(null)
    const [undoScopeSnapshot,    setUndoScopeSnapshot]    = useState(null)
    const [autoPhase,            setAutoPhase]            = useState('cds')
    const [civilManualOverride,  setCivilManualOverride]  = useState(false) // false=Main Only, true=Include Civil
    const undoTimerRef = useRef(null)
    useEffect(() => () => clearTimeout(undoTimerRef.current), [])

    // On mount: check for draft when userId is available
    useEffect(() => {
        if (!userId || draftCheckedRef.current) return
        draftCheckedRef.current = true

        const wasActive = sessionStorage.getItem('arch_estimator_active') === 'true'
        sessionStorage.setItem('arch_estimator_active', 'true')

        loadDraft(ARCH_DRAFT_TYPE, userId)
            .then(draft => {
                if (!draft || !draft.formData) return

                const cleanFormData = Object.fromEntries(Object.entries(draft.formData).filter(([, v]) => v !== undefined))
                // Ensure addons are merged with current definitions
                if (!cleanFormData.addons || !Array.isArray(cleanFormData.addons) || cleanFormData.addons.length === 0) {
                    cleanFormData.addons = getDefaultEstimateAddons(addonDefs)
                } else {
                    cleanFormData.addons = mergeEstimateAddons(cleanFormData.addons, addonDefs)
                }

                if (wasActive) {
                    // Same tab — navigate away/back, minimize, preview → silent restore
                    setEstimate({ ...EMPTY_ARCH_ESTIMATE(), ...cleanFormData })
                    if (draft.ui) setUiBackup(draft.ui)
                    setTab('constructor')
                    return
                }

                // Fresh tab → show prompt
                draftDataRef.current = cleanFormData
                setDraftPrompt({ savedAt: draft.savedAt })
                if (draft.ui) setUiBackup(draft.ui)
            })
            .catch(() => {})
    }, [userId, addonDefs]) // eslint-disable-line react-hooks/exhaustive-deps

    // Sync catalog UI changes into uiBackup so auto-save captures them
    useEffect(() => {
        setUiBackup(prev => ({
            ...prev,
            catalog: { search: catSearch, phase: catPhase, projectType: catProjectType, searchOpen: catSearchOpen }
        }))
    }, [catSearch, catPhase, catProjectType, catSearchOpen])

    // Auto-save on change (debounced 1.5s)
    useEffect(() => {
        if (isFirstRender.current) { isFirstRender.current = false; return }
        const hasData = estimate.client || estimate.address || estimate.sqft || estimate.existSqft || estimate.remSqft || estimate.addSqft
        if (!hasData) return
        clearTimeout(draftTimerRef.current)
        draftTimerRef.current = setTimeout(async () => {
            setIsSaving(true)
            try {
                await saveDraft(ARCH_DRAFT_TYPE, estimate, [], userId, uiBackup)
                setSavedAt(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }))
            } catch (e) {
                console.error('[ArchEstimateBuilder] auto-save failed:', e)
            } finally {
                setIsSaving(false)
            }
        }, 1500)
        return () => clearTimeout(draftTimerRef.current)
    }, [estimate, uiBackup]) // eslint-disable-line react-hooks/exhaustive-deps

    // Flush pending draft to IndexedDB before tab close / hide
    useEffect(() => {
        let flushing = false
        const flush = () => {
            if (flushing || !draftTimerRef.current) return
            flushing = true
            clearTimeout(draftTimerRef.current)
            draftTimerRef.current = null
            saveDraft(ARCH_DRAFT_TYPE, estimate, [], userId, uiBackup).catch(() => {})
        }
        window.addEventListener('beforeunload', flush)
        const onVis = () => { if (document.visibilityState === 'hidden') flush() }
        document.addEventListener('visibilitychange', onVis)
        return () => {
            window.removeEventListener('beforeunload', flush)
            document.removeEventListener('visibilitychange', onVis)
        }
    }, [estimate, uiBackup, userId])

    // ── Derived calculations ──────────────────────────────────────────────────
    const calc = useMemo(() => {
        const cfg  = getMarketConfig(estimate.market)

        // Detect state from address details or fall back to market field
        const addrState = (estimate.addressDetails?.state || '').toUpperCase()
        const detectedState = (addrState === 'TX' || addrState === 'TEXAS') ? 'TX'
            : (addrState === 'FL' || addrState === 'FLORIDA') ? 'FL'
            : estimate.market?.startsWith('TX') ? 'TX' : 'FL'

        const isAddAlt = ADD_ALT_PROJECT_TYPES.has(estimate.projectType)
        const isResType = ['res_add_alt', 'res_rem'].includes(estimate.projectType)
        const addAltMarket = `${detectedState}_${isResType ? 'RES' : 'COM'}`
        const addAltCfg  = getMarketConfig(addAltMarket)
        const fallbackRate = ADD_ALT_BASE_PRICES[addAltMarket] ?? ADD_ALT_BASE_PRICES.FL_RES
        const existBaseRate = (addAltCfg.add_alt_exist_base_price > 0) ? addAltCfg.add_alt_exist_base_price : fallbackRate
        const remBaseRate   = (addAltCfg.add_alt_rem_base_price   > 0) ? addAltCfg.add_alt_rem_base_price   : fallbackRate
        const addBaseRate   = (addAltCfg.add_alt_add_base_price   > 0) ? addAltCfg.add_alt_add_base_price   : fallbackRate

        let sqft, salePrice, existEff, remEff, addEff, computedAddAlt, activeMin
        let mainPrice = 0, civilPrice = 0, civilCfg = null
        let scopeMult = 1
        const useCivilSplit = !isAddAlt
            && NEW_CONSTRUCTION_TYPES.has(estimate.projectType)
            && civilManualOverride === true

        if (isAddAlt) {
            existEff       = round2(parseFloat(estimate.existSqft || 0) * parseFloat(estimate.existDiff || 0))
            remEff         = round2(parseFloat(estimate.remSqft   || 0) * parseFloat(estimate.remDiff   || 0))
            addEff         = round2(parseFloat(estimate.addSqft   || 0) * parseFloat(estimate.addDiff   || 0))
            sqft           = round2(existEff + remEff + addEff)
            computedAddAlt = round2(existEff * existBaseRate + remEff * remBaseRate + addEff * addBaseRate)
            const minFloors = getMinFloors(estimate.market)
            activeMin      = (parseFloat(estimate.existSqft || 0) > 0 ? minFloors.exist   : 0)
                           + (parseFloat(estimate.remSqft   || 0) > 0 ? minFloors.remodel : 0)
                           + (parseFloat(estimate.addSqft   || 0) > 0 ? minFloors.adicion : 0)
            const mpo = estimate.minPriceOverride
            if (mpo?.type === 'disable') {
                salePrice = round2(computedAddAlt)
            } else if (mpo?.type === 'custom' && mpo.value != null) {
                salePrice = round2(Math.max(computedAddAlt, Number(mpo.value)))
            } else {
                salePrice = round2(Math.max(computedAddAlt, activeMin))
            }
            scopeMult      = 1
        } else {
            existEff = 0; remEff = 0; addEff = 0; computedAddAlt = 0; activeMin = 0
            const rawSqft = estimate.useArea === 'lw'
                ? (parseFloat(estimate.lengthFt || 0) * parseFloat(estimate.widthFt || 0))
                : parseFloat(estimate.sqft || 0)
            sqft = rawSqft || 0

            if (useCivilSplit) {
                const civilMarket = getCivilMarket(estimate.market)
                civilCfg = getMarketConfig(civilMarket)
                const civilSqftVal = estimate.civilSqft != null ? Number(estimate.civilSqft) : sqft
                const mainScopeMult = (dbScopeMultipliers[estimate.market]?.[estimate.disciplineScope] ?? DISCIPLINE_SCOPE_MULTIPLIERS[estimate.market]?.[estimate.disciplineScope]) ?? 1
                const civilScopeMult = (dbScopeMultipliers[civilMarket]?.[estimate.disciplineScope] ?? DISCIPLINE_SCOPE_MULTIPLIERS[civilMarket]?.[estimate.disciplineScope]) ?? 1
                scopeMult = mainScopeMult
                mainPrice = round2(sqft * (cfg.base_price_sqft || 0) * (estimate.difficulty || 1) * mainScopeMult)
                civilPrice = round2(civilSqftVal * (civilCfg.base_price_sqft || 0) * ((estimate.civilDifficulty ?? estimate.difficulty) || 1) * civilScopeMult)
                if (estimate.projectType === 'civil') {
                    mainPrice = 0
                }
                salePrice = round2(mainPrice + civilPrice)
            } else {
                scopeMult = (dbScopeMultipliers[estimate.market]?.[estimate.disciplineScope] ?? DISCIPLINE_SCOPE_MULTIPLIERS[estimate.market]?.[estimate.disciplineScope]) ?? 1
                salePrice = round2(sqft * (cfg.base_price_sqft || 0) * (estimate.difficulty || 1) * scopeMult)
            }
        }

        const archAmt   = round2((useCivilSplit ? mainPrice : salePrice) * ((estimate.archPct   || 0) / 100))
        const structAmt = round2((useCivilSplit ? mainPrice : salePrice) * ((estimate.structPct || 0) / 100))
        const mechAmt   = round2((useCivilSplit ? mainPrice : salePrice) * ((estimate.mechPct   || 0) / 100))
        const elecAmt   = round2((useCivilSplit ? mainPrice : salePrice) * ((estimate.elecPct   || 0) / 100))
        const plumbAmt  = round2((useCivilSplit ? mainPrice : salePrice) * ((estimate.plumbPct  || 0) / 100))
        const mepAmt    = round2(mechAmt + elecAmt + plumbAmt)
        // Civil engineering is intentionally suppressed for Add/Alt and Remodel projects
        // (res_add_alt, res_rem, com_add_alt, com_rem). These estimates use a single
        // sale-price model and the discipline split UI hides the civil field, so leaving
        // civilPct active produces a phantom amount in the price summary. To re-enable
        // civil for these project types, remove the isAddAlt ternary below and ensure
        // the discipline_split panel renders the civil input for them.
        const effectiveCivilPct = isAddAlt ? 0 : (estimate.civilPct || 0)
        const effectiveCivilAdminPct = isAddAlt ? 0 : (estimate.civilAdminPct || 0)
        const civilAmt  = round2((useCivilSplit ? civilPrice : salePrice) * (effectiveCivilPct / 100))
        const adminAmt   = round2((useCivilSplit ? mainPrice : salePrice) * ((estimate.adminPct  || 0) / 100))
        const civilAdminAmt = useCivilSplit ? round2(civilPrice * (effectiveCivilAdminPct / 100)) : 0

        const addonsTotal  = (estimate.addons || [])
            .filter(a => a.enabled === true)
            .reduce((s, a) => s + Number(a.amount || 0), 0)
        const deductionsTotal = (estimate.disciplineDeductions || []).reduce((s, d) => s + Number(d.amount || 0), 0)
        const totalContract = round2((useCivilSplit ? mainPrice + civilPrice : salePrice) + addonsTotal - deductionsTotal)

        // Internal breakdown — use DB roles with per-project overrides
        const BUCKET_FIELD = {
            direct:      'direct_cost_pct',
            certifier:   'certifier_pct',
            commission:  'sales_pct',
            operating:   'operating_pct',
            franchise:   'franchise_pct',
            profit:      'profit_pct',
            contingency: 'contingency_pct',
        }
        const getOverrides = (type) => {
            if (type === 'direct')      return estimate.directCostOverrides || {}
            if (type === 'certifier')   return estimate.certCostOverrides || {}
            if (type === 'commission')  return estimate.commissionOverrides || {}
            if (type === 'operating')   return estimate.operatingCostOverrides || {}
            if (type === 'franchise')   return estimate.franchiseCostOverrides || {}
            if (type === 'profit')      return estimate.profitCostOverrides || {}
            if (type === 'contingency') return estimate.contingencyCostOverrides || {}
            return {}
        }
        const makeRoleCalc = (roles, overrides, bucketField) => {
            const bucketPct = cfg[bucketField] || 0
            return roles.map(r => {
                const override = overrides?.[r.roleKey]
                const amount = override != null
                    ? round2(Number(override))
                    : round2(totalContract * bucketPct * (r.pct / 100))
                return { ...r, amount, isOverridden: override != null }
            })
        }

        // Derive active disciplines from deliverables; empty = show all roles
        const activeDisciplines = new Set(
            (estimate.scopeItems || []).map(i => (i.discipline || '').toUpperCase()).filter(Boolean)
        )

        const groupedRoles = {}
        const groupTotals = {}
        for (const [type, roles] of Object.entries(rolesByType)) {
            // Filter by discipline — roles with ALL always pass through
            const visibleRoles = activeDisciplines.size === 0
                ? roles
                : roles.filter(r => !r.discipline || r.discipline === 'ALL' || activeDisciplines.has(r.discipline))
            // Re-normalize pcts of enabled visible roles so they sum to 100% within each group
            const enabledVisible = visibleRoles.filter(r => r.is_enabled !== false)
            const pctSum = enabledVisible.reduce((s, r) => s + r.pct, 0)
            const normalizedRoles = visibleRoles.map(r =>
                r.is_enabled !== false && pctSum > 0
                    ? { ...r, pct: (r.pct / pctSum) * 100 }
                    : r
            )
            const calc = makeRoleCalc(normalizedRoles, getOverrides(type), BUCKET_FIELD[type])
            groupedRoles[type] = calc
            groupTotals[type] = round2(calc.filter(r => r.is_enabled !== false).reduce((s, r) => s + r.amount, 0))
        }
        const COST_TYPES = ['direct', 'certifier', 'commission', 'operating', 'franchise']
        const totalRoleCosts = COST_TYPES.reduce((s, t) => s + (groupTotals[t] || 0), 0)
        const totalCosts = totalRoleCosts
        const netProfit = round2(totalContract - totalCosts)
        const profitPct = totalContract > 0 ? (netProfit / totalContract) * 100 : 0

        // Cuotas with amounts
        const cuotasCalc = (estimate.cuotas || []).map(c => ({
            ...c, amount: round2(totalContract * ((c.pct || 0) / 100))
        }))
        const cuotaSum = (estimate.cuotas || []).reduce((s, c) => s + (c.pct || 0), 0)

        return {
            sqft, salePrice, archAmt, structAmt, mechAmt, elecAmt, plumbAmt, mepAmt, civilAmt, adminAmt, civilAdminAmt,
            addonsTotal, deductionsTotal, totalContract,
            groupedRoles, groupTotals, totalCosts, netProfit, profitPct,
            cuotasCalc, cuotaSum,
            cfg,
            isAddAlt, detectedState, addAltMarket,
            existBaseRate, remBaseRate, addBaseRate,
            existEff, remEff, addEff, computedAddAlt, activeMin,
            isMinOverridden: !!estimate.minPriceOverride,
            mainPrice, civilPrice,
            civilCfg,
            useCivilSplit,
            scopeMult,
        }
    }, [ // eslint-disable-line react-hooks/exhaustive-deps
        estimate.market, estimate.useArea, estimate.sqft, estimate.lengthFt, estimate.widthFt,
        estimate.disciplineScope, estimate.difficulty, estimate.scopeItems,
        estimate.archPct, estimate.structPct, estimate.mechPct, estimate.elecPct, estimate.plumbPct, estimate.civilPct, estimate.adminPct, estimate.civilAdminPct,
        estimate.addons, estimate.cuotas,
        estimate.projectType, estimate.addressDetails,
        estimate.existSqft, estimate.existDiff,
        estimate.remSqft, estimate.remDiff,
        estimate.addSqft, estimate.addDiff,
        estimate.civilSqft, estimate.civilDifficulty,
        estimate.certCostOverrides, estimate.directCostOverrides, estimate.commissionOverrides,
        estimate.operatingCostOverrides, estimate.franchiseCostOverrides, estimate.profitCostOverrides, estimate.contingencyCostOverrides,
        estimate.minPriceOverride, estimate.disciplineDeductions,
        getMarketConfig,
        rolesByType,
        civilManualOverride,
    ])

    // Track civil split toggle for save/restore of civilPct
    const savedCivilPctRef = useRef(null)
    const prevUseCivilSplitRef = useRef(false)
    useEffect(() => {
        const wasSplit = prevUseCivilSplitRef.current
        const nowSplit = calc.useCivilSplit
        if (!wasSplit && nowSplit) {
            // Entering split mode — save old civilPct and auto-init civilAdminPct
            savedCivilPctRef.current = estimate.civilPct
            const civilVal = estimate.civilPct || 0
            setEstimate(prev => ({ ...prev, civilPct: civilVal || 100, civilAdminPct: 100 - (civilVal || 100) }))
        } else if (wasSplit && !nowSplit) {
            // Exiting split mode — restore old civilPct and clear civilAdmin
            const restored = savedCivilPctRef.current ?? 0
            setEstimate(prev => ({ ...prev, civilPct: restored, civilAdminPct: 0 }))
        }
        prevUseCivilSplitRef.current = nowSplit
    }, [calc.useCivilSplit]) // eslint-disable-line react-hooks/exhaustive-deps

    // Add/Alt/Rem projects never use civil. Save civilPct when entering and restore when exiting.
    const savedCivilPctForAddAltRef = useRef(null)
    const prevIsAddAltRef = useRef(false)
    useEffect(() => {
        const wasAddAlt = prevIsAddAltRef.current
        const nowAddAlt = calc.isAddAlt
        if (!wasAddAlt && nowAddAlt) {
            // Entering Add/Alt/Rem — stash old civilPct and clear it
            savedCivilPctForAddAltRef.current = estimate.civilPct
            setEstimate(prev => ({ ...prev, civilPct: 0, civilAdminPct: 0 }))
        } else if (wasAddAlt && !nowAddAlt) {
            // Exiting Add/Alt/Rem — restore stashed civilPct
            const restored = savedCivilPctForAddAltRef.current ?? 0
            setEstimate(prev => ({ ...prev, civilPct: restored, civilAdminPct: 0 }))
        }
        prevIsAddAltRef.current = nowAddAlt
    }, [calc.isAddAlt]) // eslint-disable-line react-hooks/exhaustive-deps

    // Reset civilManualOverride when projectType changes
    const prevProjectTypeRef = useRef(estimate.projectType)
    useEffect(() => {
        if (prevProjectTypeRef.current !== estimate.projectType) {
            const isCivil = estimate.projectType === 'civil'
            setCivilManualOverride(isCivil)
            if (isCivil) {
                setEstimate(prev => ({
                    ...prev,
                    disciplineScope: 'solo_civil',
                    archPct: 0, structPct: 0, mechPct: 0, elecPct: 0, plumbPct: 0, adminPct: 0,
                    civilPct: 80, civilAdminPct: 20, mepPct: 0,
                }))
            }
            prevProjectTypeRef.current = estimate.projectType
        }
    }, [estimate.projectType])

    // Redistribute percentages when civilManualOverride changes
    const savedCivilPctOverrideRef = useRef(null)
    const prevOverrideRef = useRef(false)
    useEffect(() => {
        const prev = prevOverrideRef.current
        const now = civilManualOverride
        if (prev === now) return
        const isAddAlt = ADD_ALT_PROJECT_TYPES.has(estimate.projectType)
        const isNewConstr = NEW_CONSTRUCTION_TYPES.has(estimate.projectType)
        if (isAddAlt || !isNewConstr) {
            prevOverrideRef.current = now
            return
        }
        if (estimate.projectType === 'civil') {
            prevOverrideRef.current = now
            return
        }
        if (now === false && prev === true) {
            // Excluding civil — remember the civil engineering share, then fold the
            // building pool back into a single 100% pool applied to salePrice.
            const savedCivil = estimate.civilPct || 0
            if (savedCivil > 0) savedCivilPctOverrideRef.current = savedCivil
            setEstimate(prevEst => {
                const next = scalePctsTo(prevEst, MAIN_PCT_FIELDS, 100)
                next.civilPct = 0
                next.civilAdminPct = 0
                return { ...prevEst, ...withMepRollup(next) }
            })
        } else if (now === true && prev === false) {
            // Including civil — the two pools become independent, each applied to its
            // own base (building → mainPrice, civil → civilPrice). Both must total
            // 100% on their own; scaling the building pool down by the civil share
            // would leave that remainder billed but never itemized.
            const restored = savedCivilPctOverrideRef.current ?? DEFAULT_CIVIL_ENGINEERING_PCT
            savedCivilPctOverrideRef.current = null
            setEstimate(prevEst => {
                const next = scalePctsTo(prevEst, MAIN_PCT_FIELDS, 100)
                next.civilPct = restored
                next.civilAdminPct = 100 - restored
                return { ...prevEst, ...withMepRollup(next) }
            })
        }
        prevOverrideRef.current = now
    }, [civilManualOverride]) // eslint-disable-line react-hooks/exhaustive-deps

    // ── Handlers ─────────────────────────────────────────────────────────────
    const update = useCallback((field, value) => {
        setEstimate(prev => ({ ...prev, [field]: value }))
    }, [])

    const setDisciplinePct = useCallback((field, value) => {
        setEstimate(prev => {
            const newVal = Math.max(0, Math.min(100, Math.round(value || 0)))
            const isAddAlt = ADD_ALT_PROJECT_TYPES.has(prev.projectType)
            const isCivilSplit = !isAddAlt &&
                NEW_CONSTRUCTION_TYPES.has(prev.projectType) &&
                civilManualOverride === true
            // Civil bucket fields don't participate in main discipline redistribution
            if (isCivilSplit && (field === 'civilPct' || field === 'civilAdminPct')) {
                return { ...prev, [field]: newVal }
            }
            // Add/Alt/Rem projects never include civil in the discipline split, so exclude it
            // from redistribution to prevent a hidden phantom percentage from accumulating value.
            const fields = (isCivilSplit || isAddAlt)
                ? ['archPct', 'structPct', 'mechPct', 'elecPct', 'plumbPct', 'adminPct']
                : ['archPct', 'structPct', 'mechPct', 'elecPct', 'plumbPct', 'civilPct', 'adminPct']
            const others = fields.filter(f => f !== field)
            const othersCount = others.length

            // If user sets 100, everything else goes to 0
            if (newVal === 100) {
                const update = {}
                fields.forEach(f => { update[f] = f === field ? 100 : 0 })
                update.mepPct = (update.mechPct || 0) + (update.elecPct || 0) + (update.plumbPct || 0)
                return { ...prev, ...update }
            }

            // If user sets 0, do not redistribute — the discipline is removed from scope
            if (newVal === 0) {
                const update = { [field]: 0 }
                update.mepPct = (prev.mechPct || 0) + (prev.elecPct || 0) + (prev.plumbPct || 0)
                return { ...prev, ...update }
            }

            const remaining = 100 - newVal
            const currentOthers = others.map(f => prev[f] || 0)
            const sumOthers = currentOthers.reduce((a, b) => a + b, 0)

            const newValues = {}
            newValues[field] = newVal

            if (sumOthers > 0) {
                // Proportional redistribution
                const raw = currentOthers.map(v => (v / sumOthers) * remaining)
                let rounded = raw.map(v => Math.round(v))

                // Fix rounding drift so total is exactly 100
                const total = rounded.reduce((a, b) => a + b, 0) + newVal
                let drift = 100 - total
                while (drift !== 0) {
                    const candidates = raw
                        .map((v, i) => ({ idx: i, frac: Math.abs(v - rounded[i]), val: rounded[i] }))
                        .sort((a, b) => b.frac - a.frac)
                    const best = candidates.find(x => drift > 0 ? x.val < 100 : x.val > 0)
                    if (!best) break
                    rounded[best.idx] += Math.sign(drift)
                    drift -= Math.sign(drift)
                }

                others.forEach((f, i) => { newValues[f] = rounded[i] })
            } else {
                // All others are 0 — split remaining equally
                const base = Math.floor(remaining / othersCount)
                const extra = remaining % othersCount
                others.forEach((f, i) => { newValues[f] = base + (i < extra ? 1 : 0) })
            }

            newValues.mepPct = (newValues.mechPct || 0) + (newValues.elecPct || 0) + (newValues.plumbPct || 0)
            return { ...prev, ...newValues }
        })
    }, [civilManualOverride])

    // Bring every discipline pool back to exactly 100% by scaling its members
    // proportionally. This preserves the estimator's relative weighting instead of
    // imposing a preset, and only touches the pool that is actually off.
    const balanceDisciplinePcts = useCallback(() => {
        setEstimate(prev => {
            const isAddAlt = ADD_ALT_PROJECT_TYPES.has(prev.projectType)
            const isCivilSplit = !isAddAlt &&
                NEW_CONSTRUCTION_TYPES.has(prev.projectType) &&
                civilManualOverride === true

            if (!isCivilSplit) {
                // Single pool applied to salePrice. Civil rides along with the rest
                // unless this is an Add/Alt/Rem, where civil is out of scope entirely.
                const fields = isAddAlt
                    ? MAIN_PCT_FIELDS
                    : [...MAIN_PCT_FIELDS, 'civilPct']
                if (sumPcts(prev, fields) <= 0) return prev
                if (Math.abs(sumPcts(prev, fields) - 100) < PCT_EPSILON) return prev
                return { ...prev, ...withMepRollup(scalePctsTo(prev, fields, 100)) }
            }

            // Two independent pools — balance each one on its own.
            const next = {}
            const mainSum = sumPcts(prev, MAIN_PCT_FIELDS)
            if (mainSum > 0 && Math.abs(mainSum - 100) >= PCT_EPSILON) {
                Object.assign(next, scalePctsTo(prev, MAIN_PCT_FIELDS, 100))
            }
            const civilSum = sumPcts(prev, CIVIL_PCT_FIELDS)
            if (Math.abs(civilSum - 100) >= PCT_EPSILON) {
                Object.assign(next, civilSum > 0
                    ? scalePctsTo(prev, CIVIL_PCT_FIELDS, 100)
                    : { civilPct: DEFAULT_CIVIL_ENGINEERING_PCT, civilAdminPct: 100 - DEFAULT_CIVIL_ENGINEERING_PCT })
            }
            if (Object.keys(next).length === 0) return prev
            return { ...prev, ...withMepRollup({ ...prev, ...next }) }
        })
    }, [civilManualOverride])

    const handleResumeDraft = () => {
        if (draftDataRef.current) setEstimate({ ...EMPTY_ARCH_ESTIMATE(), ...draftDataRef.current })
        setDraftPrompt(null)
        draftDataRef.current = null
        setTab('constructor')
    }
    const handleDiscardDraft = () => {
        deleteDraft(ARCH_DRAFT_TYPE, userId).catch(() => {})
        setDraftPrompt(null)
        draftDataRef.current = null
        setUiBackup(null)
    }

    const handleSaveToDb = useCallback(async () => {
        if (isDbSaving) return
        if (!estimate.client && !estimate.sqft) {
            alert(t('admin.arch.save_guard'))
            return
        }
        setIsDbSaving(true)
        try {
            const toSave = {
                ...estimate,
                basePrice:     calc.salePrice,
                salePrice:     calc.salePrice,
                totalContract: calc.totalContract,
            }
            const saved = await saveArchEstimate(toSave, userId)
            // Sync full saved state back so frontend matches DB row (especially cuotas)
            setEstimate(prev => ({
                ...prev,
                id: saved.id,
                number: saved.number || prev.number,
                client: saved.client || prev.client,
                cuotas: (Array.isArray(saved.cuotas) && saved.cuotas.length > 0) ? saved.cuotas : DEFAULT_CUOTAS.map(c => ({ ...c })),
                addons: Array.isArray(saved.addons) ? mergeEstimateAddons(saved.addons, addonDefs) : getDefaultEstimateAddons(addonDefs),
                scopeItems: Array.isArray(saved.scope_items) ? saved.scope_items : prev.scopeItems,
                terms: Array.isArray(saved.terms) ? saved.terms : prev.terms,
                serviceGroups: Array.isArray(saved.service_groups) ? saved.service_groups : prev.serviceGroups,
                notes: saved.notes || prev.notes,
                additionalComments: saved.additional_comments || prev.additionalComments,
                status: saved.status || prev.status,
                projectType: saved.project_type || prev.projectType,
                market: saved.market || prev.market,
                difficulty: saved.difficulty || prev.difficulty,
                disciplineScope: saved.discipline_scope || prev.disciplineScope,
                address: saved.address || prev.address,
                addressDetails: saved.address_details || prev.addressDetails,
                sqft: saved.sqft != null ? String(saved.sqft) : prev.sqft,
                existSqft: saved.exist_sqft ?? prev.existSqft,
                existDiff: saved.exist_diff ?? prev.existDiff,
                remSqft: saved.rem_sqft ?? prev.remSqft,
                remDiff: saved.rem_diff ?? prev.remDiff,
                addSqft: saved.add_sqft ?? prev.addSqft,
                addDiff: saved.add_diff ?? prev.addDiff,
                timeFrameValue: saved.time_frame_value != null ? String(saved.time_frame_value) : prev.timeFrameValue,
                timeFrameUnit: saved.time_frame_unit || prev.timeFrameUnit,
            }))
            setDbSavedAt(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }))
            deleteDraft(ARCH_DRAFT_TYPE, userId).catch(() => {})
            reloadArchEstimates()
        } catch (e) {
            console.error('[ArchEstimateBuilder] db-save failed:', e)
            alert(t('admin.estimator.save_failed', { msg: e.message }))
        } finally {
            setIsDbSaving(false)
        }
    }, [estimate, calc, isDbSaving, userId, t])

    const handleResumeFromList = useCallback((dbEst) => {
        const scopeItems = Array.isArray(dbEst.scope_items) ? dbEst.scope_items : []
        const loadedMep = dbEst.mep_pct ?? 20
        const [fallbackMech, fallbackElec, fallbackPlumb] = splitMepPct(loadedMep)
        const isAddAlt = ADD_ALT_PROJECT_TYPES.has(dbEst.project_type || '')
        prevProjectTypeRef.current = dbEst.project_type || ''
        prevIsAddAltRef.current = isAddAlt
        setEstimate({
            id:              dbEst.id,
            number:          dbEst.number,
            date:            dbEst.date || '',
            client:             dbEst.client            || '',
            phone:              dbEst.phone             || '',
            email:              dbEst.email             || '',
            mondayItemId:       dbEst.monday_item_id    || '',
            projectType:        dbEst.project_type      || '',
            projectDescription: dbEst.project_description || '',
            address:            dbEst.address           || '',
            addressDetails:  dbEst.address_details || {},
            market:          dbEst.market || 'FL_RES',
            sqft:            dbEst.sqft != null ? String(dbEst.sqft) : '',
            lengthFt:        dbEst.length_ft != null ? String(dbEst.length_ft) : '',
            widthFt:         dbEst.width_ft != null ? String(dbEst.width_ft) : '',
            useArea:         dbEst.use_area  || 'direct',
            existSqft:       dbEst.exist_sqft ?? 0,
            existDiff:       dbEst.exist_diff ?? 0.125,
            remSqft:         dbEst.rem_sqft   ?? 0,
            remDiff:         dbEst.rem_diff   ?? 1,
            addSqft:         dbEst.add_sqft   ?? 0,
            addDiff:         dbEst.add_diff   ?? 1,
            difficulty:      dbEst.difficulty || 1,
            civilSqft:       dbEst.civil_sqft       ?? null,
            civilDifficulty: dbEst.civil_difficulty ?? null,
            disciplineScope: (dbEst.project_type === 'civil'
                ? (dbEst.discipline_scope || 'solo_civil')
                : (dbEst.discipline_scope || 'full')),
            archPct:         dbEst.arch_pct   ?? 50,
            structPct:       dbEst.struct_pct ?? 22,
            mepPct:          loadedMep,
            mechPct:         dbEst.mech_pct   ?? fallbackMech,
            elecPct:         dbEst.elec_pct   ?? fallbackElec,
            plumbPct:        dbEst.plumb_pct  ?? fallbackPlumb,
            // Civil engineering is never part of Add/Alt/Rem discipline split.
            civilPct:        isAddAlt ? 0 : (dbEst.civil_pct ?? 0),
            adminPct:        dbEst.admin_pct  ?? 8,
            civilAdminPct:   isAddAlt ? 0 : (dbEst.civil_admin_pct ?? 0),
            addons:          Array.isArray(dbEst.addons)  ? mergeEstimateAddons(dbEst.addons, addonDefs)  : getDefaultEstimateAddons(addonDefs),
            cuotas:          (Array.isArray(dbEst.cuotas) && dbEst.cuotas.length > 0) ? dbEst.cuotas : getStandardCuotas(t, schedulePresets),
            scopeItems:      scopeItems,
            notes:           dbEst.notes || '',
            additionalComments: dbEst.additional_comments || '',
            timeFrameValue:  dbEst.time_frame_value != null ? String(dbEst.time_frame_value) : '',
            timeFrameUnit:   dbEst.time_frame_unit || 'weeks',
            status:          dbEst.status || 'draft',
            serviceGroups:   Array.isArray(dbEst.service_groups) ? dbEst.service_groups : [],
            terms:           Array.isArray(dbEst.terms) ? dbEst.terms : [],
            certCostOverrides:         dbEst.cert_cost_overrides         || {},
            directCostOverrides:       dbEst.direct_cost_overrides       || {},
            commissionOverrides:       dbEst.commission_overrides        || {},
            operatingCostOverrides:    dbEst.operating_cost_overrides    || {},
            franchiseCostOverrides:    dbEst.franchise_cost_overrides    || {},
            profitCostOverrides:       dbEst.profit_cost_overrides       || {},
            contingencyCostOverrides:  dbEst.contingency_cost_overrides  || {},
            minPriceOverride:          dbEst.min_price_override          || null,
            disciplineDeductions:    Array.isArray(dbEst.discipline_deductions) ? dbEst.discipline_deductions : [],
        })
        // A saved estimate already carries the percentages the estimator decided on —
        // re-suggesting a preset on every load is noise. The suggestion is only raised
        // when starting fresh (project type auto-populate) or switching client (Monday).
        setDisciplineSuggestion(null)
        const willUseCivil = (!isAddAlt && hasCivilDeliverables(scopeItems)) || (dbEst.project_type === 'civil')
        prevOverrideRef.current = willUseCivil
        prevUseCivilSplitRef.current = willUseCivil
        setCivilManualOverride(willUseCivil)
        setDbSavedAt(
            dbEst.updated_at
                ? new Date(dbEst.updated_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                : null
        )
        setSavedAt(null)
        deleteDraft(ARCH_DRAFT_TYPE, userId).catch(() => {})
        setTab('constructor')
    }, [userId, addonDefs, t, schedulePresets])

    const handleReset = () => {
        if (window.confirm(t('admin.estimator.reset_confirm'))) {
            deleteDraft(ARCH_DRAFT_TYPE, userId).catch(() => {})
            const base = EMPTY_ARCH_ESTIMATE()
            base.cuotas = getStandardCuotas(t, schedulePresets)
            base.addons = getDefaultEstimateAddons(addonDefs)
            base.terms  = getDefaultTermsFromCatalog(archTermsConditions, defaultSourceFiles, base.market)
            setEstimate(base)
            setSavedAt(null)
            setDbSavedAt(null)
            setIsSaving(false)
            setUiBackup(null)
            setTab('constructor')
        }
    }

    // Address autocomplete
    const handleAddressInput = async (val) => {
        update('address', val)
        if (val.length < 3) { setAddrSuggestions([]); return }
        setAddrLoading(true)
        try {
            const s = await fetchAddressSuggestions(val, addrSessionRef.current)
            setAddrSuggestions(s || [])
        } catch { setAddrSuggestions([]) }
        finally { setAddrLoading(false) }
    }
    const handleSelectAddress = async (suggestion) => {
        const details = await getPlaceDetails(suggestion.place_id, addrSessionRef.current)
        addrSessionRef.current = newSessionToken()
        setAddrSuggestions([])
        setEstimate(prev => ({
            ...prev,
            address:        details?.formatted_address || suggestion.description,
            addressDetails: details || {},
        }))
    }

    // Normalize array of percentages to sum exactly 100 (with drift fix)
    const normalizeTo100 = (values) => {
        const sum = values.reduce((a, b) => a + b, 0)
        if (sum === 0 || Math.abs(sum - 100) < 0.01) return values.map(v => Math.round(v))
        const raw = values.map(v => (v / sum) * 100)
        let rounded = raw.map(v => Math.round(v))
        const total = rounded.reduce((a, b) => a + b, 0)
        let drift = 100 - total
        const candidates = raw.map((v, i) => ({ idx: i, frac: Math.abs(v - rounded[i]), val: rounded[i] })).sort((a, b) => b.frac - a.frac)
        while (drift !== 0) {
            const best = candidates.find(x => drift > 0 ? x.val < 100 : x.val > 0)
            if (!best) break
            rounded[best.idx] += Math.sign(drift)
            drift -= Math.sign(drift)
        }
        return rounded
    }

    const splitMepPct = (mep) => {
        const m = Math.max(0, Number(mep) || 0)
        const mech = Math.round(m * 0.35)
        const elec = Math.round(m * 0.35)
        const plumb = m - mech - elec  // exact remainder so total always equals m
        return [mech, elec, plumb]
    }

    // Discipline preset
    const applyPreset = (preset) => {
        setEstimate(prev => {
            const isAddAlt = ADD_ALT_PROJECT_TYPES.has(prev.projectType)
            const isCivilSplit = !isAddAlt &&
                NEW_CONSTRUCTION_TYPES.has(prev.projectType) &&
                civilManualOverride === true
            const [mech, elec, plumb] = splitMepPct(preset.mepPct ?? 0)
            if (isCivilSplit) {
                if (prev.projectType === 'civil') {
                    return {
                        ...prev,
                        archPct: 0, structPct: 0, mechPct: 0, elecPct: 0, plumbPct: 0, adminPct: 0,
                        civilPct: 80, civilAdminPct: 20, mepPct: 0,
                    }
                }
                const mainValues = normalizeTo100([preset.archPct ?? 0, preset.structPct ?? 0, mech, elec, plumb, preset.adminPct ?? 0])
                return {
                    ...prev,
                    archPct: mainValues[0],
                    structPct: mainValues[1],
                    mechPct: mainValues[2],
                    elecPct: mainValues[3],
                    plumbPct: mainValues[4],
                    adminPct: mainValues[5],
                    civilPct: 80,
                    civilAdminPct: 20,
                    mepPct: mainValues[2] + mainValues[3] + mainValues[4],
                }
            }
            return {
                ...prev,
                archPct: preset.archPct ?? 0,
                structPct: preset.structPct ?? 0,
                mechPct: mech,
                elecPct: elec,
                plumbPct: plumb,
                // Civil is never part of Add/Alt/Rem discipline split.
                civilPct: isAddAlt ? 0 : (preset.civilPct ?? 0),
                adminPct: preset.adminPct ?? 0,
                mepPct: mech + elec + plumb,
            }
        })
    }

    // Scope change — update discipline scope and pct splits (catalog view filters separately)
    const handleScopeChange = useCallback((scopeKey, pct) => {
        setDisciplineSuggestion(null)
        setEstimate(prev => {
            const isAddAlt = ADD_ALT_PROJECT_TYPES.has(prev.projectType)
            const isCivilSplit = !isAddAlt &&
                NEW_CONSTRUCTION_TYPES.has(prev.projectType) &&
                civilManualOverride === true
            const [mech, elec, plumb] = splitMepPct(pct.mepPct ?? 0)
            if (isCivilSplit) {
                if (prev.projectType === 'civil') {
                    return {
                        ...prev,
                        disciplineScope: scopeKey,
                        archPct: 0, structPct: 0, mechPct: 0, elecPct: 0, plumbPct: 0, adminPct: 0,
                        civilPct: 80, civilAdminPct: 20, mepPct: 0,
                    }
                }
                const mainValues = normalizeTo100([pct.archPct ?? 0, pct.structPct ?? 0, mech, elec, plumb, pct.adminPct ?? 0])
                return {
                    ...prev,
                    disciplineScope: scopeKey,
                    archPct: mainValues[0],
                    structPct: mainValues[1],
                    mechPct: mainValues[2],
                    elecPct: mainValues[3],
                    plumbPct: mainValues[4],
                    adminPct: mainValues[5],
                    civilPct: 80,
                    civilAdminPct: 20,
                    mepPct: mainValues[2] + mainValues[3] + mainValues[4],
                }
            }
            return {
                ...prev,
                disciplineScope: scopeKey,
                archPct: pct.archPct,
                structPct: pct.structPct,
                mechPct: mech,
                elecPct: elec,
                plumbPct: plumb,
                // Civil is never part of Add/Alt/Rem discipline split.
                civilPct: isAddAlt ? 0 : pct.civilPct,
                adminPct: pct.adminPct,
                mepPct: mech + elec + plumb,
            }
        })
    }, [civilManualOverride])

    // Addon toggle + amount — syncs scopeItems
    const toggleAddon = useCallback((key) => {
        setEstimate(prev => {
            const addon = prev.addons.find(a => a.key === key)
            const wasEnabled = addon?.enabled || false
            const nextAddons = prev.addons.map(a => a.key === key ? { ...a, enabled: !a.enabled } : a)
            const isNowEnabled = !wasEnabled

            let nextScopeItems = prev.scopeItems || []
            const ptKey = prev.projectType
            const phase = autoPhase || 'cds'
            const def = addonDefs.find(d => d.key === key)
            if (!def) return { ...prev, addons: nextAddons }
            const idsToToggle = getAddonScopeItemIds(def, archCatalogItems, ptKey, phase)

            if (isNowEnabled) {
                const existingIds = new Set(nextScopeItems.map(i => i.id))
                const itemsToAdd = archCatalogItems
                    .filter(ci => idsToToggle.has(ci.id) && !existingIds.has(ci.id))
                    .map(ci => ({ id: ci.id, name: ci.name, discipline: ci.discipline, subcat: ci.subcat, phase: ci.phase }))
                nextScopeItems = [...nextScopeItems, ...itemsToAdd]
            } else {
                nextScopeItems = nextScopeItems.filter(si => !idsToToggle.has(si.id))
            }

            return { ...prev, addons: nextAddons, scopeItems: nextScopeItems }
        })
    }, [archCatalogItems, autoPhase, addonDefs])
    const setAddonAmount = (key, val) => {
        setEstimate(prev => ({
            ...prev,
            addons: prev.addons.map(a => a.key === key ? { ...a, amount: val } : a)
        }))
    }
    const moveAddon = useCallback((fromIndex, toIndex) => {
        setEstimate(prev => {
            const nextAddons = arrayMove(prev.addons || [], fromIndex, toIndex)
            // Persist new order to DB so it survives page reloads
            for (let i = 0; i < nextAddons.length; i++) {
                const def = addonDefs.find(d => d.key === nextAddons[i].key)
                if (def && (def.sort_order ?? 0) !== i) {
                    updateArchAddon(def.id, { sort_order: i }).catch(() => {})
                }
            }
            return { ...prev, addons: nextAddons }
        })
    }, [addonDefs, updateArchAddon])

    // Cuotas — single handler replaces per-field setters
    const setCuotas = (rows) => setEstimate(prev => ({ ...prev, cuotas: rows }))

    const scopeIncludesCivil = (estimate.disciplineScope || 'full') === 'full' || (estimate.disciplineScope || '').includes('civil')
    // Civil is hidden from the Add/Alt/Rem discipline split UI, so it must not be
    // counted toward the visible percentage total or validation.
    const hideCivilForProject = calc.isAddAlt
    const mainPctSum = (estimate.archPct || 0) + (estimate.structPct || 0) + (estimate.mechPct || 0) + (estimate.elecPct || 0) + (estimate.plumbPct || 0) + (estimate.adminPct || 0)
    const civilPctSumLocal = (estimate.civilPct || 0) + (estimate.civilAdminPct || 0)
    const disciplinePctSum = calc.useCivilSplit
        ? mainPctSum + (scopeIncludesCivil ? civilPctSumLocal : 0)
        : mainPctSum + (hideCivilForProject ? 0 : (estimate.civilPct || 0))

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-cloud">

            {/* Preview modal */}
            <AnimatePresence>
                {previewEst && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="flex overflow-hidden fixed inset-0 z-50 flex-col bg-black/60"
                    >
                        <div className="flex items-center justify-between px-4 py-2.5 bg-white border-b border-gray-200 shrink-0">
                            <div className="flex gap-3 items-center">
                                <p className="text-sm font-bold text-gray-800">
                                    {t('admin.arch.preview_title')} — {previewEst.client || previewEst.number}
                                </p>
                                <div className="flex gap-1">
                                    <button
                                        onClick={() => setPreviewEst(p => ({ ...p, _previewMode: 'formal' }))}
                                        className={`px-2.5 py-1 text-[11px] font-bold rounded-lg transition-all ${
                                            (previewEst._previewMode || 'formal') === 'formal'
                                                ? 'bg-gray-800 text-white'
                                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                        }`}
                                    >{t('admin.arch.preview_formal')}</button>
                                    <button
                                        onClick={() => setPreviewEst(p => ({ ...p, _previewMode: 'preliminary' }))}
                                        className={`px-2.5 py-1 text-[11px] font-bold rounded-lg transition-all ${
                                            previewEst._previewMode === 'preliminary'
                                                ? 'bg-amber-500 text-white'
                                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                        }`}
                                    >{t('admin.arch.preview_preliminary')}</button>
                                </div>
                            </div>
                            <button onClick={() => setPreviewEst(null)} className="p-2 text-gray-400 rounded-lg transition-all hover:text-gray-700 hover:bg-gray-100">
                                <FaTimes size={14} />
                            </button>
                        </div>
                        <div className="overflow-y-auto flex-1 p-4 bg-gray-100">
                            <ArchEstimatePreview estimate={previewEst} mode={previewEst._previewMode || 'formal'} difficultyLevels={marketOptions.find(m => m.key === previewEst.market)?.type === 'CIVIL' ? civilLevels : difficultyLevels} paymentInfo={getPaymentInfos(getOfficeState(previewEst))} session={session} addonDefs={addonDefs} />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Draft recovery modal */}
            <AnimatePresence>
                {draftPrompt && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="flex fixed inset-0 z-50 justify-center items-center px-4 bg-black/40"
                    >
                        <motion.div
                            initial={{ scale: 0.92, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 16 }}
                            className="p-6 w-full max-w-sm bg-white rounded-2xl shadow-2xl"
                        >
                            <div className="flex gap-3 items-center mb-3">
                                <div className="p-2.5 rounded-xl bg-violet-100">
                                    <FaHistory className="text-violet-600" size={18} />
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-gray-800">{t('admin.estimator.draft_title')}</p>
                                    <p className="text-xs text-gray-400">
                                        {new Date(draftPrompt.savedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                            </div>
                            <p className="mb-5 text-sm text-gray-500">{t('admin.estimator.draft_body')}</p>
                            <div className="flex gap-2">
                                <button onClick={handleResumeDraft} className="flex-1 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-bold hover:bg-violet-700 transition-all">
                                    {t('admin.estimator.draft_resume')}
                                </button>
                                <button onClick={handleDiscardDraft} className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-600 text-sm font-bold hover:bg-gray-200 transition-all">
                                    {t('admin.estimator.draft_fresh')}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Min-price-override modal */}
            <AnimatePresence>
                {minOvOpen && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="flex fixed inset-0 z-50 justify-center items-center px-4 bg-black/40"
                    >
                        <motion.div
                            initial={{ scale: 0.93, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.93, y: 12 }}
                            className="p-6 w-full max-w-sm bg-white rounded-2xl shadow-2xl"
                        >
                            <h3 className="mb-4 font-bold text-gray-800">{t('admin.arch.override_min_title')}</h3>
                            <div className="mb-4 space-y-3">
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setMinOvType('disable')}
                                        className={`flex-1 py-2 rounded-xl text-xs font-bold border transition-all ${minOvType === 'disable' ? 'bg-primary text-white border-primary' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`}
                                    >
                                        {t('admin.arch.override_min_disable')}
                                    </button>
                                    <button
                                        onClick={() => setMinOvType('custom')}
                                        className={`flex-1 py-2 rounded-xl text-xs font-bold border transition-all ${minOvType === 'custom' ? 'bg-primary text-white border-primary' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`}
                                    >
                                        {t('admin.arch.override_min_custom')}
                                    </button>
                                </div>
                                {minOvType === 'custom' && (
                                    <div>
                                        <label className="block mb-1 text-xs font-medium text-gray-500">{t('admin.arch.override_min_value_label')}</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 text-sm text-gray-400 -translate-y-1/2">$</span>
                                            <input
                                                type="number"
                                                step="1"
                                                value={minOvValue}
                                                onChange={e => setMinOvValue(e.target.value)}
                                                placeholder={String(calc.activeMin || 0)}
                                                className="px-3 py-2 pl-7 w-full text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
                                            />
                                        </div>
                                    </div>
                                )}
                                <div>
                                    <label className="block mb-1 text-xs font-medium text-gray-500">{t('admin.arch.override_min_reason_label')}</label>
                                    <textarea
                                        value={minOvReason}
                                        onChange={e => setMinOvReason(e.target.value)}
                                        placeholder={t('admin.arch.override_min_reason_placeholder')}
                                        rows={3}
                                        className="px-3 py-2 w-full text-sm rounded-xl border border-gray-200 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => {
                                        const userEmail = session?.user?.email || 'unknown'
                                        setEstimate(prev => ({
                                            ...prev,
                                            minPriceOverride: {
                                                type: minOvType,
                                                value: minOvType === 'custom' ? Number(minOvValue) : null,
                                                reason: minOvReason.trim(),
                                                appliedBy: userEmail,
                                                appliedAt: new Date().toISOString(),
                                            }
                                        }))
                                        setMinOvOpen(false)
                                    }}
                                    disabled={!minOvReason.trim() || (minOvType === 'custom' && (minOvValue === '' || Number(minOvValue) <= 0))}
                                    className="flex-1 py-2.5 rounded-xl bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-all disabled:opacity-50"
                                >
                                    {t('admin.arch.override_min_confirm')}
                                </button>
                                <button
                                    onClick={() => { setMinOvOpen(false); setMinOvReason(''); setMinOvValue('') }}
                                    className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-600 text-sm font-bold hover:bg-gray-200 transition-all"
                                >
                                    {t('admin.arch.override_min_cancel')}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Header */}
            <header className="sticky top-20 z-40 bg-white border-b border-gray-200 shadow-sm">
                <div className="flex gap-3 items-center px-4 py-3 mx-auto max-w-7xl">
                    <button onClick={onReturn} className="p-2 rounded-lg border border-gray-200 transition-all hover:bg-gray-100 text-ash shrink-0" title={t('admin.estimator.back')}>
                        <FaArrowLeft />
                    </button>
                    <h1 className="flex flex-1 gap-2 items-center min-w-0 text-xl font-bold text-primary">
                        <FaBuilding size={17} className="shrink-0" />
                        <span className="truncate">{t('admin.arch.title')}</span>
                    </h1>
                    {showModeToggle && (
                    <div className="inline-flex rounded-lg bg-gray-100 p-0.5 gap-0.5 shadow-inner shrink-0">
                        <button
                            onClick={onSwitchToConstruction}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all text-gray-500 hover:text-gray-700"
                            title={t('admin.arch.mode_construction')}
                        >
                            <FaHardHat size={11} />
                            <span className="hidden sm:inline">{t('admin.arch.mode_construction')}</span>
                        </button>
                        <button
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all bg-white text-gray-800 shadow-sm"
                            title={t('admin.arch.mode_arch')}
                        >
                            <FaDraftingCompass size={11} />
                            <span className="hidden sm:inline">{t('admin.arch.mode_arch')}</span>
                        </button>
                    </div>
                    )}
                    <button
                        onClick={() => navigate('/admin/arch-calculator')}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-primary bg-primary/10 rounded-lg border border-primary/20 hover:bg-primary/20 transition-all shrink-0"
                        title={t('admin.arch.quick_calc')}
                    >
                        <FaCalculator size={11} />
                        <span className="hidden sm:inline">{t('admin.arch.quick_calc_label')}</span>
                    </button>
                    <button
                        onClick={handleSaveToDb}
                        disabled={isDbSaving}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-primary rounded-lg border border-primary/80 transition-all hover:bg-primary/90 disabled:opacity-60 shrink-0"
                    >
                        <FaSync size={12} className={isDbSaving ? 'animate-spin' : ''} />
                        <span className="hidden sm:inline">{isDbSaving ? t('admin.estimator.saving') : t('admin.estimator.save')}</span>
                    </button>
                    {tab === 'constructor' && (
                        <button onClick={() => setPreviewEst({
                            ...estimate,
                            sqft: calc.sqft,
                            salePrice: calc.salePrice,
                            totalContract: calc.totalContract,
                            mainPrice: calc.mainPrice,
                            civilPrice: calc.civilPrice,
                            useCivilSplit: calc.useCivilSplit,
                            civilAdminAmt: calc.civilAdminAmt,
                        })} className="p-2 text-gray-400 rounded-lg border border-gray-200 transition-all hover:bg-primary/10 hover:text-primary shrink-0" title={t('admin.arch.preview_title')}>
                            <FaEye size={13} />
                        </button>
                    )}
                    <button onClick={handleReset} className="p-2 text-gray-400 rounded-lg border border-gray-200 transition-all hover:bg-gray-50 hover:text-gray-600 shrink-0" title={t('admin.estimator.new_estimate')}>
                        <FaPlus size={13} />
                    </button>
                    <button onClick={onLogout} className="p-2 rounded-lg border border-gray-200 transition-all hover:bg-red-600 hover:text-white hover:border-red-600 text-ash shrink-0" title={t('admin.estimator.sign_out')}>
                        <FaSignOutAlt />
                    </button>
                </div>

                {/* Status sub-row */}
                <div className="flex gap-2 items-center px-4 pb-2 mx-auto max-w-7xl text-xs text-gray-400">
                    <span>{estimate.number}</span>
                    <span>·</span>
                    {isDbSaving && (
                        <span className="flex gap-1 items-center text-violet-400">
                            <FaSync size={9} className="animate-spin" /> {t('admin.estimator.saving')}
                        </span>
                    )}
                    {!isDbSaving && dbSavedAt && (
                        <span className="flex gap-1 items-center text-emerald-500">
                            <FaCheckCircle size={9} /> {t('admin.estimator.saved_at', { time: dbSavedAt })}
                        </span>
                    )}
                    {!isDbSaving && !dbSavedAt && isSaving && (
                        <span className="flex gap-1 items-center text-gray-400">
                            <FaSync size={9} /> {t('admin.estimator.auto_draft')}
                        </span>
                    )}
                </div>

                {/* Sub-tab bar */}
                <div className="flex px-4 mx-auto max-w-7xl border-t border-gray-100 overflow-x-auto [&::-webkit-scrollbar]:hidden">
                    {ARCH_TAB_DEFS.filter(tb => tb.id !== 'config' || (!roleLoading && canEditCatalog)).map(({ id, tKey, icon: Icon }) => (
                        <button
                            key={id}
                            onClick={() => setTab(id)}
                            className={`flex-1 shrink-0 flex items-center justify-center gap-1.5 px-2 sm:px-4 py-2.5 text-xs sm:text-sm font-semibold border-b-2 transition-all -mb-px ${
                                tab === id ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <Icon size={13} />
                            {t(tKey)}
                        </button>
                    ))}
                </div>
            </header>

            {/* Body */}
            <main className="px-3 py-5 mx-auto max-w-7xl sm:px-6">
                <AnimatePresence mode="wait">
                    {tab === 'estimados' && (
                        <motion.div key="estimados" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                            <ArchEstimadosTab
                                estimates={archEstimates}
                                loading={archEstimatesLoading}
                                error={archEstimatesError}
                                reload={reloadArchEstimates}
                                onResume={handleResumeFromList}
                                onClone={(id) => cloneArchEstimate(id, userId)}
                                onPreview={est => {
                                    const isAddAltPreview = ADD_ALT_PROJECT_TYPES.has(est.project_type || '')
                                    setPreviewEst({
                                        id:              est.id,
                                        number:          est.number,
                                        date:            est.date || '',
                                        client:          est.client            || '',
                                        phone:           est.phone             || '',
                                        email:           est.email             || '',
                                        mondayItemId:    est.monday_item_id    || '',
                                        projectType:     est.project_type      || '',
                                        projectDescription: est.project_description || '',
                                        address:         est.address           || '',
                                        addressDetails:  est.address_details || {},
                                        market:          est.market || 'FL_RES',
                                        sqft:            est.sqft != null ? String(est.sqft) : '',
                                        lengthFt:        est.length_ft != null ? String(est.length_ft) : '',
                                        widthFt:         est.width_ft != null ? String(est.width_ft) : '',
                                        useArea:         est.use_area  || 'direct',
                                        existSqft:       est.exist_sqft ?? 0,
                                        existDiff:       est.exist_diff ?? 0.125,
                                        remSqft:         est.rem_sqft   ?? 0,
                                        remDiff:         est.rem_diff   ?? 1,
                                        addSqft:         est.add_sqft   ?? 0,
                                        addDiff:         est.add_diff   ?? 1,
                                        difficulty:      est.difficulty || 1,
                                        disciplineScope: est.discipline_scope || 'full',
                                        archPct:         est.arch_pct   ?? 50,
                                        structPct:       est.struct_pct ?? 22,
                                        mepPct:          est.mep_pct    ?? 20,
                                        // Civil is never part of Add/Alt/Rem discipline split.
                                        civilPct:        isAddAltPreview ? 0 : (est.civil_pct ?? 0),
                                        adminPct:        est.admin_pct  ?? 8,
                                        civilAdminPct:   isAddAltPreview ? 0 : (est.civil_admin_pct ?? 0),
                                        addons:          Array.isArray(est.addons)  ? mergeEstimateAddons(est.addons, addonDefs)  : getDefaultEstimateAddons(addonDefs),
                                        cuotas:          (Array.isArray(est.cuotas) && est.cuotas.length > 0) ? est.cuotas : DEFAULT_CUOTAS.map(c => ({ ...c })),
                                        scopeItems:      Array.isArray(est.scope_items) ? est.scope_items : [],
                                        notes:           est.notes || '',
                                        additionalComments: est.additional_comments || '',
                                        timeFrameValue:  est.time_frame_value != null ? String(est.time_frame_value) : '',
                                        timeFrameUnit:   est.time_frame_unit || 'weeks',
                                        status:          est.status || 'draft',
                                        serviceGroups:   Array.isArray(est.service_groups) ? est.service_groups : [],
                                        terms:           Array.isArray(est.terms) ? est.terms : [],
                                        salePrice:       Number(est.sale_price)    || 0,
                                        totalContract:   Number(est.total_contract) || 0,
                                    })
                                }}
                                userId={userId}
                                session={session}
                            />
                        </motion.div>
                    )}

                    {tab === 'constructor' && (
                        <motion.div key="constructor" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                            <ConstructorTab
                                estimate={estimate}
                                update={update}
                                setDisciplinePct={setDisciplinePct}
                                balanceDisciplinePcts={balanceDisciplinePcts}
                                calc={calc}
                                canViewInternal={!!permissions['admin.estimator.internal_analysis']}
                                addrSuggestions={addrSuggestions}
                                addrLoading={addrLoading}
                                handleAddressInput={handleAddressInput}
                                handleSelectAddress={handleSelectAddress}
                                applyPreset={applyPreset}
                                toggleAddon={toggleAddon}
                                setAddonAmount={setAddonAmount}
                                moveAddon={moveAddon}
                                setCuotas={setCuotas}
                                customPresets={schedulePresets}
                                disciplinePctSum={disciplinePctSum}
                                mainPctSum={mainPctSum}
                                civilPctSumLocal={civilPctSumLocal}
                                dbDisciplinePresets={dbDisciplinePresets}
                                internalOpen={internalOpen}
                                setInternalOpen={setInternalOpen}
                                catSearch={catSearch}
                                setCatSearch={setCatSearch}
                                catPhase={catPhase}
                                setCatPhase={setCatPhase}
                                catProjectType={catProjectType}
                                setCatProjectType={setCatProjectType}
                                catSearchOpen={catSearchOpen}
                                setCatSearchOpen={setCatSearchOpen}
                                catalogItems={archCatalogItems}
                                marketOptions={marketOptions}
                                session={session}
                                onApplyMonday={handleApplyMonday}
                                mondayItems={allMondayItems}
                                addonDefs={addonDefs}
                                mondayItemsLoading={mondayItemsLoading}
                                refreshMondayItems={() => loadMondayItems(true)}
                                setEstimate={setEstimate}
                                archTermsConditions={archTermsConditions}
                                civilManualOverride={civilManualOverride}
                                setCivilManualOverride={setCivilManualOverride}
                                onScopeChange={handleScopeChange}
                                disciplineSuggestion={disciplineSuggestion}
                                onApplySuggestion={() => {
                                    if (disciplineSuggestion) {
                                        applyPreset(disciplineSuggestion)
                                        update('disciplineScope', disciplineSuggestion.scope)
                                        setDisciplineSuggestion(null)
                                    }
                                }}
                                onDismissSuggestion={() => setDisciplineSuggestion(null)}
                                undoScopeSnapshot={undoScopeSnapshot}
                                onUndoScope={() => {
                                    if (undoScopeSnapshot !== null) {
                                        update('scopeItems', undoScopeSnapshot)
                                        setUndoScopeSnapshot(null)
                                        setDisciplineSuggestion(null)
                                    }
                                }}
                                onDismissUndo={() => setUndoScopeSnapshot(null)}
                                autoPhase={autoPhase}
                                onSwitchPhase={handleRepopulatePhase}
                                difficultyLevels={difficultyLevels}
                                civilLevels={civilLevels}
                                getOptionsForComponent={getOptionsForComponent}
                                openMinOverride={openMinOverride}
                            />
                        </motion.div>
                    )}

                    {tab === 'config' && (
                        <motion.div key="config" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                            <ArchConfigTab
                                session={session}
                                canEditCatalog={canEditCatalog}
                                catalogItems={archCatalogItems}
                                upsertItem={upsertArchCatalogItem}
                                removeItem={removeArchCatalogItem}
                                reloadCatalog={reloadCatalog}
                                difficultyLevels={difficultyLevels}
                                difficultyLoading={difficultyLoading}
                                upsertDifficultyLevel={upsertDifficultyLevel}
                                removeDifficultyLevel={removeDifficultyLevel}
                                reloadDifficultyLevels={reloadDifficultyLevels}
                                civilLevels={civilLevels}
                                civilLoading={civilLoading}
                                upsertCivilLevel={upsertCivilLevel}
                                removeCivilLevel={removeCivilLevel}
                                reloadCivilLevels={reloadCivilLevels}
                                paymentInfos={paymentInfoRows}
                                createPaymentInfo={createPaymentInfo}
                                updatePaymentInfo={updatePaymentInfo}
                                deletePaymentInfo={deletePaymentInfo}
                                reloadPaymentInfo={reloadPaymentInfo}
                                addAltDiffRows={addAltDiffRows}
                                addAltDiffLoading={addAltDiffLoading}
                                getOptionsForComponent={getOptionsForComponent}
                                upsertDifficultyOption={upsertDifficultyOption}
                                removeDifficultyOption={removeDifficultyOption}
                                reloadAddAltDifficulties={reloadAddAltDifficulties}
                                addonDefs={addonDefs}
                                createArchAddon={createArchAddon}
                                updateArchAddon={updateArchAddon}
                                removeArchAddon={removeArchAddon}
                                reloadAddons={reloadAddons}
                                schedulePresets={schedulePresets}
                                schedulePresetsLoading={schedulePresetsLoading}
                                createSchedulePreset={createSchedulePreset}
                                updateSchedulePreset={updateSchedulePreset}
                                deleteSchedulePreset={deleteSchedulePreset}
                                setDefaultPreset={setDefaultPreset}
                                clearDefaultPreset={clearDefaultPreset}
                                reloadSchedulePresets={reloadSchedulePresets}
                                defaultSourceFiles={defaultSourceFiles}
                                setSourceDefault={setSourceDefault}
                                uiDraft={uiBackup}
                                onUiChange={setUiBackup}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>
        </div>
    )
}

// ── Panel drag-and-drop helpers ───────────────────────────────────────────────
const displayFileName = (name) => name?.replace(/\.docx$/i, '') || name || ''
const CONSTRUCTOR_STORAGE_KEY = 'arch_constructor_panel_order'
const DEFAULT_PANEL_ORDER = [
    'client_info', 'scope_of_work',
    'market', 'discipline_split',
    'price_summary', 'payment_schedule',
    'terms',
    'internal_analysis',
]

function ConstructorTermsPanel({ catalogTerms, estimate, setEstimate, t }) {
    const [expandedFiles, setExpandedFiles] = useState(new Set())
    const [searchQuery, setSearchQuery] = useState('')
    const debouncedQuery = useDebouncedValue(searchQuery)
    const searchTokens = useMemo(() => buildSearchTokens(debouncedQuery), [debouncedQuery])

    const { catalogByFile, existingMap, activeCount } = useMemo(() => {
        const map = new Map()
        for (const item of (catalogTerms || [])) {
            if (!map.has(item.source_file)) map.set(item.source_file, [])
            const arr = map.get(item.source_file)
            const seenTexts = new Set(arr.map(i => i.text))
            if (!seenTexts.has(item.text)) arr.push(item)
        }
        for (const [, items] of map) {
            items.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        }
        const groups = Array.from(map.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([sourceFile, items]) => ({ sourceFile, items }))

        const eMap = new Map()
        for (const term of (estimate.terms || [])) {
            eMap.set(term.id, term)
        }

        const active = (estimate.terms || []).filter(t => t.included && !t.is_section_title).length

        return { catalogByFile: groups, existingMap: eMap, activeCount: active }
    }, [catalogTerms, estimate.terms])

    const toggleExpand = (sourceFile) => {
        setExpandedFiles(prev => {
            const next = new Set(prev)
            if (next.has(sourceFile)) next.delete(sourceFile)
            else next.add(sourceFile)
            return next
        })
    }

    const toggleGroup = (groupItems) => {
        const contentItems = groupItems.filter(i => !i.is_section_title)
        const anyIncluded = contentItems.some(item => {
            const existing = existingMap.get(item.id)
            return existing && existing.included
        })

        if (anyIncluded) {
            const groupIds = new Set(contentItems.map(i => i.id))
            const next = (estimate.terms || []).map(t =>
                groupIds.has(t.id) ? { ...t, included: false } : t
            )
            setEstimate(prev => ({ ...prev, terms: next }))
        } else {
            const nextTerms = [...(estimate.terms || [])]
            const existingIds = new Set(nextTerms.map(t => t.id))
            for (const item of contentItems) {
                if (existingIds.has(item.id)) {
                    const idx = nextTerms.findIndex(t => t.id === item.id)
                    if (idx !== -1) nextTerms[idx] = { ...nextTerms[idx], included: true }
                } else {
                    nextTerms.push({
                        id: item.id,
                        text: item.text,
                        included: true,
                        sort_order: item.sort_order ?? 0,
                        source_file: item.source_file,
                        section_title: item.section_title ?? null,
                        is_section_title: item.is_section_title ?? false,
                        is_bullet: item.is_bullet ?? false,
                    })
                }
            }
            setEstimate(prev => ({ ...prev, terms: nextTerms }))
        }
    }

    const toggleTerm = (item) => {
        if (item.is_section_title) return
        const existing = existingMap.get(item.id)
        if (existing) {
            const next = (estimate.terms || []).map(t =>
                t.id === item.id ? { ...t, included: !t.included } : t
            )
            setEstimate(prev => ({ ...prev, terms: next }))
        } else {
            setEstimate(prev => ({
                ...prev,
                terms: [...(prev.terms || []), {
                    id: item.id,
                    text: item.text,
                    included: true,
                    sort_order: item.sort_order ?? 0,
                    source_file: item.source_file,
                    section_title: item.section_title ?? null,
                    is_section_title: item.is_section_title ?? false,
                    is_bullet: item.is_bullet ?? false,
                }]
            }))
        }
    }

    const filteredGroups = useMemo(() => {
        if (!debouncedQuery.trim()) return catalogByFile
        const tokens = buildSearchTokens(debouncedQuery)
        return catalogByFile.map(({ sourceFile, items }) => {
            const fileName = displayFileName(sourceFile).toLowerCase()
            const matches = (text, secTitle) => {
                const t = (text || '').toLowerCase()
                const s = (secTitle || '').toLowerCase()
                return tokens.every(tok => t.includes(tok) || s.includes(tok) || fileName.includes(tok))
            }
            const result = []
            let lastHeader = null
            for (const item of items) {
                if (item.is_section_title) {
                    lastHeader = item
                    if (matches(item.text, item.section_title)) {
                        result.push(item)
                        lastHeader = null
                    }
                } else if (matches(item.text, item.section_title)) {
                    if (lastHeader) { result.push(lastHeader); lastHeader = null }
                    result.push(item)
                }
            }
            return { sourceFile, items: result }
        }).filter(g => g.items.length > 0)
    }, [catalogByFile, debouncedQuery])

    const totalCount = catalogTerms?.length || 0

    return (
        <section className="overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="h-[3px] bg-gray-300" />
            <div className="p-5">
                <div className="flex justify-between items-center mb-3">
                    <h2 className="flex gap-2 items-center text-sm font-bold text-gray-700">
                        <FaStickyNote size={12} className="text-primary" />
                        {t('admin.arch.config_section_terms')}
                        <span className="text-[10px] font-normal text-gray-400 ml-1">{activeCount}/{totalCount}</span>
                    </h2>
                </div>
                <div className="relative mb-3">
                    <FaSearch size={12} className="absolute left-3 top-1/2 text-gray-400 -translate-y-1/2" />
                    <input
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder={t('admin.arch.terms_search_placeholder')}
                        className="py-2 pr-8 pl-8 w-full text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                    {searchQuery && (
                        <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 text-gray-400 -translate-y-1/2 hover:text-gray-600">
                            <FaTimes size={10} />
                        </button>
                    )}
                </div>
                <div className="space-y-4 max-h-[600px] overflow-y-auto pr-1">
                    {filteredGroups.map(({ sourceFile, items }) => {
                        const contentItems = items.filter(i => !i.is_section_title)
                        const includedCount = contentItems.filter(item => {
                            const existing = existingMap.get(item.id)
                            return existing && existing.included
                        }).length
                        const allChecked = includedCount === contentItems.length && contentItems.length > 0
                        const someChecked = includedCount > 0 && includedCount < contentItems.length
                        const isExpanded = expandedFiles.has(sourceFile) || !!searchQuery.trim()
                        return (
                            <div key={sourceFile} className="overflow-hidden rounded-lg border border-gray-100">
                                <div className="flex gap-2 items-center px-3 py-2 bg-gray-50 border-b border-gray-100">
                                    <input
                                        type="checkbox"
                                        checked={allChecked}
                                        ref={el => { if (el) el.indeterminate = someChecked }}
                                        onChange={() => toggleGroup(items)}
                                        className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary/30"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => toggleExpand(sourceFile)}
                                        className="flex flex-1 gap-2 items-center min-w-0 text-left"
                                    >
                                        {isExpanded ? <FaChevronUp size={10} className="text-gray-400 shrink-0" /> : <FaChevronDown size={10} className="text-gray-400 shrink-0" />}
                                        <span className="text-xs font-bold tracking-wide text-gray-700 uppercase truncate" title={displayFileName(sourceFile)}>{displayFileName(sourceFile)}</span>
                                    </button>
                                    <span className="text-[10px] text-gray-400 shrink-0">{includedCount}/{contentItems.length}</span>
                                </div>
                                {isExpanded && (
                                    <div className="space-y-0.5 p-2">
                                        {items.map(item => {
                                            if (item.is_section_title) {
                                                return (
                                                    <div key={item.id} className="px-2 py-0.5 text-[10px] font-bold text-gray-500 uppercase tracking-wide truncate mt-1 mb-0.5">
                                                        <HighlightText text={cleanTermText(item.text)} tokens={searchTokens} />
                                                    </div>
                                                )
                                            }
                                            const existing = existingMap.get(item.id)
                                            const isIncluded = existing ? existing.included : false
                                            return (
                                                <label key={item.id} className="flex gap-2 items-center px-2 py-1 rounded cursor-pointer select-none hover:bg-gray-50">
                                                    <input
                                                        type="checkbox"
                                                        checked={isIncluded}
                                                        onChange={() => toggleTerm(item)}
                                                        className="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-primary/30"
                                                    />
                                                    <span className={`text-xs ${isIncluded ? 'text-gray-700' : 'text-gray-400 line-through'}`}><HighlightText text={cleanTermText(item.text)} tokens={searchTokens} /></span>
                                                </label>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
                {filteredGroups.length === 0 && (
                    <p className="text-sm italic text-center text-gray-400">{t('admin.arch.terms_empty')}</p>
                )}
            </div>
        </section>
    )
}

function SortablePanelWrapper({ id, children }) {
    const { t } = useTranslation()
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
    return (
        <div
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : undefined }}
            className={`relative group ${isDragging ? 'rounded-2xl ring-2 opacity-60 ring-primary/30' : ''}`}
        >
            <div
                {...attributes} {...listeners}
                className="absolute top-2.5 right-2.5 z-10 p-1.5 rounded-lg opacity-40 group-hover:opacity-100 cursor-grab active:cursor-grabbing transition-opacity bg-white shadow-sm border border-gray-200 text-gray-300 hover:text-gray-500 select-none"
                title={t('admin.arch.drag_to_reorder')}
            >
                <FaGripVertical size={10} />
            </div>
            {children}
        </div>
    )
}

// ── Sortable add-on row for ConstructorTab DnD ──────────────────────────────
function SortableAddonRow({ id, children }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
    return (
        <div
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : undefined }}
            className={`flex gap-2 items-center ${isDragging ? 'opacity-60' : ''}`}
        >
            <div
                {...attributes} {...listeners}
                className="p-1 text-gray-300 select-none shrink-0 cursor-grab active:cursor-grabbing hover:text-gray-500"
                title="Drag to reorder"
            >
                <FaGripVertical size={12} />
            </div>
            <div className="flex-1 min-w-0">{children}</div>
        </div>
    )
}

// ── ConstructorTab ────────────────────────────────────────────────────────────
function ConstructorTab({
    estimate, update, setDisciplinePct, balanceDisciplinePcts, calc, canViewInternal,
    addrSuggestions, addrLoading, handleAddressInput, handleSelectAddress,
    applyPreset, toggleAddon, setAddonAmount, moveAddon,
    setCuotas, disciplinePctSum, mainPctSum, civilPctSumLocal,
    dbDisciplinePresets = {},
    customPresets = [],
    internalOpen, setInternalOpen,
    catSearch, setCatSearch,
    catPhase, setCatPhase,
    catProjectType, setCatProjectType,
    catSearchOpen, setCatSearchOpen,
    catalogItems, marketOptions = [],
    session, onApplyMonday,
    disciplineSuggestion, onApplySuggestion, onDismissSuggestion,
    undoScopeSnapshot, onUndoScope, onDismissUndo,
    autoPhase, onSwitchPhase,
    setEstimate,
    onScopeChange,
    addonDefs,
    mondayItems,
    mondayItemsLoading,
    refreshMondayItems,
    difficultyLevels,
    civilLevels,
    getOptionsForComponent,
    archTermsConditions,
    openMinOverride,
    civilManualOverride,
    setCivilManualOverride,
}) {
    const { t } = useTranslation()
    // Collapsible block toggles (market = area/difficulty, civil = civil split)
    const [marketBlockOpen, setMarketBlockOpen] = useState(false)
    const [showMoreMarkets, setShowMoreMarkets] = useState(false)
    const [civilBlockOpen,  setCivilBlockOpen]  = useState(false)

    // Auto-expand civil split block when activated
    useEffect(() => {
        if (calc.useCivilSplit) setCivilBlockOpen(true)
    }, [calc.useCivilSplit])

    const scopeIncludesCivilTab = (estimate.disciplineScope || 'full') === 'full' || (estimate.disciplineScope || '').includes('civil')
    const isCivilProject = estimate.projectType === 'civil'
    const mainPctOk = isCivilProject || Math.abs(mainPctSum - 100) < 0.01
    const civilPctOk = !scopeIncludesCivilTab || Math.abs(civilPctSumLocal - 100) < 0.01
    const pctSumOk = calc.useCivilSplit ? (mainPctOk && civilPctOk) : Math.abs(disciplinePctSum - 100) < 0.01
    const addonSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))

    // Use civil difficulty levels when market is CIVIL, otherwise use standard new-construction levels
    const isCivilMarket = (marketOptions.find(m => m.key === estimate.market)?.type || '') === 'CIVIL'
    const effectiveDiffLevels = isCivilMarket ? (civilLevels || []) : (difficultyLevels || [])

    // Reset difficulty when switching between civil/non-civil markets if current value is not in the new set
    useEffect(() => {
        if (effectiveDiffLevels.length === 0) return
        const hasCurrent = effectiveDiffLevels.some(l => Math.abs(l.value - estimate.difficulty) < 0.001)
        if (!hasCurrent) {
            update('difficulty', effectiveDiffLevels[0].value)
        }
    }, [isCivilMarket]) // eslint-disable-line react-hooks/exhaustive-deps

    // ── Discipline deduction (Option B: post-zero prompt) ───────────────────
    const [pendingDeduct, setPendingDeduct] = useState(null)

    const FIELD_TO_AMT = {
        archPct: 'archAmt', structPct: 'structAmt', mechPct: 'mechAmt',
        elecPct: 'elecAmt', plumbPct: 'plumbAmt', civilPct: 'civilAmt', adminPct: 'adminAmt',
        civilAdminPct: 'civilAdminAmt',
    }

    const handleDisciplinePctChange = useCallback((field, value) => {
        const currentPct = estimate[field] || 0
        const alreadyDeducted = (estimate.disciplineDeductions || []).some(d => d.field === field)
        if (Number(value) === 0 && currentPct > 0 && !alreadyDeducted) {
            const amtKey = FIELD_TO_AMT[field]
            const amount = amtKey ? (calc[amtKey] || 0) : 0
            setPendingDeduct({ field, savedPct: currentPct, amount })
        } else if (Number(value) !== 0 && pendingDeduct?.field === field) {
            setPendingDeduct(null)
        }
        setDisciplinePct(field, value)
    }, [estimate, calc, pendingDeduct, setDisciplinePct])

    const handleConfirmDeduct = useCallback((fieldLabel) => {
        if (!pendingDeduct) return
        update('disciplineDeductions', [...(estimate.disciplineDeductions || []), { ...pendingDeduct, label: fieldLabel }])
        setPendingDeduct(null)
    }, [pendingDeduct, update, estimate.disciplineDeductions])

    const handleRestoreDeduction = useCallback((field, savedPct) => {
        update('disciplineDeductions', (estimate.disciplineDeductions || []).filter(d => d.field !== field))
        setDisciplinePct(field, savedPct)
    }, [update, estimate.disciplineDeductions, setDisciplinePct])

    // Notes modal state
    const [notesOpen, setNotesOpen] = useState(false)

    // Local notes state — decoupled from parent to avoid re-rendering the full tree on every keystroke
    const [localNotes, setLocalNotes] = useState(estimate.notes || '')
    const notesDebounceRef = useRef(null)
    useEffect(() => () => clearTimeout(notesDebounceRef.current), [])
    // Sync when a different estimate is loaded (covers both DB-saved estimates and unsaved drafts)
    const prevEstimateRef = useRef(estimate)
    useEffect(() => {
        const prev = prevEstimateRef.current
        if (prev !== estimate && (prev.id !== estimate.id || prev.notes !== estimate.notes)) {
            setLocalNotes(estimate.notes || '')
        }
        prevEstimateRef.current = estimate
    }) // eslint-disable-line react-hooks/exhaustive-deps
    const handleNotesChange = (val) => {
        setLocalNotes(val)
        clearTimeout(notesDebounceRef.current)
        notesDebounceRef.current = setTimeout(() => update('notes', val), 300)
    }

    // Local additionalComments state
    const [localAdditionalComments, setLocalAdditionalComments] = useState(estimate.additionalComments || '')
    const additionalCommentsDebounceRef = useRef(null)
    useEffect(() => () => clearTimeout(additionalCommentsDebounceRef.current), [])
    const prevAdditionalCommentsRef = useRef(estimate)
    useEffect(() => {
        const prev = prevAdditionalCommentsRef.current
        if (prev !== estimate && (prev.id !== estimate.id || prev.additionalComments !== estimate.additionalComments)) {
            setLocalAdditionalComments(estimate.additionalComments || '')
        }
        prevAdditionalCommentsRef.current = estimate
    }) // eslint-disable-line react-hooks/exhaustive-deps
    const handleAdditionalCommentsChange = (val) => {
        setLocalAdditionalComments(val)
        clearTimeout(additionalCommentsDebounceRef.current)
        additionalCommentsDebounceRef.current = setTimeout(() => update('additionalComments', val), 300)
    }

    // Sync catalog project-type filter when a different estimate is loaded
    useEffect(() => {
        setCatProjectType(estimate.projectType || '')
        setCatPhase(autoPhase || 'cds')
    }, [estimate.id]) // eslint-disable-line react-hooks/exhaustive-deps

    // Sync catPhase when autoPhase changes (user toggles CDs/Design)
    useEffect(() => {
        setCatPhase(autoPhase || 'cds')
    }, [autoPhase]) // eslint-disable-line react-hooks/exhaustive-deps

    const DISC_COLORS = {
        ARCHITECTURE: 'bg-blue-100 text-blue-700 border-blue-200',
        ARCH:         'bg-blue-100 text-blue-700 border-blue-200',
        STRUCTURAL:   'bg-amber-100 text-amber-700 border-amber-200',
        STRUCT:       'bg-amber-100 text-amber-700 border-amber-200',
        MEP:          'bg-teal-100 text-teal-700 border-teal-200',
        '3D':         'bg-pink-100 text-pink-700 border-pink-200',
        CIVIL:        'bg-green-100 text-green-700 border-green-200',
        ECC:          'bg-orange-100 text-orange-700 border-orange-200',
        ADMIN:        'bg-violet-100 text-violet-700 border-violet-200',
        PM:           'bg-violet-100 text-violet-700 border-violet-200',
    }

    const DISCIPLINE_SCOPE_ALLOWED = {
        full:         ['ARCH', 'STRUCT', 'MEP', '3D', 'CIVIL', 'ECC', 'ADMIN'],
        arch_struct:  ['ARCH', 'STRUCT'],
        arch_mep:     ['ARCH', 'MEP'],
        arch_civil:   ['ARCH', 'CIVIL'],
        struct_civil: ['STRUCT', 'CIVIL'],
        mep_civil:    ['MEP', 'CIVIL'],
        solo_arch:    ['ARCH'],
        solo_struct:  ['STRUCT'],
        solo_mep:     ['MEP'],
        solo_civil:   ['CIVIL'],
    }

    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    const filteredCatalog = useMemo(() => {
        let list = catalogItems || []
        if (catPhase)       list = list.filter(i => i.phase        === catPhase)
        if (catProjectType) list = list.filter(i => i.project_type === catProjectType)
        list = filterByProjectTypeSuffix(list, estimate.projectType)
        const discs = DISCIPLINE_SCOPE_ALLOWED[estimate.disciplineScope] || DISCIPLINE_SCOPE_ALLOWED.full
        list = list.filter(i => discs.includes(normalizeDiscipline(i.discipline)))
        const q = catSearch.trim().toLowerCase()
        if (!q) return list
        return list.filter(i =>
            i.name.toLowerCase().includes(q) ||
            i.discipline?.toLowerCase().includes(q) ||
            (i.subcat || '').toLowerCase().includes(q)
        )
    }, [catalogItems, catSearch, catPhase, catProjectType, estimate.projectType, estimate.disciplineScope])

    const scopeItems    = estimate.scopeItems || []
    const allowedDiscs  = DISCIPLINE_SCOPE_ALLOWED[estimate.disciplineScope] || DISCIPLINE_SCOPE_ALLOWED.full
    const visibleScopeItems = filterByProjectTypeSuffix(
        applyAddonGatedFilter(
            scopeItems.filter(i => allowedDiscs.includes(normalizeDiscipline(i.discipline))),
            estimate.addons,
            addonDefs
        ),
        estimate.projectType
    )
    const selectedIds   = new Set(scopeItems.map(i => i.id))

    const addScopeItem = (item) => {
        if (selectedIds.has(item.id)) return
        update('scopeItems', [...scopeItems, { id: item.id, name: item.name, discipline: item.discipline, subcat: item.subcat, phase: item.phase }])
    }
    const removeScopeItem = (id) => update('scopeItems', scopeItems.filter(i => i.id !== id))

    // ── Scope item native DnD state ──────────────────────────────────────────
    const [draggedScopeId, setDraggedScopeId] = useState(null)
    const [dragOverScopeId, setDragOverScopeId] = useState(null)

    const handleScopeDragStart = (e, id) => {
        setDraggedScopeId(id)
        e.dataTransfer.effectAllowed = 'move'
        // Build a lightweight ghost so the user sees what's being dragged
        const item = scopeItems.find(i => i.id === id)
        const ghost = document.createElement('div')
        ghost.textContent = item?.name || ''
        ghost.style.cssText = 'padding:8px 16px;background:#fff;border:1px solid #e5e7eb;border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,0.15);font-size:13px;color:#374151;position:fixed;top:-9999px;pointer-events:none;'
        document.body.appendChild(ghost)
        e.dataTransfer.setDragImage(ghost, 0, 0)
        requestAnimationFrame(() => { if (ghost.parentNode) ghost.parentNode.removeChild(ghost) })
    }
    const handleScopeDragOver = (e, id) => {
        e.preventDefault()
        if (id !== draggedScopeId) setDragOverScopeId(id)
    }
    const handleScopeDragLeave = () => {
        setDragOverScopeId(null)
    }
    const handleScopeDrop = (e, targetId) => {
        e.preventDefault()
        if (!draggedScopeId || draggedScopeId === targetId) {
            setDraggedScopeId(null)
            setDragOverScopeId(null)
            return
        }
        const fromIndex = scopeItems.findIndex(i => i.id === draggedScopeId)
        const toIndex   = scopeItems.findIndex(i => i.id === targetId)
        if (fromIndex === -1 || toIndex === -1) {
            setDraggedScopeId(null)
            setDragOverScopeId(null)
            return
        }
        const next = [...scopeItems]
        const [moved] = next.splice(fromIndex, 1)
        // When moving forward, toIndex shifts down by 1 after removal, so insert at toIndex - 1
        const insertIndex = fromIndex < toIndex ? toIndex - 1 : toIndex
        next.splice(insertIndex, 0, moved)
        update('scopeItems', next)
        setDraggedScopeId(null)
        setDragOverScopeId(null)
    }
    const handleScopeDragEnd = () => {
        setDraggedScopeId(null)
        setDragOverScopeId(null)
    }

    // ── Panel DnD state ──────────────────────────────────────────────────────
    const [panelOrder, setPanelOrder] = useState(() => {
        try {
            const saved = localStorage.getItem(CONSTRUCTOR_STORAGE_KEY)
            if (saved) {
                const parsed = JSON.parse(saved)
                const merged = parsed.filter(id => DEFAULT_PANEL_ORDER.includes(id))
                DEFAULT_PANEL_ORDER.forEach(id => { if (!merged.includes(id)) merged.push(id) })
                // Ensure internal_analysis is always last so visibleOrder filter never skews indices
                const iaIndex = merged.indexOf('internal_analysis')
                if (iaIndex !== -1 && iaIndex !== merged.length - 1) {
                    merged.splice(iaIndex, 1)
                    merged.push('internal_analysis')
                }
                return merged
            }
        } catch {}
        return DEFAULT_PANEL_ORDER
    })
    const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))
    const handlePanelDragEnd = useCallback((event) => {
        const { active, over } = event
        if (!over || active.id === over.id) return
        setPanelOrder(order => {
            const next = arrayMove(order, order.indexOf(active.id), order.indexOf(over.id))
            localStorage.setItem(CONSTRUCTOR_STORAGE_KEY, JSON.stringify(next))
            return next
        })
    }, [])
    const resetPanelOrder = useCallback(() => {
        localStorage.removeItem(CONSTRUCTOR_STORAGE_KEY)
        setPanelOrder([...DEFAULT_PANEL_ORDER])
    }, [])
    const visibleOrder = panelOrder.filter(id => id !== 'internal_analysis' || canViewInternal)

    // ── Panel renderer ───────────────────────────────────────────────────────
    const renderPanel = (id) => {
        switch (id) {
            case 'client_info': return (
                <section className="overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-sm">
                <div className="h-[3px] bg-blue-500" />
                <div className="p-5">
                    <div className="mb-4">
                        <h2 className="flex gap-2 items-center text-sm font-bold text-gray-700">
                            <FaUser size={12} className="text-primary" />
                            {t('admin.estimator.client_info')}
                        </h2>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                            <label className="block mb-1 text-xs text-gray-500">
                                <FaHashtag size={9} className="inline mr-0.5" />{t('admin.estimator.estimate_num')}
                            </label>
                            <input readOnly value={estimate.number} className="px-3 py-2 w-full text-sm text-gray-400 bg-gray-50 rounded-xl border border-gray-200 cursor-not-allowed" />
                        </div>
                        <div>
                            <label className="block mb-1 text-xs text-gray-500">
                                <FaCalendarAlt size={9} className="inline mr-0.5" />{t('admin.estimator.issue_date')}
                            </label>
                            <input type="date" value={estimate.date} onChange={e => update('date', e.target.value)}
                                className="px-3 py-2 w-full text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                        </div>
                        <div>
                            <label className="block mb-1 text-xs text-gray-500">
                                <FaUser size={9} className="inline mr-0.5" />{t('admin.estimator.client_name')}
                            </label>
                            <ArchClientAutocomplete
                                value={estimate.client || ''}
                                items={mondayItems}
                                itemsLoading={mondayItemsLoading}
                                onSelect={onApplyMonday}
                                onRefreshItems={refreshMondayItems}
                            />
                        </div>
                        <div>
                            <label className="block mb-1 text-xs text-gray-500">{t('admin.estimator.client_phone')}</label>
                            <input value={estimate.phone || ''} onChange={e => update('phone', e.target.value)}
                                placeholder="(305) 555-0100"
                                className="px-3 py-2 w-full text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                        </div>
                        <div>
                            <label className="block mb-1 text-xs text-gray-500">{t('admin.estimator.client_email')}</label>
                            <input type="email" value={estimate.email || ''} onChange={e => update('email', e.target.value)}
                                placeholder="client@email.com"
                                className="px-3 py-2 w-full text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                        </div>
                        <div className="relative">
                            <label className="block mb-1 text-xs text-gray-500">
                                <FaMapMarkerAlt size={9} className="inline mr-0.5" />{t('admin.estimator.project_address')}
                            </label>
                            <MondayAddressAutocomplete
                                value={estimate.address}
                                items={mondayItems}
                                itemsLoading={mondayItemsLoading}
                                onSelectMonday={onApplyMonday}
                                onSelectGoogle={handleSelectAddress}
                                googleSuggestions={addrSuggestions}
                                googleLoading={addrLoading}
                                onInput={handleAddressInput}
                            />
                        </div>
                    </div>
                    {(estimate.projectType || estimate.projectDescription) && (
                        <div className="flex flex-wrap gap-1.5 items-center mt-3 pt-3 border-t border-gray-100">
                            <span className="flex items-center gap-1 text-[10px] text-gray-400"><FaLink size={8} /> Monday</span>
                            {estimate.projectType && (
                                <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-violet-50 text-violet-600 border border-violet-200">{t('admin.arch.ptype_' + estimate.projectType) || estimate.projectType}</span>
                            )}
                            {estimate.projectDescription && (
                                <span className="px-2 py-0.5 text-[10px] rounded-full bg-gray-100 text-gray-500">{estimate.projectDescription}</span>
                            )}
                        </div>
                    )}
                </div>
                </section>
            )
            case 'scope_of_work': return (
                <section className="overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-sm">
                <div className="h-[3px] bg-gray-300" />
                <div className="p-5">
                    <div className="flex justify-between items-center mb-3">
                        <h2 className="flex gap-2 items-center text-sm font-bold text-gray-700">
                            <FaStickyNote size={12} className="text-primary" />
                            {t('admin.arch.scope_of_work')}
                            <span className="text-[10px] font-normal text-gray-400 ml-1">{t('admin.estimator.optional_printed')}</span>
                        </h2>
                        <button onClick={() => setNotesOpen(true)} title="Expand notes" className="p-1.5 text-gray-400 rounded-lg transition-colors hover:bg-gray-100 hover:text-gray-600"><FaExpand size={11} /></button>
                    </div>
                    <textarea value={localNotes} onChange={e => handleNotesChange(e.target.value)} rows={4} placeholder={t('admin.estimator.notes_placeholder')} className="px-3 py-2 w-full text-sm rounded-xl border border-gray-200 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30" />
                    <div className="pt-4 mt-4 border-t border-gray-100">
                        <div className="flex justify-between items-center mb-2">
                            <h2 className="flex gap-2 items-center text-sm font-bold text-gray-700">
                                <FaComment size={12} className="text-primary" />
                                {t('admin.arch.additional_comments')}
                                <span className="text-[10px] font-normal text-gray-400 ml-1">{t('admin.estimator.optional_printed')}</span>
                            </h2>
                        </div>
                        <textarea value={localAdditionalComments} onChange={e => handleAdditionalCommentsChange(e.target.value)} rows={3} placeholder={t('admin.arch.additional_comments')} className="px-3 py-2 w-full text-sm rounded-xl border border-gray-200 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30" />
                    </div>
                    <div className="pt-4 mt-4 border-t border-gray-100">
                        <label className="block mb-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wide">{t('admin.arch.time_frame')}</label>
                        <div className="flex gap-2 items-center">
                            <input
                                type="number" min="0" step="1"
                                value={estimate.timeFrameValue || ''}
                                onChange={e => update('timeFrameValue', e.target.value)}
                                placeholder="6"
                                className="flex-1 px-3 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
                            />
                            <div className="grid overflow-hidden grid-cols-3 rounded-xl border border-gray-200 divide-x divide-gray-200 shrink-0">
                                {[
                                    { v: 'days', l: t('admin.arch.time_frame_unit_days') },
                                    { v: 'weeks', l: t('admin.arch.time_frame_unit_weeks') },
                                    { v: 'months', l: t('admin.arch.time_frame_unit_months') },
                                ].map(opt => (
                                    <button
                                        key={opt.v}
                                        onClick={() => update('timeFrameUnit', opt.v)}
                                        className={`px-3 py-2 text-xs font-bold whitespace-nowrap text-center transition-all ${(estimate.timeFrameUnit || 'weeks') === opt.v ? 'bg-primary text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                                    >
                                        {opt.l}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                </section>
            )
            case 'market': return (
                <section className="overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-sm">
                <div className={`h-[3px] ${calc.isAddAlt ? 'bg-orange-500' : 'bg-blue-500'}`} />
                <div className="p-5">
                    <button
                        type="button"
                        onClick={() => setMarketBlockOpen(o => !o)}
                        className="flex gap-2 items-center mb-4 w-full text-left"
                    >
                        <span className="text-[10px] text-gray-400 transition-transform">
                            {marketBlockOpen ? '▼' : '▶'}
                        </span>
                        <h2 className="flex gap-2 items-center text-sm font-bold text-gray-700">
                            <FaRulerCombined size={12} className="text-primary" />
                            {t('admin.arch.area_difficulty')} — {estimate.projectType ? t('admin.arch.ptype_' + estimate.projectType) : ''}
                        </h2>
                        {!marketBlockOpen && (
                            <span className="ml-auto text-[11px] text-gray-400">
                                {calc.sqft > 0 ? `${calc.sqft.toLocaleString()} sqft · $${fmt(calc.totalContract)}` : ''}
                            </span>
                        )}
                    </button>

                    <AnimatePresence initial={false}>
                        {marketBlockOpen && (
                            <motion.div
                                key="market-body"
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                            >
                                <div>
                    {calc.isAddAlt ? (
                        /* ── Add/Alt 3-component calculator ── */
                        <>
                            {/* Auto-detected market badge */}
                            <div className="flex gap-2 items-center px-3 py-2 mb-5 bg-blue-50 rounded-xl border border-blue-200">
                                <FaMapMarkerAlt size={10} className="text-blue-500 shrink-0" />
                                <span className="text-xs font-semibold text-blue-700">
                                    {t(MARKET_OPTIONS.find(m => m.key === calc.addAltMarket)?.labelKey) || calc.addAltMarket}
                                </span>
                                <span className="ml-auto text-[11px] font-mono text-blue-500 shrink-0">
                                    ${calc.addAltBaseRate}/eff-sqft
                                </span>
                            </div>

                            {/* Existente */}
                            <div className="mb-4">
                                <div className="flex justify-between items-center mb-1.5">
                                    <p className="text-xs font-semibold text-gray-700">{t('admin.arch.add_alt_existente')}</p>
                                    <span className="text-[11px] text-gray-400">eff: {calc.existEff.toLocaleString()} sqft</span>
                                </div>
                                <div className="flex gap-2 items-center mb-1.5">
                                    <input type="number" min="0" step="1" value={estimate.existSqft}
                                        onChange={e => update('existSqft', e.target.value)} placeholder="2400"
                                        className="flex-1 px-3 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                                    <span className="text-gray-300">×</span>
                                    <input type="number" min="0" step="0.05" value={estimate.existDiff}
                                        onChange={e => update('existDiff', e.target.value)} placeholder="0.125"
                                        className="w-24 px-2.5 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                                </div>
                                {(() => {
                                    const opts = getOptionsForComponent('exist') || []
                                    if (opts.length === 0) return null
                                    return (
                                        <div className="flex overflow-hidden flex-wrap rounded-xl border border-gray-200">
                                            {opts.map(opt => (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    title={opt.description || opt.desc}
                                                    onClick={() => update('existDiff', opt.value)}
                                                    className={`flex-1 min-w-[50px] py-2 text-[11px] font-bold transition-all ${Math.abs(parseFloat(estimate.existDiff || 0) - opt.value) < 0.001 ? 'bg-primary text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    )
                                })()}
                            </div>

                            {/* Remodelación */}
                            <div className="mb-4">
                                <div className="flex justify-between items-center mb-1.5">
                                    <p className="text-xs font-semibold text-gray-700">{t('admin.arch.add_alt_remodel')}</p>
                                    <span className="text-[11px] text-gray-400">eff: {calc.remEff.toLocaleString()} sqft</span>
                                </div>
                                <div className="flex gap-2 items-center mb-1">
                                    <input type="number" min="0" step="1" value={estimate.remSqft}
                                        onChange={e => update('remSqft', e.target.value)} placeholder="200"
                                        className="flex-1 px-3 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                                    <span className="text-gray-300">×</span>
                                    <input type="number" min="0" step="0.05" value={estimate.remDiff}
                                        onChange={e => update('remDiff', e.target.value)} placeholder="1.0"
                                        className="w-24 px-2.5 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                                </div>
                                {(() => {
                                    const opts = getOptionsForComponent('rem') || []
                                    if (opts.length === 0) return null
                                    return (
                                        <div className="flex overflow-hidden flex-wrap rounded-xl border border-gray-200">
                                            {opts.map(opt => (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    title={opt.description || opt.desc}
                                                    onClick={() => update('remDiff', opt.value)}
                                                    className={`flex-1 min-w-[50px] py-2 text-[11px] font-bold transition-all ${Math.abs(parseFloat(estimate.remDiff || 0) - opt.value) < 0.001 ? 'bg-primary text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    )
                                })()}
                            </div>

                            {/* Adición */}
                            <div className="mb-4">
                                <div className="flex justify-between items-center mb-1.5">
                                    <p className="text-xs font-semibold text-gray-700">{t('admin.arch.add_alt_adicion')}</p>
                                    <span className="text-[11px] text-gray-400">eff: {calc.addEff.toLocaleString()} sqft</span>
                                </div>
                                <div className="flex gap-2 items-center mb-1.5">
                                    <input type="number" min="0" step="1" value={estimate.addSqft}
                                        onChange={e => update('addSqft', e.target.value)} placeholder="300"
                                        className="flex-1 px-3 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                                    <span className="text-gray-300">×</span>
                                    <input type="number" min="0" step="0.05" value={estimate.addDiff}
                                        onChange={e => update('addDiff', e.target.value)} placeholder="1.0"
                                        className="w-24 px-2.5 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                                </div>
                                {(() => {
                                    const opts = getOptionsForComponent('add') || []
                                    if (opts.length === 0) return null
                                    return (
                                        <div className="flex overflow-hidden flex-wrap rounded-xl border border-gray-200">
                                            {opts.map(opt => (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    title={opt.description || opt.desc}
                                                    onClick={() => update('addDiff', opt.value)}
                                                    className={`flex-1 min-w-[50px] py-2 text-[11px] font-bold transition-all ${Math.abs(parseFloat(estimate.addDiff || 0) - opt.value) < 0.001 ? 'bg-primary text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    )
                                })()}
                            </div>

                            {/* Price preview */}
                            <div className="pt-3 border-t border-gray-100 space-y-1.5">
                                <div className="flex justify-between text-xs text-gray-500">
                                    <span>{t('admin.arch.add_alt_eff_sqft_total')}: {(calc.existEff + calc.remEff + calc.addEff).toLocaleString()} sqft</span>
                                    <span className="font-mono">${calc.addAltBaseRate}/sqft</span>
                                </div>
                                {calc.computedAddAlt < calc.activeMin && calc.activeMin > 0 && !calc.isMinOverridden && (
                                    <div className="flex items-center justify-between gap-2 px-2.5 py-1.5 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg">
                                        <div className="flex items-center gap-1.5">
                                            <FaExclamationTriangle size={10} className="shrink-0" />
                                            {t('admin.arch.add_alt_below_min_warning', { min: fmt(calc.activeMin) })}
                                        </div>
                                        <button
                                            onClick={openMinOverride}
                                            className="px-2 py-0.5 text-[10px] font-semibold text-white bg-amber-500 rounded hover:bg-amber-600 transition-all"
                                        >
                                            {t('admin.arch.override_min_button')}
                                        </button>
                                    </div>
                                )}
                                {calc.isMinOverridden && estimate.minPriceOverride && (
                                    <div className={`flex items-center justify-between gap-2 px-2.5 py-1.5 text-xs rounded-lg border ${estimate.minPriceOverride.type === 'disable' ? 'text-red-600 bg-red-50 border-red-200' : 'text-amber-600 bg-amber-50 border-amber-200'}`}>
                                        <div className="flex items-center gap-1.5">
                                            <FaExclamationTriangle size={10} className="shrink-0" />
                                            {estimate.minPriceOverride.type === 'disable'
                                                ? (t('admin.arch.override_min_badge_disabled'))
                                                : t('admin.arch.override_min_badge_custom', { value: fmt(estimate.minPriceOverride.value) })}
                                            {estimate.minPriceOverride.reason && (
                                                <span className="opacity-60">— {estimate.minPriceOverride.reason}</span>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => setEstimate(prev => ({ ...prev, minPriceOverride: null }))}
                                            className="px-2 py-0.5 text-[10px] font-semibold text-gray-600 bg-white rounded border border-gray-200 hover:bg-gray-50 transition-all"
                                            title={t('admin.arch.override_restore')}
                                        >
                                            <FaUndo size={8} className="inline" />
                                        </button>
                                    </div>
                                )}
                                <div className="flex justify-between items-center">
                                    <span className="text-xs text-gray-500">
                                        {calc.isMinOverridden && estimate.minPriceOverride?.type === 'custom'
                                            ? t('admin.arch.add_alt_min_floor') + ': $' + fmt(estimate.minPriceOverride.value)
                                            : t('admin.arch.add_alt_min_floor') + ': $' + fmt(calc.activeMin)}
                                    </span>
                                    <span className={`text-base font-bold ${calc.computedAddAlt < calc.activeMin && calc.activeMin > 0 && !calc.isMinOverridden ? 'text-amber-600' : 'text-primary'}`}>
                                        ${fmt(calc.salePrice)}
                                    </span>
                                </div>
                            </div>
                        </>
                    ) : (
                        /* ── Existing market / area / difficulty UI ── */
                        <>
                            <div className="mb-4">
                                <label className="block mb-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wide">{t('admin.arch.market')}</label>
                                {(() => {
                                    const isMondayPreselected = !!estimate.mondayItemId
                                    const selectedMarket = marketOptions.find(m => m.key === estimate.market)
                                    const otherMarkets = marketOptions.filter(m => m.key !== estimate.market)
                                    if (isMondayPreselected && selectedMarket) {
                                        return (
                                            <>
                                                <div className="flex gap-2 items-stretch">
                                                    <button
                                                        onClick={() => update('market', selectedMarket.key)}
                                                        className="flex-1 p-3 text-xs font-semibold text-left text-white rounded-xl border transition-all border-primary bg-primary"
                                                    >
                                                        <span className="block text-[10px] uppercase tracking-wider opacity-60">{selectedMarket.state}{selectedMarket.type ? ` — ${selectedMarket.type}` : ''}</span>
                                                        <span>{t(selectedMarket.labelKey) || selectedMarket.key}</span>
                                                        <span className="block mt-0.5 text-[11px] font-mono opacity-70">${calc.cfg?.base_price_sqft ?? '…'}/sqft</span>
                                                    </button>
                                                    {otherMarkets.length > 0 && (
                                                        <button
                                                            onClick={() => setShowMoreMarkets(v => !v)}
                                                            className="flex justify-center items-center px-3 text-gray-400 rounded-xl border border-gray-200 transition-all shrink-0 hover:border-gray-300 hover:bg-gray-50"
                                                            title={t('admin.arch.more_markets')}
                                                        >
                                                            {showMoreMarkets ? '▲' : '▼'}
                                                        </button>
                                                    )}
                                                </div>
                                                {otherMarkets.length > 0 && (
                                                    <AnimatePresence initial={false}>
                                                        {showMoreMarkets && (
                                                            <motion.div
                                                                key="more-markets"
                                                                initial={{ height: 0, opacity: 0 }}
                                                                animate={{ height: 'auto', opacity: 1 }}
                                                                exit={{ height: 0, opacity: 0 }}
                                                                className="overflow-hidden"
                                                            >
                                                                <div className="grid grid-cols-2 gap-2 mt-2">
                                                                    {otherMarkets.map(m => (
                                                                        <button key={m.key} onClick={() => update('market', m.key)}
                                                                            className={`p-3 rounded-xl border text-xs font-semibold transition-all text-left ${estimate.market === m.key ? 'border-primary bg-primary text-white' : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'}`}
                                                                        >
                                                                            <span className="block text-[10px] uppercase tracking-wider opacity-60">{m.state}{m.type ? ` — ${m.type}` : ''}</span>
                                                                            <span>{t(m.labelKey) || m.key}</span>
                                                                            <span className="block mt-0.5 text-[11px] font-mono opacity-70">${calc.cfg?.base_price_sqft ?? '…'}/sqft</span>
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                )}
                                            </>
                                        )
                                    }
                                    return (
                                        <div className="grid grid-cols-2 gap-2">
                                            {marketOptions.map(m => (
                                                <button key={m.key} onClick={() => update('market', m.key)}
                                                    className={`p-3 rounded-xl border text-xs font-semibold transition-all text-left ${estimate.market === m.key ? 'border-primary bg-primary text-white' : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'}`}
                                                >
                                                    <span className="block text-[10px] uppercase tracking-wider opacity-60">{m.state}{m.type ? ` — ${m.type}` : ''}</span>
                                                    <span>{t(m.labelKey) || m.key}</span>
                                                    <span className="block mt-0.5 text-[11px] font-mono opacity-70">${calc.cfg?.base_price_sqft ?? '…'}/sqft</span>
                                                </button>
                                            ))}
                                        </div>
                                    )
                                })()}
                            </div>
                            <div className="mb-4">
                                <label className="block mb-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wide">{t('admin.arch.area')}</label>
                                <div className="flex overflow-hidden mb-2 rounded-xl border border-gray-200">
                                    {[{v:'direct',l:t('admin.arch.area_direct')},{v:'lw',l:t('admin.arch.area_lw')}].map(opt => (
                                        <button key={opt.v} onClick={() => update('useArea', opt.v)}
                                            className={`flex-1 py-2.5 text-xs font-bold transition-all ${estimate.useArea === opt.v ? 'bg-primary text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>{opt.l}</button>
                                    ))}
                                </div>
                                {estimate.useArea === 'direct' ? (
                                    <div className="flex gap-2 items-center">
                                        <input type="number" min="0" step="1" value={estimate.sqft} onChange={e => update('sqft', e.target.value)} placeholder="3000" className="flex-1 px-3 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                                        <span className="text-xs text-gray-400 shrink-0">sqft</span>
                                    </div>
                                ) : (
                                    <div className="flex gap-2 items-center">
                                        <input type="number" min="0" step="0.1" value={estimate.lengthFt} onChange={e => update('lengthFt', e.target.value)} placeholder={t('admin.arch.length_placeholder')} className="flex-1 px-3 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                                        <span className="text-gray-300">×</span>
                                        <input type="number" min="0" step="0.1" value={estimate.widthFt} onChange={e => update('widthFt', e.target.value)} placeholder={t('admin.arch.width_placeholder')} className="flex-1 px-3 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                                        <span className="text-xs text-gray-400 shrink-0">= {Number(calc.sqft).toLocaleString()} sqft</span>
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="block mb-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wide">{t('admin.arch.difficulty')}</label>
                                {(() => {
                                    const lvls = effectiveDiffLevels
                                    if (lvls.length === 0) return null
                                    return (
                                        <div className="flex overflow-hidden flex-wrap rounded-xl border border-gray-200">
                                            {lvls.map(lvl => (
                                                <button
                                                    key={lvl.value}
                                                    onClick={() => update('difficulty', lvl.value)}
                                                    title={t(lvl.descKey) || lvl.desc || ''}
                                                    className={`flex-1 min-w-[60px] py-2.5 text-xs font-bold transition-all ${estimate.difficulty === lvl.value ? 'bg-primary text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                                                >
                                                    {t(lvl.labelKey) || lvl.label || ''}
                                                </button>
                                            ))}
                                        </div>
                                    )
                                })()}
                            </div>

                            {/* ── Civil Engineering Split (new construction + civil deliverables) ── */}
                            {calc.useCivilSplit && (
                                <div className="mt-4 p-3.5 rounded-xl border border-indigo-200 bg-indigo-50">
                                    <button
                                        type="button"
                                        onClick={() => setCivilBlockOpen(o => !o)}
                                        className="flex gap-2 items-center w-full text-left"
                                    >
                                        <span className="text-[10px] text-indigo-500 transition-transform">
                                            {civilBlockOpen ? '▼' : '▶'}
                                        </span>
                                        <h3 className="text-xs font-bold tracking-wide text-indigo-800 uppercase">
                                            {t('admin.arch.civil_split_title')}
                                        </h3>
                                        <span className="ml-auto text-[10px] text-indigo-400">
                                            {civilBlockOpen ? '' : `$${fmt(calc.civilPrice)}`}
                                        </span>
                                    </button>

                                    <AnimatePresence initial={false}>
                                        {civilBlockOpen && (
                                            <motion.div
                                                key="civil-body"
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.2 }}
                                                className="overflow-hidden"
                                            >
                                                <div className="pt-2.5">
                                                    {/* Market badge */}
                                                    <div className="flex gap-2 items-center px-3 py-2 mb-3 bg-white rounded-xl border border-indigo-200">
                                                        <FaMapMarkerAlt size={10} className="text-indigo-500 shrink-0" />
                                                        <span className="text-xs font-semibold text-indigo-700">
                                                            {(() => {
                                                                const cm = marketOptions.find(m => m.key === getCivilMarket(estimate.market))
                                                                return cm ? `${cm.state} — ${t(cm.labelKey) || cm.key}` : getCivilMarket(estimate.market)
                                                            })()}
                                                        </span>
                                                        <span className="ml-auto text-[11px] font-mono text-indigo-500 shrink-0">
                                                            ${calc.civilCfg?.base_price_sqft ?? '…'}/sqft
                                                        </span>
                                                    </div>

                                                    {/* Sqft row */}
                                                    <div className="mb-3">
                                                        <div className="flex justify-between items-center mb-1.5">
                                                            <p className="text-xs font-semibold text-gray-700">{t('admin.arch.civil_sqft_label')}</p>
                                                            {estimate.civilSqft == null && (
                                                                <span className="text-[10px] text-indigo-400">{t('admin.arch.same_as_main')}</span>
                                                            )}
                                                        </div>
                                                        <div className="flex gap-2 items-center">
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="1"
                                                                value={estimate.civilSqft ?? calc.sqft}
                                                                onChange={e => update('civilSqft', Number(e.target.value))}
                                                                className="flex-1 px-3 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => update('civilSqft', estimate.civilSqft != null ? null : calc.sqft)}
                                                                className={`flex justify-center items-center shrink-0 h-9 w-9 rounded-lg border transition-colors ${estimate.civilSqft != null ? 'border-indigo-200 bg-white text-indigo-600 hover:bg-indigo-100' : 'border-gray-200 bg-gray-50 text-gray-400 hover:bg-gray-100'}`}
                                                                title={t('admin.arch.same_as_main')}
                                                                aria-label={t('admin.arch.same_as_main')}
                                                            >
                                                                <FaLink size={12} />
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Difficulty row */}
                                                    <div className="mb-3">
                                                        <div className="flex justify-between items-center mb-1.5">
                                                            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">{t('admin.arch.civil_difficulty_label')}</label>
                                                            {estimate.civilDifficulty == null && (
                                                                <span className="text-[10px] text-indigo-400">{t('admin.arch.same_as_main')}</span>
                                                            )}
                                                        </div>
                                                        <div className="flex gap-2 items-center">
                                                            <div className="flex-1">
                                                                {(civilLevels || []).length > 0 && (
                                                                    <div className="flex overflow-hidden flex-wrap rounded-xl border border-gray-200">
                                                                        {(civilLevels || []).map(lvl => (
                                                                            <button
                                                                                key={lvl.value}
                                                                                onClick={() => update('civilDifficulty', lvl.value)}
                                                                                title={t(lvl.descKey) || lvl.desc || ''}
                                                                                className={`flex-1 min-w-[50px] py-2 text-[11px] font-bold transition-all ${(estimate.civilDifficulty ?? estimate.difficulty) === lvl.value ? 'bg-primary text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                                                                            >
                                                                                {t(lvl.labelKey) || lvl.label || ''}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => update('civilDifficulty', estimate.civilDifficulty != null ? null : estimate.difficulty)}
                                                                className={`flex justify-center items-center shrink-0 h-9 w-9 rounded-lg border transition-colors ${estimate.civilDifficulty != null ? 'border-indigo-200 bg-white text-indigo-600 hover:bg-indigo-100' : 'border-gray-200 bg-gray-50 text-gray-400 hover:bg-gray-100'}`}
                                                                title={t('admin.arch.same_as_main')}
                                                                aria-label={t('admin.arch.same_as_main')}
                                                            >
                                                                <FaLink size={12} />
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Civil discipline split */}
                                                    <div className="grid grid-cols-2 gap-3 mb-3">
                                                        <div>
                                                            <label className="block mb-1 text-[11px] text-gray-500">{t('admin.arch.disc_civil')}</label>
                                                            <div className="flex gap-1 items-center">
                                                                <input type="number" min="0" max="100" step="1" value={estimate.civilPct} onChange={e => {
                                                                    const v = Math.max(0, Math.min(100, Math.round(Number(e.target.value) || 0)))
                                                                    setEstimate(prev => ({ ...prev, civilPct: v, civilAdminPct: 100 - v }))
                                                                }} className="w-full px-2 py-1.5 text-sm bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20" />
                                                                <span className="text-xs text-gray-400">%</span>
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <label className="block mb-1 text-[11px] text-gray-500">{t('admin.arch.disc_civil_admin')}</label>
                                                            <div className="flex gap-1 items-center">
                                                                <input type="number" min="0" max="100" step="1" value={estimate.civilAdminPct} onChange={e => {
                                                                    const v = Math.max(0, Math.min(100, Math.round(Number(e.target.value) || 0)))
                                                                    setEstimate(prev => ({ ...prev, civilAdminPct: v, civilPct: 100 - v }))
                                                                }} className="w-full px-2 py-1.5 text-sm bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20" />
                                                                <span className="text-xs text-gray-400">%</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {(() => {
                                                        const civilSplitSum = (estimate.civilPct || 0) + (estimate.civilAdminPct || 0)
                                                        const civilSplitOk = Math.abs(civilSplitSum - 100) < 0.01
                                                        return (
                                                            <p className={`mb-3 text-xs font-semibold ${civilSplitOk ? 'text-emerald-500' : 'text-amber-500'}`}>
                                                                {civilSplitOk ? t('admin.arch.pct_ok') : t('admin.arch.pct_warn', { sum: civilSplitSum })}
                                                            </p>
                                                        )
                                                    })()}

                                                    {/* Civil portion total */}
                                                    {calc.civilPrice > 0 && (
                                                        <div className="flex justify-between items-center pt-2.5 border-t border-indigo-200/60">
                                                            <span className="text-[11px] text-indigo-600 font-medium">
                                                                {t('admin.arch.civil_portion')}
                                                            </span>
                                                            <span className="text-sm font-bold text-indigo-800">
                                                                ${fmt(calc.civilPrice)}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            )}
                        </>
                    )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
                </section>
            )
            case 'discipline_split': {
                const isNewConstrType = NEW_CONSTRUCTION_TYPES.has(estimate.projectType)
                const showCivil = !['res_add_alt', 'res_rem', 'com_add_alt', 'com_rem'].includes(estimate.projectType)
                    && civilManualOverride === true
                const scopeOptions = showCivil ? DISCIPLINE_SCOPE_OPTIONS : DISCIPLINE_SCOPE_OPTIONS.filter(o => !o.key.includes('civil'))
                return (
                <section className="overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-sm">
                <div className="h-[3px] bg-amber-500" />
                <div className="p-5">
                    <h2 className="flex gap-2 items-center mb-4 text-sm font-bold text-gray-700">
                        <FaPercentage size={12} className="text-primary" />
                        {t('admin.arch.disciplines')}
                    </h2>
                    {/* Civil toggle — only for new construction types (not civil-only projects) */}
                    {isNewConstrType && estimate.projectType !== 'civil' && (
                        <div className="flex overflow-hidden mb-4 rounded-xl border border-gray-200">
                            <button
                                type="button"
                                onClick={() => setCivilManualOverride(false)}
                                className={`flex-1 py-2.5 text-xs font-bold transition-all ${!civilManualOverride ? 'bg-primary text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                            >
                                {t('admin.arch.main_only')}
                            </button>
                            <button
                                type="button"
                                onClick={() => setCivilManualOverride(true)}
                                className={`flex-1 py-2.5 text-xs font-bold transition-all ${civilManualOverride ? 'bg-primary text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                            >
                                {t('admin.arch.include_civil')}
                            </button>
                        </div>
                    )}
                    {/* NOTE: Preset scope buttons hidden (Jul 2026) — they set disciplineScope manually,
                        which conflicts with percentage distribution and deliverable-driven field visibility.
                        The suggestion banner (below) already auto-proposes the right preset from scopeItems.
                    */}
                    {/* <div className="grid grid-cols-3 gap-1.5 mb-4 sm:grid-cols-5">
                        {scopeOptions.map(opt => (
                            <button key={opt.key} onClick={() => {
                                const pct = dbDisciplinePresets[opt.key] || SCOPE_OPTION_TO_PCT[opt.key]
                                if (pct && onScopeChange) {
                                    onScopeChange(opt.key, pct)
                                } else {
                                    update('disciplineScope', opt.key)
                                }
                            }}
                                className={`flex items-center justify-center py-1.5 px-1.5 text-[10px] font-bold rounded-lg border transition-all leading-tight text-center min-h-[36px] ${estimate.disciplineScope === opt.key ? 'bg-primary text-white border-primary' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}>{t(opt.labelKey)}</button>
                        ))}
                    </div> */}
                    <div className={`grid grid-cols-2 gap-3 ${calc.useCivilSplit ? 'sm:grid-cols-3' : showCivil ? 'sm:grid-cols-3' : 'sm:grid-cols-3'}`}>
                        {(() => {
                            const scope = estimate.disciplineScope || 'full'
                            const isCivilProj = estimate.projectType === 'civil'
                            const fields = []
                            const showArch = !isCivilProj && (scope === 'full' || scope.includes('arch'))
                            const showStruct = !isCivilProj && (scope === 'full' || scope.includes('struct'))
                            const showMep = !isCivilProj && (scope === 'full' || scope.includes('mep'))
                            const showCivilField = showCivil && (scope === 'full' || scope.includes('civil'))
                            if (showArch) {
                                fields.push({ field: 'archPct', label: !showStruct && !showMep && !showCivilField ? t('admin.arch.disc_arch_only') : t('admin.arch.disc_arch') })
                            }
                            if (showStruct) fields.push({ field: 'structPct', label: t('admin.arch.disc_struct') })
                            if (showMep) {
                                fields.push({ field: 'mechPct', label: t('admin.arch.disc_mech') })
                                fields.push({ field: 'elecPct', label: t('admin.arch.disc_elec') })
                                fields.push({ field: 'plumbPct', label: t('admin.arch.disc_plumb') })
                            }
                            if (showCivilField) {
                                if (calc.useCivilSplit) {
                                    fields.push({ field: 'civilPct', label: t('admin.arch.disc_civil') })
                                    fields.push({ field: 'civilAdminPct', label: t('admin.arch.disc_civil_admin') })
                                } else {
                                    fields.push({ field: 'civilPct', label: t('admin.arch.disc_civil') })
                                }
                            }
                            if (!isCivilProj) {
                                fields.push({ field: 'adminPct', label: t('admin.arch.disc_pm') })
                            }
                            return fields
                        })().map(({ field, label }) => {
                            const isDeducted = (estimate.disciplineDeductions || []).some(d => d.field === field)
                            const isPending  = pendingDeduct?.field === field
                            return (
                            <div key={field}>
                                <label className="block mb-1 text-[11px] text-gray-500">{label}</label>
                                <div className="flex gap-1 items-center">
                                    <input
                                        type="number" min="0" max="100" step="1"
                                        value={estimate[field]}
                                        onChange={e => handleDisciplinePctChange(field, Number(e.target.value))}
                                        disabled={isDeducted}
                                        className={`w-full px-2 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${isDeducted ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'border-gray-200'}`}
                                    />
                                    <span className="text-xs text-gray-400">%</span>
                                </div>
                                {isDeducted && (
                                    <div className="flex justify-between items-center mt-1 px-1.5 py-1 bg-red-50 rounded-lg border border-red-100">
                                        <span className="text-[10px] text-red-400 line-through">{label}</span>
                                        <button
                                            onClick={() => handleRestoreDeduction(field, (estimate.disciplineDeductions || []).find(d => d.field === field)?.savedPct || 0)}
                                            className="text-[10px] font-semibold text-primary hover:underline"
                                        >
                                            {t('admin.arch.restore')}
                                        </button>
                                    </div>
                                )}
                                {isPending && (
                                    <div className="flex gap-1 mt-1">
                                        <button
                                            onClick={() => setPendingDeduct(null)}
                                            className="flex-1 px-2 py-1 text-[10px] font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                        >
                                            ↕ {t('admin.arch.deduct_redistribute')}
                                        </button>
                                        <button
                                            onClick={() => handleConfirmDeduct(label)}
                                            className="flex-1 px-2 py-1 text-[10px] font-semibold text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                                        >
                                            🗑 −${fmt(pendingDeduct.amount)}
                                        </button>
                                    </div>
                                )}
                            </div>
                            )
                        })}
                    </div>
                    <div className="flex gap-2 items-center mt-2">
                        <p className={`text-xs font-semibold ${pctSumOk ? 'text-emerald-500' : 'text-amber-500'}`}>
                            {calc.useCivilSplit && scopeIncludesCivilTab
                                ? (mainPctOk && civilPctOk
                                    ? t('admin.arch.pct_ok')
                                    : t('admin.arch.pct_warn_split', { main: mainPctSum, civil: civilPctSumLocal }))
                                : (pctSumOk ? t('admin.arch.pct_ok') : t('admin.arch.pct_warn', { sum: disciplinePctSum }))}
                        </p>
                        {!pctSumOk && (
                            <button
                                onClick={balanceDisciplinePcts}
                                className="px-2.5 py-1 text-[10px] font-bold text-white bg-amber-500 rounded-lg transition-colors hover:bg-amber-600 shrink-0"
                            >
                                {t('admin.arch.pct_balance')}
                            </button>
                        )}
                    </div>
                    {disciplineSuggestion && (
                        <div className="flex flex-wrap gap-2 justify-between items-center p-3 mt-3 bg-amber-50 rounded-xl border border-amber-200">
                            <div className="flex gap-2 items-center text-xs text-amber-700">
                                <FaExclamationTriangle size={11} className="shrink-0" />
                                <span className="font-semibold">
                                    {t('admin.arch.suggestion_label')}: {disciplineSuggestion.archPct}/{disciplineSuggestion.structPct}/{disciplineSuggestion.mepPct}/{calc.isAddAlt ? 0 : disciplineSuggestion.civilPct}/{disciplineSuggestion.adminPct}
                                    <span className="ml-1 font-normal opacity-70">({t(DISCIPLINE_SCOPE_OPTIONS.find(s => s.key === disciplineSuggestion.scope)?.labelKey) || disciplineSuggestion.scope})</span>
                                </span>
                            </div>
                            <div className="flex gap-1.5">
                                <button onClick={onApplySuggestion} className="px-3 py-1 text-xs font-bold text-white bg-amber-500 rounded-lg transition-colors hover:bg-amber-600">{t('admin.arch.suggestion_apply')}</button>
                                <button onClick={onDismissSuggestion} className="p-1 text-amber-400 rounded-lg transition-colors hover:bg-amber-100 hover:text-amber-600"><FaTimes size={10} /></button>
                            </div>
                        </div>
                    )}
                    <div className="pt-3 mt-4 border-t border-gray-200">
                        <div className="flex justify-between items-center py-2">
                            <button onClick={() => setCatSearchOpen(!catSearchOpen)} className="flex gap-2 items-center text-sm font-bold text-gray-700 transition-all hover:text-gray-900">
                                <FaTag size={11} className="text-primary" />
                                {t('admin.arch.catalog.scope_title')}
                                {visibleScopeItems.length > 0 && <span className="ml-1 px-2 py-0.5 bg-primary/10 text-primary text-xs font-bold rounded-full">{visibleScopeItems.length}</span>}
                                {catSearchOpen ? <FaChevronUp size={10} className="ml-1 text-gray-400" /> : <FaChevronDown size={10} className="ml-1 text-gray-400" />}
                            </button>
                            {estimate.projectType && (
                                <div className="inline-flex rounded-lg bg-gray-100 p-0.5 gap-0.5 shrink-0">
                                    <button onClick={() => onSwitchPhase('cds')}
                                        className={`px-2.5 py-1 text-[11px] font-bold rounded-md transition-all ${autoPhase === 'cds' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                    >CDs</button>
                                    <button onClick={() => onSwitchPhase('design')}
                                        className={`px-2.5 py-1 text-[11px] font-bold rounded-md transition-all ${autoPhase === 'design' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                    >Design</button>
                                </div>
                            )}
                        </div>
                        {undoScopeSnapshot !== null && (
                            <div className="flex gap-2 justify-between items-center px-3 py-2 mt-1 rounded-lg border border-amber-200 bg-amber-50/80">
                                <span className="text-xs text-amber-700">{t('admin.arch.auto_populated', { count: scopeItems.length })}</span>
                                <div className="flex gap-1.5">
                                    <button onClick={onUndoScope} className="px-2 py-0.5 text-[11px] font-bold text-amber-600 rounded transition-colors hover:bg-amber-100">{t('admin.arch.undo')}</button>
                                    <button onClick={onDismissUndo} className="p-0.5 text-amber-400 transition-colors hover:text-amber-600"><FaTimes size={9} /></button>
                                </div>
                            </div>
                        )}
                        <AnimatePresence>
                            {catSearchOpen && (
                                <motion.div key="catpanel" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.18 }} className="overflow-hidden">
                                    <div className="pt-2">
                                        {visibleScopeItems.length > 0 && (
                                            <div className="mb-3 space-y-1.5">
                                                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-2">{t('admin.arch.catalog.selected')}</p>
                                                {visibleScopeItems.map(item => {
                                                    const discColor = DISC_COLORS[normalizeDiscipline(item.discipline)] || 'bg-gray-100 text-gray-600 border-gray-200'
                                                    const isDragging = draggedScopeId === item.id
                                                    const isOver = dragOverScopeId === item.id
                                                    const itemIsAddon = isAddonItem(item, addonDefs)
                                                    return (
                                                        <div
                                                            key={item.id}
                                                            draggable
                                                            onDragStart={e => handleScopeDragStart(e, item.id)}
                                                            onDragOver={e => handleScopeDragOver(e, item.id)}
                                                            onDragLeave={handleScopeDragLeave}
                                                            onDrop={e => handleScopeDrop(e, item.id)}
                                                            onDragEnd={handleScopeDragEnd}
                                                            className={`flex gap-2 items-center px-3 py-2 rounded-xl border transition-all ${isDragging ? 'opacity-40' : 'opacity-100'} ${isOver ? 'border-t-2 border-t-primary bg-primary/5' : 'bg-primary/5 border-primary/10'}`}
                                                        >
                                                            <span className="text-gray-300 cursor-grab active:cursor-grabbing shrink-0"><FaGripVertical size={11} /></span>
                                                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold border shrink-0 ${discColor}`}>{item.discipline || 'GEN'}</span>
                                                            {itemIsAddon && <span className="px-1 py-0.5 rounded text-[9px] font-bold bg-violet-100 text-violet-600 shrink-0">ADD</span>}
                                                            <span className="flex-1 text-sm text-gray-800 truncate">{item.name}</span>
                                                            {item.subcat && <span className="text-[10px] text-gray-400 shrink-0">{item.subcat}</span>}
                                                            <button onClick={() => removeScopeItem(item.id)} className="text-gray-300 transition-colors shrink-0 hover:text-red-400"><FaTimesCircle size={13} /></button>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        )}
                                        <div className="grid grid-cols-1 gap-2 mt-2 sm:grid-cols-2">
                                            <div className="relative">
                                                <select value={catPhase} onChange={e => setCatPhase(e.target.value)} className="w-full px-2.5 py-1.5 pr-7 text-xs rounded-xl border border-gray-200 bg-white text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary/30">
                                                    <option value="">{t('admin.arch.catalog.all_phases')}</option>
                                                    {ARCH_PHASES.map(p => <option key={p.key} value={p.key}>{t(p.labelKey)}</option>)}
                                                </select>
                                                <FaChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" size={9} />
                                            </div>
                                            <div className="relative">
                                                <select value={catProjectType} onChange={e => setCatProjectType(e.target.value)} className="w-full px-2.5 py-1.5 pr-7 text-xs rounded-xl border border-gray-200 bg-white text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary/30">
                                                    <option value="">{t('admin.arch.catalog.all_project_types')}</option>
                                                    {ARCH_PROJECT_TYPES.map(p => <option key={p.key} value={p.key}>{t(p.labelKey)}</option>)}
                                                </select>
                                                <FaChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" size={9} />
                                            </div>
                                        </div>
                                        <div className="relative mt-2">
                                            <FaSearch size={11} className="absolute left-3 top-1/2 text-gray-300 -translate-y-1/2 pointer-events-none" />
                                            <input value={catSearch} onChange={e => setCatSearch(e.target.value)} placeholder={t('admin.arch.catalog.search_placeholder')} className="py-2 pr-3 pl-8 w-full text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                                        </div>
                                        {(catalogItems || []).length === 0 && <p className="mt-4 text-sm italic text-center text-gray-400">{t('admin.arch.catalog.empty_constructor')}</p>}
                                        {filteredCatalog.length > 0 && (
                                            <div className="overflow-y-auto pr-1 mt-3 space-y-1 max-h-72">
                                                {(() => {
                                                    const discOrder = ['ARCH', 'STRUCT', 'MEP', '3D', 'CIVIL', 'ECC', 'ADMIN']
                                                    const grouped = {}
                                                    for (const item of filteredCatalog) {
                                                        const d = normalizeDiscipline(item.discipline)
                                                        if (!grouped[d]) grouped[d] = []
                                                        grouped[d].push(item)
                                                    }
                                                    for (const d in grouped) grouped[d].sort((a, b) => a.name.localeCompare(b.name))
                                                    return discOrder.filter(d => grouped[d]?.length > 0).map(d => {
                                                        const discColor = DISC_COLORS[d] || 'bg-gray-100 text-gray-600 border-gray-200'
                                                        return (
                                                            <div key={d}>
                                                                <div className={`sticky top-0 z-10 flex items-center gap-1.5 px-2 py-1 mb-1 mt-2 text-[10px] font-bold uppercase tracking-wider rounded-md ${discColor}`}>
                                                                    {d}
                                                                </div>
                                                                <div className="space-y-1">
                                                                    {grouped[d].map(item => {
                                                                        const isSelected = selectedIds.has(item.id)
                                                                        return (
                                                                            <button key={item.id} onClick={() => isSelected ? removeScopeItem(item.id) : addScopeItem(item)}
                                                                                className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl border text-left transition-all ${isSelected ? 'opacity-60 border-primary/30 bg-primary/5' : 'border-gray-100 hover:border-primary/20 hover:bg-gray-50'}`}
                                                                            >
                                                                                {item.phase && (
                                                                                    <span className={`px-1 py-0.5 rounded text-[9px] font-bold border shrink-0 ${item.phase === 'cds' ? 'bg-slate-100 text-slate-500 border-slate-200' : 'bg-indigo-50 text-indigo-400 border-indigo-200'}`}>{item.phase === 'cds' ? 'CDs' : 'DES'}</span>
                                                                                )}
                                                                                <span className="flex-1 text-sm text-gray-700 truncate">{item.name}</span>
                                                                                {item.subcat && <span className="text-[10px] text-gray-400 shrink-0 truncate max-w-[120px]">{item.subcat}</span>}
                                                                                {isSelected ? <FaCheck size={10} className="text-primary shrink-0" /> : <FaPlus size={10} className="text-gray-300 shrink-0" />}
                                                                            </button>
                                                                        )
                                                                    })}
                                                                </div>
                                                            </div>
                                                        )
                                                    })
                                                })()}
                                            </div>
                                        )}
                                        {filteredCatalog.length === 0 && (catalogItems || []).length > 0 && (
                                            <p className="py-3 mt-4 text-sm italic text-center text-gray-400">{t('admin.arch.catalog.no_results')}</p>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
                </section>
            )}
            case 'price_summary': return (
                <section className="overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-sm">
                <div className="h-[3px] bg-amber-500" />
                <div className="p-5">
                    <h2 className="flex gap-2 items-center mb-4 text-sm font-bold text-gray-700">
                        <FaMoneyBillWave size={12} className="text-primary" />
                        {t('admin.arch.price_summary')}
                    </h2>
                    <div className="space-y-2">
                        <div className="flex justify-between items-start py-1.5 border-b border-gray-100">
                            {calc.isAddAlt ? (
                                <span className="text-sm leading-relaxed text-gray-600">
                                    {[
                                        calc.existEff > 0 && `Exist ${calc.existEff} eff-sqft×$${calc.existBaseRate}`,
                                        calc.remEff   > 0 && `Rmd ${calc.remEff} eff-sqft×$${calc.remBaseRate}`,
                                        calc.addEff   > 0 && `Add ${calc.addEff} eff-sqft×$${calc.addBaseRate}`,
                                    ].filter(Boolean).join(' + ') || '—'}
                                    {calc.computedAddAlt < calc.activeMin && calc.activeMin > 0 && (
                                        <span className="ml-1 text-amber-500">→ MIN ${fmt(calc.activeMin)}</span>
                                    )}
                                </span>
                            ) : (
                                <span className="text-sm text-gray-600">
                                    {calc.sqft.toLocaleString()} sqft × ${calc.cfg?.base_price_sqft ?? 0}/sqft
                                    × {estimate.difficulty} ({(d => t(d?.labelKey) || d?.label || '')((effectiveDiffLevels).find(l => Math.abs(l.value - estimate.difficulty) < 0.001))})
                                    × {calc.scopeMult} ({t(DISCIPLINE_SCOPE_OPTIONS.find(s => s.key === estimate.disciplineScope)?.labelKey) || ''})
                                </span>
                            )}
                            <span className="ml-2 text-sm font-bold text-gray-800 shrink-0">${fmt(calc.salePrice)}</span>
                        </div>
                        {[
                            { label: (estimate.structPct || 0) + (estimate.mechPct || 0) + (estimate.elecPct || 0) + (estimate.plumbPct || 0) + (calc.isAddAlt ? 0 : (estimate.civilPct || 0)) === 0 ? t('admin.arch.disc_arch_only') : t('admin.arch.disc_arch'), val: calc.archAmt,   pct: estimate.archPct   },
                            { label: t('admin.arch.disc_struct'), val: calc.structAmt, pct: estimate.structPct },
                            { label: t('admin.arch.disc_mech'),   val: calc.mechAmt,   pct: estimate.mechPct   },
                            { label: t('admin.arch.disc_elec'),   val: calc.elecAmt,   pct: estimate.elecPct   },
                            { label: t('admin.arch.disc_plumb'),  val: calc.plumbAmt,  pct: estimate.plumbPct  },
                            // Omit civil for Add/Alt/Rem — the discipline split UI hides it and
                            // calc.civilAmt is forced to $0 for these project types.
                            ...(!calc.isAddAlt ? [{ label: t('admin.arch.disc_civil'),  val: calc.civilAmt,  pct: estimate.civilPct  }] : []),
                            { label: t('admin.arch.disc_pm'),     val: calc.adminAmt,  pct: estimate.adminPct  },
                            ...(calc.useCivilSplit ? [{ label: t('admin.arch.disc_civil_admin'), val: calc.civilAdminAmt, pct: estimate.civilAdminPct }] : []),
                        ].filter(row => Number(row.pct) > 0).map(row => (
                            <div key={row.label} className="flex justify-between items-center text-sm">
                                <span className="text-gray-500">{row.label} <span className="text-xs opacity-60">({row.pct}%)</span></span>
                                <span className="font-semibold text-gray-700">${fmt(row.val)}</span>
                            </div>
                        ))}
                        {calc.useCivilSplit && calc.mainPrice > 0 && (
                            <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                                <span className="text-xs font-semibold text-gray-600">{t('admin.arch.subtotal_building')}</span>
                                <span className="text-sm font-bold text-gray-800">${fmt(calc.mainPrice)}</span>
                            </div>
                        )}
                        {calc.useCivilSplit && calc.civilPrice > 0 && (
                            <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                                <span className="text-xs font-semibold text-gray-600">{t('admin.arch.subtotal_civil')}</span>
                                <span className="text-sm font-bold text-gray-800">${fmt(calc.civilPrice)}</span>
                            </div>
                        )}
                        {(estimate.disciplineDeductions || []).length > 0 && (
                            <div className="pt-2 mt-1 space-y-1 border-t border-red-100">
                                {(estimate.disciplineDeductions || []).map(d => (
                                    <div key={d.field} className="flex justify-between items-center text-sm">
                                        <span className="flex gap-1.5 items-center text-red-400">
                                            <span className="line-through">{d.label}</span>
                                            <button
                                                onClick={() => handleRestoreDeduction(d.field, d.savedPct)}
                                                className="text-[10px] font-semibold text-primary no-underline hover:underline ml-1"
                                            >
                                                {t('admin.arch.restore')}
                                            </button>
                                        </span>
                                        <span className="font-semibold text-red-500">−${fmt(d.amount)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="mt-4 space-y-2">
                        <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase">{t('admin.arch.addons')}</p>
                        {estimate.addons.some(a => a._orphan || !addonDefs.find(d => d.key === a.key)) && (
                            <div className="flex gap-2 items-center p-2 text-xs text-amber-800 bg-amber-50 rounded-lg border border-amber-200">
                                <FaExclamationTriangle size={12} className="shrink-0" />
                                <span>{t('admin.arch.addon_orphan_banner')}</span>
                            </div>
                        )}
                        <DndContext sensors={addonSensors} collisionDetection={closestCenter} onDragEnd={(event) => {
                            const { active, over } = event
                            if (!over || active.id === over.id) return
                            const fromIndex = estimate.addons.findIndex(a => a.key === active.id)
                            const toIndex = estimate.addons.findIndex(a => a.key === over.id)
                            if (fromIndex !== -1 && toIndex !== -1) moveAddon(fromIndex, toIndex)
                        }}>
                            <SortableContext items={estimate.addons.map(a => a.key)} strategy={verticalListSortingStrategy}>
                                {estimate.addons.map(addon => {
                                    const def = addonDefs.find(d => d.key === addon.key)
                                    const isOrphan = addon._orphan || !def
                                    const matchedIds = def && !isOrphan ? getAddonScopeItemIds(def, catalogItems, estimate.projectType, autoPhase || 'cds') : new Set()
                                    const matchedItems = catalogItems.filter(ci => matchedIds.has(ci.id))
                                    return (
                                        <SortableAddonRow key={addon.key} id={addon.key}>
                                            <div className={`space-y-1 ${isOrphan ? 'opacity-60' : ''}`}>
                                                <div className="flex gap-3 items-center">
                                                    <button onClick={() => toggleAddon(addon.key)} className="shrink-0" disabled={isOrphan}>
                                                        {addon.enabled ? <FaToggleOn className={isOrphan ? 'text-amber-500' : 'text-primary'} size={20} /> : <FaToggleOff className="text-gray-300" size={20} />}
                                                    </button>
                                                    <span className={`flex-1 text-sm ${addon.enabled && !isOrphan ? 'text-gray-700' : 'text-gray-400'}`}>
                                                        {getAddonLabel(def, t)}
                                                        {isOrphan && <span className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-700">{t('admin.arch.addon_orphan')}</span>}
                                                        {!isOrphan && matchedItems.length > 0 && (
                                                            <span className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-violet-50 text-violet-600">{matchedItems.length}</span>
                                                        )}
                                                    </span>
                                                    <div className="flex gap-1 items-center">
                                                        <span className="text-xs text-gray-400">$</span>
                                                        <input type="number" min="0" step="1" value={addon.amount} onChange={e => setAddonAmount(addon.key, e.target.value)} disabled={!addon.enabled || isOrphan} className="px-2 py-1 w-24 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-40 disabled:bg-gray-50" />
                                                    </div>
                                                </div>
                                                {addon.enabled && !isOrphan && matchedItems.length > 0 && (
                                                    <div className="flex flex-wrap gap-1 pl-9">
                                                        {matchedItems.slice(0, 5).map(ci => (
                                                            <span key={ci.id} className="px-1.5 py-0.5 rounded text-[10px] bg-violet-50 text-violet-600 border border-violet-100">{ci.name}</span>
                                                        ))}
                                                        {matchedItems.length > 5 && <span className="px-1.5 py-0.5 rounded text-[10px] text-gray-400">+{matchedItems.length - 5} more</span>}
                                                    </div>
                                                )}
                                            </div>
                                        </SortableAddonRow>
                                    )
                                })}
                            </SortableContext>
                        </DndContext>
                        {calc.addonsTotal > 0 && (
                            <div className="flex justify-between items-center pt-1 text-sm border-t border-gray-100">
                                <span className="text-gray-500">{t('admin.arch.addons_total')}</span>
                                <span className="font-semibold">${fmt(calc.addonsTotal)}</span>
                            </div>
                        )}
                    </div>
                    <div className="flex justify-between items-center pt-3 mt-3 border-t-2 border-primary/20">
                        <span className="font-bold text-gray-700">{t('admin.arch.total_contract')}</span>
                        <span className="text-lg font-bold text-primary">${fmt(calc.totalContract)}</span>
                    </div>
                </div>
                </section>
            )
            case 'payment_schedule': return (
                <ArchPaymentScheduleEditor
                    cuotas={estimate.cuotas}
                    totalContract={calc.totalContract}
                    onChange={setCuotas}
                    customPresets={customPresets}
                />
            )
            case 'terms':
                return <ConstructorTermsPanel catalogTerms={archTermsConditions} estimate={estimate} setEstimate={setEstimate} t={t} />
            case 'internal_analysis': return canViewInternal ? (
                <section className="overflow-hidden rounded-2xl border border-violet-300 border-dashed bg-violet-50/60">
                    <div className="h-[3px] bg-gray-300" />
                    <button onClick={() => setInternalOpen(!internalOpen)} className="flex w-full justify-between items-center px-5 py-3.5 text-sm font-semibold text-violet-700 hover:bg-violet-100/60 transition-all">
                        <span className="flex gap-2 items-center"><FaInfoCircle size={13} />{t('admin.arch.internal_analysis')}</span>
                        {internalOpen ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
                    </button>
                    <AnimatePresence>
                        {internalOpen && (
                            <motion.div key="internal" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                                <div className="px-5 pb-5 space-y-4">
                                    <div className="flex justify-between items-center py-2 border-b border-violet-200">
                                        <span className="text-sm font-bold text-violet-700">{t('admin.arch.total_contract')}</span>
                                        <span className="text-sm font-bold text-violet-700">${fmt(calc.totalContract)}</span>
                                    </div>
                                    {/* Discipline Breakdown */}
                                    <div className="space-y-1">
                                        <p className="text-xs font-bold tracking-wide text-gray-600 uppercase">{t('admin.arch.disciplines')}</p>
                                        {[
                                            { label: (estimate.structPct || 0) + (estimate.mechPct || 0) + (estimate.elecPct || 0) + (estimate.plumbPct || 0) + (calc.isAddAlt ? 0 : (estimate.civilPct || 0)) === 0 ? t('admin.arch.disc_arch_only') : t('admin.arch.disc_arch'), val: calc.archAmt, pct: estimate.archPct },
                                            { label: t('admin.arch.disc_struct'), val: calc.structAmt, pct: estimate.structPct },
                                            { label: t('admin.arch.disc_mech'), val: calc.mechAmt, pct: estimate.mechPct },
                                            { label: t('admin.arch.disc_elec'), val: calc.elecAmt, pct: estimate.elecPct },
                                            { label: t('admin.arch.disc_plumb'), val: calc.plumbAmt, pct: estimate.plumbPct },
                                            // Omit civil for Add/Alt/Rem — the discipline split UI hides it and
                                            // calc.civilAmt is forced to $0 for these project types.
                                            ...(!calc.isAddAlt ? [{ label: t('admin.arch.disc_civil'), val: calc.civilAmt, pct: estimate.civilPct }] : []),
                                            { label: t('admin.arch.disc_pm'), val: calc.adminAmt, pct: estimate.adminPct },
                                            ...(calc.useCivilSplit ? [{ label: t('admin.arch.disc_civil_admin'), val: calc.civilAdminAmt, pct: estimate.civilAdminPct }] : []),
                                        ].filter(row => Number(row.pct) > 0).map(row => (
                                            <div key={row.label} className="flex justify-between items-center text-xs text-gray-600">
                                                <span>{row.label} <span className="opacity-50">({row.pct}%)</span></span>
                                                <span className="font-mono">${fmt(row.val)}</span>
                                            </div>
                                        ))}
                                        {(estimate.disciplineDeductions || []).length > 0 && (
                                            <div className="pt-2 mt-1 space-y-1 border-t border-red-100">
                                                {(estimate.disciplineDeductions || []).map(d => (
                                                    <div key={d.field} className="flex justify-between items-center text-xs text-red-500">
                                                        <span className="line-through">{d.label}</span>
                                                        <span className="font-mono">−${fmt(d.amount)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {(() => {
                                        const BUCKET_FIELD = {
                                            direct: 'direct_cost_pct', certifier: 'certifier_pct', commission: 'sales_pct',
                                            operating: 'operating_pct', franchise: 'franchise_pct', profit: 'profit_pct', contingency: 'contingency_pct',
                                        }
                                        return Object.entries(calc.groupedRoles || {}).map(([type, roles]) => {
                                            const total = calc.groupTotals?.[type] || 0
                                            const bucketPct = calc.cfg?.[BUCKET_FIELD[type]] || 0
                                            const title = roles[0]?.group_title || t(`admin.arch.${type}_costs`) || type
                                            return (
                                                <div key={type}>
                                                    <p className="mb-2 text-xs font-bold tracking-wide text-gray-600 uppercase">{title} — ${fmt(total)} <span className="opacity-50">({fmtPct(bucketPct)} bucket)</span></p>
                                                    {roles.map(r => (
                                                        <div key={r.roleKey} className="flex justify-between items-center text-xs text-gray-600 py-0.5">
                                                            <span>{t(r.labelKey)}</span>
                                                            <span className="flex items-center gap-1.5 font-mono">
                                                                <span>${fmt(r.amount)}</span>
                                                                <span className="w-10 text-right opacity-50">({Number(r.pct || 0).toFixed(1)}%)</span>
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )
                                        })
                                    })()}

                                    <div className="pt-3 space-y-1 border-t border-violet-200">
                                        <div className="flex justify-between text-xs text-gray-600">
                                            <span>{t('admin.arch.total_costs')}</span><span className="font-mono">${fmt(calc.totalCosts)}</span>
                                        </div>
                                        <div className={`flex justify-between text-sm font-bold ${calc.netProfit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                            <span>{t('admin.arch.net_profit')}</span>
                                            <span>${fmt(calc.netProfit)} ({calc.profitPct.toFixed(1)}%)</span>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </section>
            ) : null
            default: return null
        }
    }

    return (
        <div>
            <DndContext sensors={dndSensors} collisionDetection={rectIntersection} onDragEnd={handlePanelDragEnd}>
                <SortableContext items={visibleOrder} strategy={rectSortingStrategy}>
                    {/* Mobile: single column */}
                    <div className="space-y-3 lg:hidden">
                        {visibleOrder.map(id => (
                            <SortablePanelWrapper key={id} id={id}>
                                {renderPanel(id)}
                            </SortablePanelWrapper>
                        ))}
                    </div>
                    {/* Desktop: two flex columns — tiled, no vertical gaps */}
                    <div className="hidden gap-x-3 items-start lg:flex">
                        <div className="flex-1 space-y-0.5 min-w-0">
                            {visibleOrder.filter((_, i) => i % 2 === 0).map(id => (
                                <SortablePanelWrapper key={id} id={id}>
                                    {renderPanel(id)}
                                </SortablePanelWrapper>
                            ))}
                        </div>
                        <div className="flex-1 space-y-0.5 min-w-0">
                            {visibleOrder.filter((_, i) => i % 2 === 1).map(id => (
                                <SortablePanelWrapper key={id} id={id}>
                                    {renderPanel(id)}
                                </SortablePanelWrapper>
                            ))}
                        </div>
                    </div>
                </SortableContext>
            </DndContext>

            {/* Reset layout button */}
            <div className="flex justify-end mt-2">
                <button
                    onClick={resetPanelOrder}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-400 rounded-lg border border-dashed border-gray-200 hover:text-gray-600 hover:border-gray-300 transition-all"
                >
                    <FaUndo size={9} />
                    {t('admin.arch.reset_layout')}
                </button>
            </div>

            {/* ── Notes modal ── */}
            <AnimatePresence>
                {notesOpen && (
                    <motion.div key="notes-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex fixed inset-0 z-50 justify-center items-center p-4 backdrop-blur-sm bg-black/40" onClick={e => { if (e.target === e.currentTarget) setNotesOpen(false) }}>
                        <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }} transition={{ duration: 0.15 }} className="flex overflow-hidden flex-col w-full max-w-2xl bg-white rounded-2xl shadow-2xl">
                            <div className="flex justify-between items-center px-5 py-3.5 border-b border-gray-100">
                                <h3 className="flex gap-2 items-center text-sm font-bold text-gray-700">
                                    <FaStickyNote size={12} className="text-primary" />
                                    {t('admin.arch.scope_of_work')}
                                    <span className="text-[10px] font-normal text-gray-400 ml-1">{t('admin.estimator.optional_printed')}</span>
                                </h3>
                                <button onClick={() => setNotesOpen(false)} className="p-1.5 text-gray-400 rounded-lg hover:bg-gray-100 hover:text-gray-600 transition-colors"><FaTimes size={12} /></button>
                            </div>
                            <textarea autoFocus value={localNotes} onChange={e => handleNotesChange(e.target.value)} rows={18} placeholder={t('admin.estimator.notes_placeholder')} className="px-5 py-4 w-full text-sm border-0 resize-none focus:outline-none" />
                            <div className="flex justify-end px-5 py-3 bg-gray-50 border-t border-gray-100">
                                <button onClick={() => setNotesOpen(false)} className="px-4 py-2 text-sm font-medium text-white rounded-xl transition-colors bg-primary hover:bg-primary/90">{t('admin.arch.notes_done')}</button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

        </div>
    )
}
